import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SODStatsCard from "components/sod/SODStatsCard";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";
import { APIURL, AMAZON_CONNECT_APIURL, CO_SUPERVISOR_GROUPS } from "variables/constants";
import Card from "components/card";
import KanbanIcon from "components/icons/KanbanIcon";
import ProfileIcon from "components/icons/ProfileIcon";
import TablesIcon from "components/icons/TablesIcon";
import PieChart from "components/charts/PieChart";
import LineChart from "components/charts/LineChart";
import BarChart from "components/charts/BarChart";
import GreatChannelNav from "./GreatChannelNav";
import {
  DIGITAL_VIEW_KEYS,
  DIGITAL_CHANNEL_ACCENTS,
  dbChannelToLabel,
  filterDigitalRows,
  summarizeDigitalRows,
  normalizeDigitalChannel,
  isDigitalSummaryChannelVisible,
  isLiveChatScopedRow,
  parseInteractionDate,
  isDigitalRowVisibleToSupervisor,
} from "./greatDigitalShared";
import { fetchAllDigitalRows } from "./greatDigitalApi";
import GreatDepartmentFilter from "./GreatDepartmentFilter";
import GreatOverallKpiHeader from "./GreatOverallKpiHeader";
import GreatSummaryChannelIcon from "./GreatSummaryChannelIcon";
import SentimentReasonLabel from "./SentimentReasonLabel";
import SentimentBucketHeader from "./SentimentBucketHeader";
import {
  extractSentimentReasonRaw,
  buildTopSentimentReasonBuckets,
  sentimentReasonMatchesFilter as canonicalSentimentReasonMatchesFilter,
  buildSentimentReasonFilterOptions,
  RETAILER_SENTIMENT_WEEK_START_LABEL,
  formatVoiceReturnErrorsMonitorNote,
  VOICE_RETURN_ERRORS_SERIES_NAME,
  voiceRowCountsAsReturnError,
} from "./greatSentimentReasons";

const GreatDigitalDashboard = React.lazy(() => import("./GreatDigitalDashboard"));

/** Toggle CS Overall KPI tables/tiles (summary, inbound, outbound). Channel average-score strip on Summary is always shown. */
const SHOW_OVERALL_KPIS = true;

const GREAT_PASS_FAIL_THRESHOLD = 80;

const voicePassFailFromRow = (row) => {
  const score = parseFloat(row?.total_score);
  if (!Number.isNaN(score)) {
    return score >= GREAT_PASS_FAIL_THRESHOLD ? "Pass" : "Fail";
  }
  const raw = String(row?.pass_fail ?? "").toLowerCase();
  if (raw === "pass" || raw === "1" || raw === "true") return "Pass";
  if (raw === "fail" || raw === "0" || raw === "false") return "Fail";
  return "Fail";
};

const formatDateTime = (date) => {
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(0);
  const mi = pad(0);
  const ss = pad(0);
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const formatDateTimeEnd = (date) => {
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  return `${yyyy}-${mm}-${dd} 23:59:59`;
};

const formatDate = (date) => {
  if (!date) return null;
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

// Normalize a "direction" URL param / state value to a known direction.
// Anything that isn't explicitly outbound resolves to inbound (the default).
const normalizeDirectionValue = (value) => {
  const v = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  if (v.startsWith("sum")) return "summary";
  if (v === "email" || v === "live-chat" || v === "sms") return v;
  return v.startsWith("out") ? "outbound" : "inbound";
};

// Robustly read a row's call direction across possible field name/casing
// variants and normalize it to "inbound" | "outbound".
//
// The DB `direction` field is effectively BOOLEAN (0/1):
//   1 (or "1" / true)              -> outbound
//   0, "0", "", null, undefined,
//   false, or anything unrecognized -> inbound (the default)
//
// Treating missing/empty as inbound means legacy rows (created before the
// `direction` column existed) still appear under Inbound and nothing silently
// disappears.
const getRowDirection = (row) => {
  if (!row || typeof row !== "object") return "inbound";
  const raw =
    row.direction ??
    row.call_direction ??
    row.Direction ??
    row.callDirection ??
    row.CallDirection ??
    null;
  if (raw === null || raw === undefined) return "inbound";
  if (raw === true) return "outbound";
  if (raw === false) return "inbound";
  const v = String(raw).trim();
  if (v === "") return "inbound";
  return v === "1" ? "outbound" : "inbound";
};

// Returns true when a row should be visible for the selected direction filter.
// Used in every memo that independently re-derives from `rows` so the direction
// logic can't drift between them.
const matchesDirection = (row, directionFilter) => {
  return getRowDirection(row) === normalizeDirectionValue(directionFilter);
};

const DEFAULT_AMAZON_CONNECT_AGENT = "synchronet";

const AMAZON_CONNECT_AGENT_DIRECTORY = [
  // Customer Service (supervisor: Barbara Ryan / Melina Vargas)
  { fullName: "Michelle Araya", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "maraya", email: "maraya@gabrielny.com" },
  { fullName: "Victoria Gaynor", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "vgaynor", email: "vgaynor@gabrielny.com" },
  { fullName: "Willie Nunes", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "wnunes", email: "wnunes@gabrielny.com" },
  //{ fullName: "Willie Nunes", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "wnunes", email: "charbel.toumieh@gabrielny.com" },
  { fullName: "Alexandra Lynn", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "alynn", email: "alynn@gabrielny.com" },
  { fullName: "Lisa Jones", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "ljones", email: "ljones@gabrielny.com" },
  { fullName: "Walid Azar", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "walid.azar", email: "walid.azar@gabrielny.com" },
  { fullName: "Elizabeth Hernandez", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "ehernandez", email: "ehernandez@gabrielny.com" },
  { fullName: "Sevag Tashjian", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "sevag.tashjian", email: "sevag.tashjian@gabrielny.com" },
  { fullName: "Anthony Saliba", supervisor: "Barbara Ryan", department: "Customer Service", amazonConnectId: "anthony.saliba", email: "anthony.saliba@gabrielny.com" },
  // Custom Order (supervisor: John Hanna / Tracy Verry)
  { fullName: "Heather Poppy", supervisor: "Tracy Verry", department: "Custom Order", amazonConnectId: "hpoppy", email: "hpoppy@gabrielny.com" },
  { fullName: "Tracy Verry", supervisor: "John Hanna", department: "Custom Order", amazonConnectId: "tverry", email: "tverry@gabrielny.com" },
  { fullName: "Samer Francis", supervisor: "John Hanna", department: "Custom Order", amazonConnectId: "samer.francis", email: "samer.francis@gabrielny.com" },
  { fullName: "Vrej Kendoyan", supervisor: "John Hanna", department: "Custom Order", amazonConnectId: "vrej.kendoyan", email: "vrej.kendoyan@gabrielny.com" },
  { fullName: "Joe Chahwan", supervisor: "John Hanna", department: "Custom Order", amazonConnectId: "joe.chahwan", email: "joe.chahwan@gabrielny.com" },
  { fullName: "John Bader", supervisor: "John Hanna", department: "Custom Order", amazonConnectId: "john.bader", email: "john.bader@gabrielny.com" },
  { fullName: "Joyce Dagher", supervisor: "John Hanna", department: "Custom Order", amazonConnectId: "joyce.dagher", email: "joyce.dagher@gabrielny.com" },
  // Concierge (supervisor: Christine Dahdah / Peter Kurani)
  { fullName: "Jasmine Almayan", supervisor: "Christine Dahdah", department: "Concierge", amazonConnectId: "jasmine.almayan", email: "jasmine.almayan@gabrielny.com" },
  { fullName: "Vartan Kaloustian", supervisor: "Christine Dahdah", department: "Concierge", amazonConnectId: "vartan.kaloustian", email: "vartan.kaloustian@gabrielny.com" },
  // Account Executives (supervisor: Janelle Fernuik)
  { fullName: "Janelle Fernuik", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "jfernuik", email: "jfernuik@gabrielny.com" },
  { fullName: "Joelle Jreije", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "joelle.jreije", email: "joelle.jreije@gabrielny.com" },
  { fullName: "Razmig AlMayan", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "razmig.almayan", email: "razmig.almayan@gabrielny.com" },
  { fullName: "Inderjit Singh", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "isingh", email: "isingh@gabrielny.com" },
  { fullName: "Belma Yucel", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "byucel", email: "byucel@gabrielny.com" },
  { fullName: "Rachel Hayduk", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "rhayduk", email: "rhayduk@gabrielny.com" },
  { fullName: "Emma Lanka", supervisor: "Janelle Fernuik", department: "Account Executive", amazonConnectId: "elanka", email: "elanka@gabrielny.com" },
];

const normalizeAmazonConnectId = (value) => String(value || "").trim().toLowerCase();

const amazonConnectDirectoryById = new Map(
  AMAZON_CONNECT_AGENT_DIRECTORY.map((agent) => [normalizeAmazonConnectId(agent.amazonConnectId), agent])
);

const amazonConnectDirectoryByEmail = new Map(
  AMAZON_CONNECT_AGENT_DIRECTORY.map((agent) => [normalizeAmazonConnectId(agent.email), agent])
);

const amazonConnectDirectoryByName = new Map(
  AMAZON_CONNECT_AGENT_DIRECTORY.map((agent) => [normalizeAmazonConnectId(agent.fullName), agent])
);

const getAmazonConnectAgentProfile = (agentUsername) =>
  amazonConnectDirectoryById.get(normalizeAmazonConnectId(agentUsername)) || null;

const getAmazonConnectAgentUsernameForEmail = (email) => {
  if (!email) return null;
  const profile = amazonConnectDirectoryByEmail.get(normalizeAmazonConnectId(email));
  return profile?.amazonConnectId || null;
};

const getAmazonConnectIdForName = (fullName) => {
  if (!fullName) return null;
  const profile = amazonConnectDirectoryByName.get(normalizeAmazonConnectId(fullName));
  return profile?.amazonConnectId || null;
};

const getAmazonConnectAgentUsername = (row) =>
  row?.Agent_Username ||
  row?.agentusername ||
  row?.AgentUsername ||
  row?.agent_username ||
  row?.agentUserName ||
  row?.Agent ||
  row?.agent ||
  row?.Username ||
  row?.username ||
  row?.UserName ||
  row?.user_name ||
  row?.user ||
  null;

const hasAgentUsername = (row) => {
  const value = getAmazonConnectAgentUsername(row);
  if (value === null || value === undefined) return false;
  const raw = String(value).trim();
  return raw.length > 0 && raw !== "0";
};

/**
 * Timezone-safe parse for DB date strings.
 * `new Date("2026-04-08")` is UTC midnight and shifts to the previous day
 * in negative-offset timezones, which can drop rows at the range boundary.
 * We parse the Y/M/D parts directly so the Date is local midnight.
 */
const parseRowDate = (raw) => {
  if (!raw) return null;
  const str = String(raw).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1;
    const d = parseInt(m[3], 10);
    if (y >= 1970 && mo >= 0 && mo < 12 && d >= 1 && d <= 31) {
      return new Date(y, mo, d);
    }
  }
  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
};

const MONTH_SHORT_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Human-readable "MMM YYYY" period for a row (e.g. "Jul 2026"), used to group
// rows by calendar month. Module-level so memos defined early in the component
// (e.g. the Top Reasons monthly table) can use it without a TDZ error.
const getRowMonthPeriod = (row) => {
  const date = parseRowDate(row?.date_of_call || row?.created_at);
  if (!date) return null;
  return `${MONTH_SHORT_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Last 3 calendar months ending at `endDate`'s month. Used by Top Reasons so
 * the trend columns follow the latest month that actually has calls (e.g.
 * Jun–Aug when September is still empty) instead of always including the
 * current calendar month.
 */
const getLast3MonthPeriods = (endDate) => {
  const src = endDate && !Number.isNaN(endDate.getTime()) ? endDate : new Date();
  const base = new Date(src.getFullYear(), src.getMonth(), 1);
  const periods = [];
  for (let i = 2; i >= 0; i--) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    periods.push({
      key: `${MONTH_SHORT_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      short: MONTH_SHORT_NAMES[d.getMonth()],
    });
  }
  return periods;
};

const normalizeGreatRowDate = (row) => {
  const raw =
    (row?.date_of_call && row.date_of_call !== "0000-00-00" ? row.date_of_call : null) ||
    row?.created_at ||
    null;
  return parseRowDate(raw);
};

/**
 * Returns a Date for any row (GREAT evaluation or Amazon Connect call),
 * checking GREAT fields first and falling back to Amazon Connect timestamps.
 */
const resolveRowDate = (row) => {
  if (!row) return null;
  const raw =
    (row.date_of_call && row.date_of_call !== "0000-00-00" ? row.date_of_call : null) ||
    row.InitiationTimestamp ||
    row.Queue_EnqueueTimestamp ||
    row.created_at ||
    null;
  return parseRowDate(raw);
};

/**
 * Per-agent team re-assignments: calls BEFORE `cutoffDate` stay under the
 * `before` supervisors/department; calls on or AFTER belong to the `after`
 * supervisors/department. This single source of truth drives:
 *   - department display / filter (getRowDepartment)
 *   - admin "filter by supervisor" matching
 *   - supervisor-view visibility of each row
 *
 * Anthony moved to Customer Service on 2026-04-21 (still listed on CO teams so
 * John/Tracy keep pre-cutover visibility; cutover hides his post-cutover rows
 * from Custom Order).
 */
const AGENT_TEAM_CUTOVERS = {
  "Anthony Saliba": {
    cutoffDate: "2026-04-21",
    before: {
      department: "Custom Order",
      supervisorsResponsible: ["John Hanna", "Tracy Verry"],
    },
    after: {
      department: "Customer Service",
      supervisorsResponsible: ["Barbara Ryan", "Melina Vargas"],
    },
  },
  // Heather is Custom Order. Force CO ownership so stale Customer Service
  // supervisor tags in GREAT data do not hide her from John/Tracy.
  "Heather Poppy": {
    cutoffDate: "1900-01-01",
    before: {
      department: "Custom Order",
      supervisorsResponsible: ["John Hanna", "Tracy Verry"],
    },
    after: {
      department: "Custom Order",
      supervisorsResponsible: ["John Hanna", "Tracy Verry"],
    },
  },
  "John Bader": {
    cutoffDate: "1900-01-01",
    before: { department: "Custom Order", supervisorsResponsible: ["John Hanna", "Tracy Verry"] },
    after: { department: "Custom Order", supervisorsResponsible: ["John Hanna", "Tracy Verry"] },
  },
  "Joelle Jreije": {
    cutoffDate: "2026-10-05",
    before: {
      department: "Customer Service",
      supervisorsResponsible: ["Barbara Ryan", "Melina Vargas"],
    },
    after: {
      department: "Account Executive",
      supervisorsResponsible: ["Janelle Fernuik"],
    },
  },
  "Walid Azar": {
    cutoffDate: "2026-10-05",
    before: {
      department: "Custom Order",
      supervisorsResponsible: ["John Hanna", "Tracy Verry"],
    },
    after: {
      department: "Customer Service",
      supervisorsResponsible: ["Barbara Ryan", "Melina Vargas"],
    },
  },
  "Joyce Dagher": {
    cutoffDate: "1900-01-01",
    before: { department: "Custom Order", supervisorsResponsible: ["John Hanna", "Tracy Verry"] },
    after: { department: "Custom Order", supervisorsResponsible: ["John Hanna", "Tracy Verry"] },
  },
};

/** Account Executive outbound Top Reasons (display order). */
const AE_OUTBOUND_REASON_BUCKETS = [
  "Account Executive Outreach",
  "Outbound Follow-Up",
  "Outbound Sales Follow-Up",
];

/**
 * Fallback outbound Top Reasons buckets — used by "All Departments" and by any
 * outbound rows whose department has no curated set of its own. Account
 * Executive, Customer Service, Custom Order and Concierge each define their
 * own buckets below and no longer come through here. Keeping one shared
 * classifier for the remainder — instead of a generic top-5-by-frequency
 * fallback — prevents the same underlying reason from showing up as separate
 * near-duplicate entries across departments (e.g. "Quote Follow-Up" and "To
 * Check Pricing" are the same customer intent and must land in the same
 * "Pricing Follow-up" bucket). "Outbound Follow-Up" is a catch-all so every
 * row is always classified — without it, any free text that doesn't mention
 * pricing/shipping/repair/rendering/product-inquiry would be silently dropped
 * from the Total instead of counted.
 */
const DEFAULT_OUTBOUND_REASON_BUCKETS = [
  "Pricing Follow-up",
  "Providing an Expedited Shipping Date",
  "Repair Status Update",
  "Product Inquiry Follow-up",
  "Image & Rendering Follow-up",
  "Outbound Follow-Up",
];

/**
 * Collapse free-text reasons into Account Executive outbound buckets.
 * Account Executive Outreach and Account Check-In merge into one.
 * "Outbound Follow-Up" is the catch-all so every reason stays inside the
 * three reported categories.
 */
const normalizeAeOutboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    s.includes("outbound sales follow") ||
    s.includes("sales follow") ||
    s.includes("quote") ||
    s.includes("pric")
  ) {
    return "Outbound Sales Follow-Up";
  }
  if (
    s.includes("account executive outreach") ||
    s.includes("account check-in") ||
    s.includes("account check in") ||
    s.includes("monthly account check") ||
    s.includes("monthly courtesy") ||
    s.includes("outbound account check") ||
    s.includes("outreach") ||
    s.includes("check-in") ||
    s.includes("check in") ||
    s.includes("courtesy") ||
    s.includes("introduc") ||
    s.includes("new account")
  ) {
    return "Account Executive Outreach";
  }
  return "Outbound Follow-Up";
};

/**
 * Collapse free-text reasons into the default (non-AE) outbound buckets.
 * Most-specific keyword matches win when a reason spans multiple topics;
 * "Outbound Follow-Up" is the catch-all so this function always returns a
 * bucket for any non-empty reason (see comment on DEFAULT_OUTBOUND_REASON_BUCKETS).
 */
const normalizeDefaultOutboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    s.includes("image") ||
    s.includes("render") ||
    s.includes("rendering") ||
    s.includes(" cad") ||
    s.startsWith("cad") ||
    s.includes("/cad") ||
    s.includes("video")
  ) {
    return "Image & Rendering Follow-up";
  }
  if (
    s.includes("repair") ||
    s.includes("remake") ||
    s.includes("redo") ||
    s.includes("return authorization") ||
    /\bra\b/.test(s) ||
    s.includes("ra approval") ||
    s.includes("ra number")
  ) {
    return "Repair Status Update";
  }
  if (
    s.includes("expedit") ||
    s.includes("rush") ||
    s.includes("shipping date") ||
    s.includes("ship date") ||
    s.includes("ship timeline") ||
    s.includes("overnight ship") ||
    s.includes("overnight shipping")
  ) {
    return "Providing an Expedited Shipping Date";
  }
  if (s.includes("product inquiry") || (s.includes("product") && s.includes("follow"))) {
    return "Product Inquiry Follow-up";
  }
  // "Quote Follow-Up" (a raw reason string used by some departments) and
  // "check pricing" are the exact same customer intent as a price-quote
  // follow-up call — merge them into one bucket rather than letting one
  // department's wording create a separate-looking entry.
  if (
    s.includes("pric") ||
    s.includes("quote") ||
    s.includes("check pricing")
  ) {
    return "Pricing Follow-up";
  }
  // Catch-all: any other non-empty reason (generic "Outbound Follow-Up",
  // "Account Check-In", etc.) — keeps every row counted in the Total instead
  // of silently disappearing because it didn't mention a specific keyword.
  return "Outbound Follow-Up";
};

/** Custom Order inbound Top Reasons (display order). */
const CUSTOM_ORDER_INBOUND_REASON_BUCKETS = [
  "To place an order/approve CAD design",
  "To revise/modify/update a CAD design",
  "To track an order",
  "To request a return/SWC",
  "To clarify a design or SOD process",
];

/** Custom Order outbound Top Reasons (display order). */
const CUSTOM_ORDER_OUTBOUND_REASON_BUCKETS = [
  "Custom orders outreach",
  "To clarify a design or SOD process",
  "To follow-up on a previous call or provide an update",
];

/** Customer Service inbound Top Reasons (display order). */
const CUSTOMER_SERVICE_INBOUND_REASON_BUCKETS = [
  "Orders",
  "Pricing",
  "Status",
  "RA & Memo Inquiries",
];

/** Customer Service outbound Top Reasons (display order). */
const CUSTOMER_SERVICE_OUTBOUND_REASON_BUCKETS = [
  "Update on an RA#",
  "Pricing Inquiries",
  "Repairs/Rush Updates",
  "Clarification on Topics or Programs",
];

/**
 * Collapse free-text reasons into Customer Service INBOUND buckets.
 * "Orders" is the catch-all: an order-related call with no pricing, status or
 * RA/memo signal is a general order call, and so is anything unrecognised.
 * Status is tested before the catch-all so "order status" is a status call.
 */
const normalizeCustomerServiceInboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    // Word-anchored: bare "ra" would otherwise match inside "program".
    /\bra\b/.test(s) ||
    /\bra#/.test(s) ||
    s.includes("return authorization") ||
    s.includes("memo")
  ) {
    return "RA & Memo Inquiries";
  }
  if (
    s.includes("pric") ||
    s.includes("quote") ||
    s.includes("cost") ||
    s.includes("discount")
  ) {
    return "Pricing";
  }
  if (
    s.includes("status") ||
    s.includes("track") ||
    /\beta\b/.test(s) ||
    /\bship/.test(s) ||
    /\blate\b/.test(s) ||
    s.includes("deliver") ||
    s.includes("arriv") ||
    s.includes("when will") ||
    s.includes("delay") ||
    s.includes("update")
  ) {
    return "Status";
  }
  return "Orders";
};

/**
 * Collapse free-text reasons into Customer Service OUTBOUND buckets.
 * "Clarification on Topics or Programs" is the catch-all — it is the
 * "any topics or programs" category, so anything unrecognised belongs there.
 */
const normalizeCustomerServiceOutboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  // Before the repairs test: both categories talk about "updates", and an RA
  // update is the more specific of the two.
  if (
    /\bra\b/.test(s) ||
    /\bra#/.test(s) ||
    s.includes("return authorization")
  ) {
    return "Update on an RA#";
  }
  if (
    s.includes("repair") ||
    /\brush\b/.test(s) ||
    s.includes("expedit") ||
    s.includes("remake") ||
    s.includes("redo")
  ) {
    return "Repairs/Rush Updates";
  }
  if (
    s.includes("pric") ||
    s.includes("quote") ||
    s.includes("cost") ||
    s.includes("discount")
  ) {
    return "Pricing Inquiries";
  }
  return "Clarification on Topics or Programs";
};

/** Concierge inbound Top Reasons (display order). */
const CONCIERGE_INBOUND_REASON_BUCKETS = [
  "Order Status & Expedited Shipping Requests",
  "Order Cancellation Requests",
  "Order Changes or Modification Requests",
  "Product Information & Specifications",
  "Retailer Purchase Concerns",
];

/** Concierge outbound Top Reasons (display order). */
const CONCIERGE_OUTBOUND_REASON_BUCKETS = [
  "UPS Delivery Coordination",
  "Customer-Requested Callbacks",
  "Fraud Prevention & Order Verification",
];

/**
 * Collapse free-text reasons into Custom Order INBOUND buckets. Ordered
 * most-specific first: a CAD revision mentions both "CAD" and often "order",
 * so the revise/modify test has to run before the place-an-order test.
 * "To clarify a design or SOD process" is the catch-all so every reason stays
 * inside the five reported categories.
 */
const normalizeCustomOrderInboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    s.includes("revise") ||
    s.includes("revision") ||
    s.includes("modify") ||
    s.includes("modification") ||
    s.includes("update a cad") ||
    s.includes("change") ||
    s.includes("resize") ||
    s.includes("ring size") ||
    s.includes("engrav") ||
    s.includes("adjust") ||
    // Word-boundary so "credit" is not read as an edit request and
    // "alternative" is not read as an alteration.
    /\bedit/.test(s) ||
    /\balter\b/.test(s)
  ) {
    return "To revise/modify/update a CAD design";
  }
  if (
    s.includes("return") ||
    s.includes("swc") ||
    /\bra\b/.test(s) ||
    s.includes("exchange") ||
    s.includes("refund") ||
    s.includes("send back")
  ) {
    return "To request a return/SWC";
  }
  if (
    s.includes("track") ||
    s.includes("status") ||
    // Short keywords are word-anchored: bare "eta"/"ship"/"late" would
    // otherwise match inside "metal", "relationship" and "unrelated".
    /\beta\b/.test(s) ||
    /\bship/.test(s) ||
    /\blate\b/.test(s) ||
    s.includes("deliver") ||
    s.includes("arriv") ||
    s.includes("when will") ||
    s.includes("delay")
  ) {
    return "To track an order";
  }
  // Explicit clarification language wins over the broad "order" test below, so
  // "clarify the order process" is not counted as placing an order.
  if (
    s.includes("clarify") ||
    s.includes("clarification") ||
    s.includes("sod process") ||
    s.includes("design process") ||
    s.includes("question") ||
    s.includes("explain")
  ) {
    return "To clarify a design or SOD process";
  }
  if (
    s.includes("place an order") ||
    s.includes("place order") ||
    s.includes("new order") ||
    s.includes("approve") ||
    s.includes("approval") ||
    /\bcad/.test(s) ||
    s.includes("quote") ||
    s.includes("order")
  ) {
    return "To place an order/approve CAD design";
  }
  // Catch-all: design/process questions and anything else unclassified.
  return "To clarify a design or SOD process";
};

/**
 * Collapse free-text reasons into Custom Order OUTBOUND buckets.
 * "To follow-up on a previous call or provide an update" is the catch-all so
 * every outbound row stays counted (same rationale as "Outbound Follow-Up" in
 * the default set).
 */
const normalizeCustomOrderOutboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    s.includes("clarify") ||
    s.includes("clarification") ||
    s.includes("sod process") ||
    s.includes("design process")
  ) {
    return "To clarify a design or SOD process";
  }
  // Follow-up wins over the outreach keywords so "follow up on a custom order"
  // is counted as a follow-up rather than as outreach.
  if (s.includes("follow") || s.includes("update") || s.includes("status")) {
    return "To follow-up on a previous call or provide an update";
  }
  if (s.includes("outreach") || s.includes("custom order")) {
    return "Custom orders outreach";
  }
  return "To follow-up on a previous call or provide an update";
};

/**
 * Collapse free-text reasons into Concierge INBOUND buckets. Ordered
 * most-specific first, because the categories overlap in wording: a retailer
 * question is still about an order, a size change still mentions the order,
 * and a change to the shipping address would otherwise be caught by the
 * shipping/status test. Returns null when nothing matches rather than forcing
 * a wrong bucket (same as the AE and Custom Order inbound classifiers).
 */
const normalizeConciergeInboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    s.includes("retailer") ||
    s.includes("dealer") ||
    s.includes("jeweler") ||
    s.includes("store purchase") ||
    s.includes("purchased elsewhere") ||
    s.includes("bought elsewhere") ||
    s.includes("third party") ||
    s.includes("3rd party")
  ) {
    return "Retailer Purchase Concerns";
  }
  if (s.includes("cancel")) {
    return "Order Cancellation Requests";
  }
  // Before the status/shipping test so "change the shipping address" is a
  // modification, not a shipping question. Bare "update" is deliberately NOT
  // here — "an update on my order" is a status call, not a change request.
  if (
    s.includes("change") ||
    s.includes("modif") ||
    s.includes("revise") ||
    s.includes("resize") ||
    s.includes("ring size") ||
    s.includes("engrav") ||
    s.includes("adjust") ||
    // Word-anchored so "alternative" is not read as an alteration.
    /\balter\b/.test(s) ||
    s.includes("swap") ||
    s.includes("correct") ||
    s.includes("exchange") ||
    s.includes("wrong")
  ) {
    return "Order Changes or Modification Requests";
  }
  if (
    s.includes("status") ||
    s.includes("track") ||
    s.includes("expedit") ||
    // Short keywords are word-anchored: bare "eta"/"ship"/"late"/"rush" would
    // otherwise match inside "metal", "relationship", "unrelated" and "brush".
    /\brush\b/.test(s) ||
    /\bship/.test(s) ||
    /\beta\b/.test(s) ||
    /\blate\b/.test(s) ||
    s.includes("deliver") ||
    s.includes("arriv") ||
    s.includes("when will") ||
    s.includes("delay") ||
    s.includes("overnight") ||
    s.includes("update")
  ) {
    return "Order Status & Expedited Shipping Requests";
  }
  // Explicit product language wins over the bare "order" catch-all below, so
  // "product specs for my order" is a product question, not a status call.
  if (
    s.includes("product") ||
    // Word-anchored so "inspect"/"special" are not read as specifications.
    /\bspec/.test(s) ||
    s.includes("dimension") ||
    s.includes("material") ||
    s.includes("gemstone") ||
    s.includes("diamond") ||
    s.includes("availab") ||
    s.includes("in stock") ||
    s.includes("customiz") ||
    s.includes("inquiry") ||
    s.includes("information") ||
    s.includes("details")
  ) {
    return "Product Information & Specifications";
  }
  // Catch-all: an order-related call with no more specific signal is treated
  // as a status question; anything else is a product/general inquiry. Keeps
  // every reason inside the five reported categories.
  if (s.includes("order")) {
    return "Order Status & Expedited Shipping Requests";
  }
  return "Product Information & Specifications";
};

/**
 * Collapse free-text reasons into Concierge OUTBOUND buckets.
 * "Customer-Requested Callbacks" is the catch-all — an outbound Concierge call
 * with no delivery or fraud signal is a callback about the customer's order —
 * so every reason stays inside the three reported categories.
 */
const normalizeConciergeOutboundReasonBucket = (reason) => {
  const s = String(reason || "").toLowerCase();
  if (!s.trim()) return null;
  if (
    s.includes("fraud") ||
    s.includes("verification") ||
    s.includes("verify") ||
    s.includes("on hold") ||
    s.includes("security") ||
    s.includes("identity") ||
    s.includes("declined")
  ) {
    return "Fraud Prevention & Order Verification";
  }
  if (
    s.includes("callback") ||
    s.includes("call back") ||
    s.includes("requested a call") ||
    s.includes("ticket") ||
    s.includes("voicemail") ||
    s.includes("missed call") ||
    s.includes("returning") ||
    s.includes("inquiry")
  ) {
    return "Customer-Requested Callbacks";
  }
  if (
    // Word boundary so "groups"/"backups" are not read as UPS.
    /\bups\b/.test(s) ||
    s.includes("fedex") ||
    s.includes("signature") ||
    s.includes("deliver") ||
    // Word-anchored so "relationship" is not read as a shipment.
    /\bship/.test(s) ||
    s.includes("package") ||
    s.includes("courier")
  ) {
    return "UPS Delivery Coordination";
  }
  return "Customer-Requested Callbacks";
};

const normalizeReasonBucket = (reason, mode) => {
  if (mode === "ae") return normalizeAeOutboundReasonBucket(reason);
  if (mode === "customOrderInbound") return normalizeCustomOrderInboundReasonBucket(reason);
  if (mode === "customOrderOutbound") return normalizeCustomOrderOutboundReasonBucket(reason);
  if (mode === "customerServiceInbound") return normalizeCustomerServiceInboundReasonBucket(reason);
  if (mode === "customerServiceOutbound") return normalizeCustomerServiceOutboundReasonBucket(reason);
  if (mode === "conciergeInbound") return normalizeConciergeInboundReasonBucket(reason);
  if (mode === "conciergeOutbound") return normalizeConciergeOutboundReasonBucket(reason);
  if (mode === "default") return normalizeDefaultOutboundReasonBucket(reason);
  return null;
};

const getReasonBucketsForMode = (mode) => {
  if (mode === "ae") return AE_OUTBOUND_REASON_BUCKETS;
  if (mode === "customOrderInbound") return CUSTOM_ORDER_INBOUND_REASON_BUCKETS;
  if (mode === "customOrderOutbound") return CUSTOM_ORDER_OUTBOUND_REASON_BUCKETS;
  if (mode === "customerServiceInbound") return CUSTOMER_SERVICE_INBOUND_REASON_BUCKETS;
  if (mode === "customerServiceOutbound") return CUSTOMER_SERVICE_OUTBOUND_REASON_BUCKETS;
  if (mode === "conciergeInbound") return CONCIERGE_INBOUND_REASON_BUCKETS;
  if (mode === "conciergeOutbound") return CONCIERGE_OUTBOUND_REASON_BUCKETS;
  if (mode === "default") return DEFAULT_OUTBOUND_REASON_BUCKETS;
  return null;
};

/**
 * Strip trailing punctuation/whitespace and collapse internal whitespace so
 * "Product Inquiry" and "Product Inquiry." (or extra spaces) are recognized
 * as the exact same reason before any bucketing/merging happens.
 */
const canonicalizeReasonText = (reason) =>
  String(reason || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,;:!?\s]+$/g, "");

const fmtScore2 = (val) => {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return "0.00";
  return n.toFixed(2);
};

const VOICE_SENTIMENT_LABELS = {
  positive: "Happy",
  neutral: "Neutral",
  negative: "Unhappy",
};

const VOICE_SENTIMENT_PILL_CLASS = {
  positive: "bg-[#DDF5E7] text-[#1B7F4E]",
  neutral: "bg-[#E8E8E8] text-gray-900",
  negative: "bg-[#FFE8E8] text-[#EF4444]",
};

const normalizeVoiceSentimentToken = (raw) => {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (!s || s === "n/a") return null;
  if (s.includes("negative") || s.includes("unhappy")) return "negative";
  if (s.includes("positive") || s.includes("happy")) return "positive";
  if (s.includes("neutral")) return "neutral";
  return null;
};

const voiceSentimentKey = (row) => {
  const fromField =
    normalizeVoiceSentimentToken(row?.sentiment) ||
    normalizeVoiceSentimentToken(row?.Customer_Sentiment);
  return fromField || "neutral";
};

const voiceSentimentHasValue = (row) => {
  const explicit = String(row?.sentiment ?? "").trim();
  const connect = String(row?.Customer_Sentiment ?? "").trim();
  if (!explicit && !connect) return false;
  if (explicit.toLowerCase() === "n/a" && !connect) return false;
  return true;
};

const voiceSentimentMatchesFilter = (row, filter) => {
  if (!filter || filter === "all") return true;
  const key = voiceSentimentKey(row);
  if (filter === "neutral") return key === "neutral";
  return key === String(filter).trim().toLowerCase();
};

const formatVoiceSentimentDisplay = (row) => {
  if (!voiceSentimentHasValue(row)) return "N/A";
  return VOICE_SENTIMENT_LABELS[voiceSentimentKey(row)] || "N/A";
};

function VoiceSentimentBadge({ row }) {
  if (!voiceSentimentHasValue(row)) {
    return <span className="text-xs text-gray-500 dark:text-gray-400">N/A</span>;
  }
  const key = voiceSentimentKey(row);
  const label = VOICE_SENTIMENT_LABELS[key];
  const pillClass = VOICE_SENTIMENT_PILL_CLASS[key] || VOICE_SENTIMENT_PILL_CLASS.neutral;
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold leading-none ${pillClass}`}
    >
      {label}
    </span>
  );
}

const getVoiceSentimentReason = (row) => extractSentimentReasonRaw(row);

/** Return errors: `order_error` = Yes (see series label for monitoring start note). */
const voiceRowHasError = (row) => voiceRowCountsAsReturnError(row);

const voiceErrorsMatchesFilter = (row, filter) => {
  if (!filter || filter === "all") return true;
  const has = voiceRowHasError(row);
  if (filter === "yes") return has;
  if (filter === "no") return !has;
  return true;
};

const sentimentReasonMatchesFilter = (row, filter, sentimentFilter) =>
  canonicalSentimentReasonMatchesFilter(row, filter, sentimentFilter, voiceSentimentKey);

/**
 * True if `shorterLower`/`longerLower` (already lowercased) are the same
 * reason, or `shorterLower` is a real-word-boundary PREFIX of `longerLower`
 * — e.g. "to check pricing" is a prefix of "to check pricing and availability".
 * Guards against mid-word false positives (e.g. "to check" must not swallow
 * "to checkout").
 */
const isReasonPrefixMatch = (shorterLower, longerLower) => {
  if (!shorterLower || !longerLower) return false;
  if (longerLower === shorterLower) return true;
  if (!longerLower.startsWith(shorterLower)) return false;
  const nextChar = longerLower[shorterLower.length];
  return nextChar === undefined || /[\s(/,.\-:;]/.test(nextChar);
};

/**
 * Builds the top-N "Top Reasons for Call" buckets for departments/directions
 * without a curated keyword bucket set (inbound, Concierge, Custom Order,
 * etc.) — used instead of a flat "top 5 raw strings" count so obvious
 * near-duplicate phrasings collapse into one bucket rather than splitting
 * the same underlying reason across several rows/slices:
 *   - exact duplicates that only differ by trailing punctuation/whitespace
 *     ("Product Inquiry" vs "Product Inquiry.")
 *   - a reason that is just a more specific version of a more common one
 *     ("To Check Pricing" vs "To Check Pricing and Availability" / "...and
 *     Place an Order") — the more common phrasing becomes the canonical
 *     bucket label and absorbs the longer variant's count.
 * Processing candidates most-frequent-first guarantees the canonical label
 * for a family of near-duplicates is always the most common phrasing.
 */
const buildTopReasonBuckets = (rows, topN = 5) => {
  const freqByKey = new Map(); // lowercased canonical text -> { label, count }
  rows.forEach((row) => {
    const canonical = canonicalizeReasonText(row?.reason_for_call || "Unknown");
    if (!canonical) return;
    const key = canonical.toLowerCase();
    const existing = freqByKey.get(key);
    if (existing) existing.count += 1;
    else freqByKey.set(key, { label: canonical, count: 1 });
  });

  const sortedCandidates = [...freqByKey.values()].sort((a, b) => b.count - a.count);
  const buckets = []; // { label, lowerLabel, count }
  sortedCandidates.forEach(({ label, count }) => {
    const lowerLabel = label.toLowerCase();
    const parent = buckets.find((b) => isReasonPrefixMatch(b.lowerLabel, lowerLabel));
    if (parent) {
      parent.count += count;
    } else {
      buckets.push({ label, lowerLabel, count });
    }
  });

  const sorted = buckets.sort((a, b) => b.count - a.count);
  if (sorted.length <= topN) return sorted;
  const top = sorted.slice(0, topN - 1);
  const othersCount = sorted.slice(topN - 1).reduce((s, b) => s + b.count, 0);
  top.push({ label: "Others", lowerLabel: "others", count: othersCount, isOthers: true });
  return top;
};

/** True if a raw row reason belongs to the given canonical (merged) bucket label. */
const reasonMatchesCanonicalLabel = (rawReason, canonicalLabel) => {
  const a = canonicalizeReasonText(rawReason).toLowerCase();
  const b = canonicalizeReasonText(canonicalLabel).toLowerCase();
  if (!a || !b) return false;
  return a.length <= b.length ? isReasonPrefixMatch(a, b) : isReasonPrefixMatch(b, a);
};

/**
 * True if a row's raw reason belongs to the selected Top Reasons filter label.
 * `mode` is the active bucket set (see getReasonBucketsForMode) — the same
 * label can exist in more than one set (e.g. "To clarify a design or SOD
 * process" is both a Custom Order inbound and outbound bucket), so the filter
 * has to be resolved against the set the user actually clicked in.
 */
const reasonMatchesSelectedFilter = (rowReason, selectedFilter, mode, topReasonLabels = []) => {
  if (!selectedFilter) return true;
  const filter = String(selectedFilter).trim();
  if (!filter) return true;
  if (filter === "Others") {
    const modeBuckets = getReasonBucketsForMode(mode);
    if (modeBuckets) {
      const bucket = normalizeReasonBucket(rowReason, mode);
      if (bucket == null) return true;
      return !topReasonLabels.includes(bucket);
    }
    return !topReasonLabels.some((l) => l !== "Others" && reasonMatchesCanonicalLabel(rowReason, l));
  }
  const modeBuckets = getReasonBucketsForMode(mode);
  if (modeBuckets && modeBuckets.includes(filter)) {
    return normalizeReasonBucket(rowReason, mode) === filter;
  }
  return reasonMatchesCanonicalLabel(rowReason, filter);
};

const computeOthersTopCallReasonLabels = (chartRows, mode) => {
  const modeBuckets = getReasonBucketsForMode(mode);
  if (modeBuckets) {
    return modeBuckets.length > 4 ? modeBuckets.slice(0, 4) : modeBuckets.slice(0, -1);
  }
  return buildTopReasonBuckets(chartRows, 5)
    .filter((b) => b.label !== "Others")
    .map((b) => b.label);
};

/**
 * Department that drives the Top Reasons bucket set for supervisors/team
 * members, who never set the department filter explicitly (it stays "") but
 * are nonetheless scoped to a single department's rows.
 */
const SUPERVISOR_DEPARTMENTS = {
  "janelle fernuik": "Account Executive",
  "john hanna": "Custom Order",
  "tracy verry": "Custom Order",
  "christine dahdah": "Concierge",
  "peter kurani": "Concierge",
  "barbara ryan": "Customer Service",
  "melina vargas": "Customer Service",
};

const normalizeNameKey = (v) => String(v || "").trim().toLowerCase();

// Account Executives are a department of their own (outbound sales). They are
// identified by their supervisor (Janelle Fernuik) and by advisor name.
// Mapping by supervisor keeps GREAT rows correct; the name set covers Amazon
// Connect rows that may lack a per-row supervisor. See getRowDepartment.
const ACCOUNT_EXECUTIVE_DEPARTMENT = "Account Executive";
const ACCOUNT_EXECUTIVE_SUPERVISOR_KEY = normalizeNameKey("Janelle Fernuik");

const ACCOUNT_EXECUTIVE_ONLY_NAME_KEYS = new Set(
  [
    "Joelle Jreije",
    "Razmig AlMayan",
    "Inderjit Singh",
    "Belma Yucel",
    "Rachel Hayduk",
    "Emma Lanka",
    "Janelle Fernuik",
  ].map(normalizeNameKey)
);

// Placeholder supervisor values from the DB that should never appear in the UI
// or contribute to stats/charts. Matched case-insensitively with optional brackets.
const isPlaceholderSupervisor = (value) => {
  if (value === null || value === undefined) return false;
  const stripped = String(value)
    .trim()
    .toLowerCase()
    .replace(/^\[+/, "")
    .replace(/\]+$/, "")
    .trim();
  return stripped === "insert supervisor name";
};

const filterPlaceholderSupervisorRows = (rows) =>
  Array.isArray(rows)
    ? rows.filter((row) => !isPlaceholderSupervisor(row?.supervisor))
    : [];

const resolveCutoverAdvisorKey = (name) => {
  const text = String(name || "").trim();
  if (!text) return "";
  if (AGENT_TEAM_CUTOVERS[text]) return text;
  if (normalizeNameKey(text) === "joyce") return "Joyce Dagher";
  return text;
};

const getAgentCutoverPeriodForRow = (row) => {
  const name = resolveCutoverAdvisorKey(row?.advisor_name);
  if (!name) return null;
  const cutover = AGENT_TEAM_CUTOVERS[name];
  if (!cutover) return null;
  const rowDate = resolveRowDate(row);
  const cutoffDate = parseRowDate(cutover.cutoffDate);
  // Without a reliable row date, assume the current (after-cutoff) assignment.
  if (!rowDate || !cutoffDate) return cutover.after;
  return rowDate < cutoffDate ? cutover.before : cutover.after;
};

/**
 * True if `supervisorName` is the currently responsible supervisor for this
 * row. For rows covered by an AGENT_TEAM_CUTOVERS entry this uses the period's
 * supervisors list (so a pre-cutover row is "owned" by the old supervisors and
 * a post-cutover row by the new ones). For everyone else it falls back to a
 * case-insensitive match against the DB-stored row.supervisor value, treating
 * co-managers (e.g. Barbara/Melina, John/Tracy) as equivalent.
 */
const isSupervisorResponsibleForRow = (supervisorName, row) => {
  if (!supervisorName) return true;
  const period = getAgentCutoverPeriodForRow(row);
  if (period) {
    const sup = normalizeNameKey(supervisorName);
    return period.supervisorsResponsible.some(
      (s) => normalizeNameKey(s) === sup
    );
  }
  const rowSup = row?.supervisor;
  if (!rowSup) return false;
  const a = normalizeNameKey(supervisorName);
  const b = normalizeNameKey(rowSup);
  if (a === b) return true;
  return (CO_SUPERVISOR_GROUPS || []).some((group) => {
    const keys = group.map(normalizeNameKey);
    return keys.includes(a) && keys.includes(b);
  });
};

/**
 * Remove duplicate GREAT rows based on the physical uniqueness of a call:
 * a single advisor cannot be on two calls at the same date + time, so the
 * tuple (advisor_name, supervisor, date_of_call, time_of_call) uniquely
 * identifies a call. Any row sharing that tuple is a re-import or
 * re-evaluation of the same call and should be collapsed.
 *
 * Everything else (total_score, rubric answers, retailer text, audio URL,
 * transcript) is descriptive data *about* that call. Allowing those to
 * differ between rows — and still deduping — handles all the real-world
 * edge cases we've observed:
 *   - presigned audio/transcript URLs changing between ingest runs
 *   - retailer strings formatted differently ("Acct: C05199" vs "/ Account C05199")
 *   - a human re-scoring the same call with slightly different rubric answers
 *
 * Within a group of duplicates, keep the row with the largest numeric
 * sr_no (most recently inserted evaluation).
 */
const GREAT_DEDUP_FIELDS = [
  "advisor_name",
  "supervisor",
  "date_of_call",
  "time_of_call",
];

/**
 * Canonicalize name-ish fields so formatting variants (extra spaces,
 * punctuation, case) don't bypass dedup:
 *   "Joe Chahwan " vs "joe chahwan" vs "Joe  Chahwan" → "joechahwan"
 */
const canonicalizeText = (v) => {
  if (v === null || v === undefined) return "";
  return String(v).toLowerCase().replace(/[^a-z0-9]/g, "");
};

/**
 * Canonicalize date_of_call. Accepts "2026-01-08", "2026-01-08 00:00:00",
 * "01/08/2026", etc., and reduces them all to YYYYMMDD.
 */
const canonicalizeDate = (v) => {
  if (v === null || v === undefined) return "";
  const str = String(v).trim();
  if (!str || str === "0000-00-00") return "";
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}${iso[2]}${iso[3]}`;
  const us = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    const mm = us[1].padStart(2, "0");
    const dd = us[2].padStart(2, "0");
    return `${us[3]}${mm}${dd}`;
  }
  return str.replace(/[^0-9]/g, "");
};

/**
 * Canonicalize time_of_call to HHMMSS (digits only). Handles "03:58:54",
 * "3:58:54", "03:58:54 AM", "03:58", etc. Missing seconds → "00".
 */
const canonicalizeTime = (v) => {
  if (v === null || v === undefined) return "";
  const str = String(v).trim().toLowerCase();
  if (!str) return "";
  const m = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/);
  if (!m) return str.replace(/[^0-9]/g, "");
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  const ss = m[3] || "00";
  const ampm = m[4];
  if (ampm === "pm" && hh < 12) hh += 12;
  if (ampm === "am" && hh === 12) hh = 0;
  return `${String(hh).padStart(2, "0")}${mm}${ss}`;
};

const GREAT_DEDUP_NORMALIZERS = {
  advisor_name: canonicalizeText,
  supervisor: canonicalizeText,
  date_of_call: canonicalizeDate,
  time_of_call: canonicalizeTime,
};

const dedupeGreatRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const defaultNorm = (v) =>
    v === null || v === undefined ? "" : String(v).trim();
  const normField = (field, value) => {
    const fn = GREAT_DEDUP_NORMALIZERS[field];
    return fn ? fn(value) : defaultNorm(value);
  };
  // Safety net: if advisor_name, date_of_call, or time_of_call is missing,
  // we can't confidently identify the call — leave the row as distinct
  // (use the sr_no as the "key" so every such row is unique).
  const buildKey = (r) => {
    const parts = GREAT_DEDUP_FIELDS.map((f) => normField(f, r?.[f]));
    const advisor = parts[0];
    const date = parts[2];
    const time = parts[3];
    if (!advisor || !date || !time) {
      return `__incomplete__\u0001${r?.sr_no ?? Math.random()}`;
    }
    return parts.join("\u0001");
  };

  // Deterministic ordering: the same row always wins across re-fetches and
  // re-renders, regardless of the order the API returned them in.
  // Primary: highest numeric sr_no (latest evaluation).
  // Secondary: latest `created_at` (parsed as a timestamp; invalid = -Infinity).
  // Tertiary: raw sr_no string compare (stable lexicographic).
  const sorted = rows.slice().sort((a, b) => {
    const aSr = parseInt(a?.sr_no, 10);
    const bSr = parseInt(b?.sr_no, 10);
    const aSrOk = !isNaN(aSr);
    const bSrOk = !isNaN(bSr);
    if (aSrOk && bSrOk && aSr !== bSr) return bSr - aSr;
    if (aSrOk && !bSrOk) return -1;
    if (!aSrOk && bSrOk) return 1;
    const aAt = Date.parse(a?.created_at || "");
    const bAt = Date.parse(b?.created_at || "");
    const aAtOk = !isNaN(aAt);
    const bAtOk = !isNaN(bAt);
    if (aAtOk && bAtOk && aAt !== bAt) return bAt - aAt;
    if (aAtOk && !bAtOk) return -1;
    if (!aAtOk && bAtOk) return 1;
    return String(b?.sr_no ?? "").localeCompare(String(a?.sr_no ?? ""));
  });

  const seen = new Map();
  sorted.forEach((row) => {
    const key = buildKey(row);
    if (!seen.has(key)) seen.set(key, row);
  });
  return Array.from(seen.values());
};

// ---------------------------------------------------------------------------
// Weekly data cache — the underlying GREAT/Amazon Connect data source is only
// refreshed by the team once a week (Fridays), so re-fetching everything from
// the API on every page load is pure wasted wait time. We cache the last
// successful fetch in localStorage and only hit the network again once we've
// crossed into a new "week" (the most recent Friday 00:00 local time). A
// manual search/submit still always goes to the network so users aren't
// stuck looking at stale data if they explicitly ask for a different range.
// ---------------------------------------------------------------------------
// Bumped to v2 when the default fetch window changed back to a full year —
// this invalidates any 3-month cache already sitting in someone's browser.
const GREAT_DATA_CACHE_KEY = "great_dashboard_cache_v7";
const AMAZON_CONNECT_CACHE_KEY = "great_dashboard_ac_cache_v4";
const GREAT_DIGITAL_CACHE_KEY = "great_dashboard_digital_cache_v2";

// The most recent Friday 00:00 (local time). If today IS Friday, that's
// today at midnight — i.e. any cache written before today is already stale.
const getMostRecentFridayBoundary = () => {
  const now = new Date();
  const daysSinceFriday = (now.getDay() - 5 + 7) % 7; // Fri=5
  const boundary = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceFriday);
  boundary.setHours(0, 0, 0, 0);
  return boundary;
};

const readDataCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.cachedAt || !Array.isArray(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const isDataCacheFresh = (cache) => {
  if (!cache) return false;
  const cachedAt = new Date(cache.cachedAt);
  if (isNaN(cachedAt.getTime())) return false;
  return cachedAt.getTime() >= getMostRecentFridayBoundary().getTime();
};

const writeDataCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ data, cachedAt: new Date().toISOString() }));
  } catch (e) {
    // Quota exceeded or storage disabled — caching is a nice-to-have, so
    // just skip it rather than let it break the actual data fetch.
    console.warn(`[GREAT] Could not write cache for ${key}:`, e);
  }
};

// Production great_data must filter by date in PHP (see php/great_data_memory_fix.php).
// Chunking/bisecting in the browser cannot fix a server that json_encodes the whole table.
const GREAT_VOICE_PRODUCTION_UNAVAILABLE_MSG =
  "GREAT voice scores need a server-side API update (deploy php/great_data_memory_fix.php on ai_dashboard.php). Email, Live Chat, and SMS digital QA still work.";

const isLocalGreatDataProxy = () =>
  process.env.NODE_ENV === "development" ||
  (typeof window !== "undefined" && window.location.hostname === "localhost");

const fetchGreatDataRawWindow = async (fromdate, todate) => {
  if (isLocalGreatDataProxy()) {
    const resp = await fetch("/api/great-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fromdate, todate }),
    });
    let json;
    try {
      json = await resp.json();
    } catch {
      throw new Error("GREAT voice data proxy returned invalid JSON");
    }
    if (!resp.ok || !json?.ok) {
      throw new Error(json?.error || `GREAT data proxy failed (${resp.status})`);
    }
    return Array.isArray(json.rows) ? json.rows : [];
  }

  const params = new URLSearchParams();
  params.set("query", "great_data");
  if (fromdate) params.set("fromdate", fromdate);
  if (todate) params.set("todate", todate);

  const url = `${APIURL}?${params.toString()}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

  const text = await resp.text();
  const looksLikePhpFatal =
    /fatal error/i.test(text) ||
    /allowed memory size/i.test(text) ||
    /<\s*br\s*\/?>/i.test(text);
  try {
    const json = JSON.parse(text);
    if (json && typeof json === "object" && !Array.isArray(json)) {
      if (json.error) {
        const err = new Error(String(json.error));
        err.apiError = true;
        err.phpFatal = /memory/i.test(String(json.error));
        throw err;
      }
      return [];
    }
    return Array.isArray(json) ? json : [];
  } catch (parseError) {
    if (parseError.apiError || parseError.phpFatal) throw parseError;
    console.error("JSON parse error:", parseError);
    console.error("Response text:", text.substring(0, 200));
    const err = new Error(
      looksLikePhpFatal
        ? GREAT_VOICE_PRODUCTION_UNAVAILABLE_MSG
        : `Invalid JSON response from API. Response: ${text.substring(0, 100)}`
    );
    err.invalidJson = true;
    err.phpFatal = looksLikePhpFatal;
    throw err;
  }
};

// The list payload omits transcript + performance_snapshot (the bulk of great_data).
// They are fetched for a single call when its detail popup opens.
const fetchGreatRowDetail = async (srNo) => {
  if (isLocalGreatDataProxy()) {
    const resp = await fetch("/api/great-data-detail", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sr_no: srNo }),
    });
    const json = await resp.json();
    if (!resp.ok || !json?.ok) throw new Error(json?.error || "Detail request failed");
    return json.row || null;
  }
  const params = new URLSearchParams({ query: "great_data_detail", sr_no: String(srNo) });
  const resp = await fetch(`${APIURL}?${params.toString()}`);
  const json = await resp.json();
  if (!resp.ok || !json || json.error) throw new Error(json?.error || "Detail request failed");
  return Array.isArray(json) ? json[0] || null : json;
};

const fetchGreatDataForRange = async (fromdate, todate) => {
  if (isLocalGreatDataProxy()) {
    return fetchGreatDataRawWindow(fromdate, todate);
  }
  // One production request — date-filtered PHP returns the range; unfixed PHP OOMs on any window.
  try {
    return await fetchGreatDataRawWindow(fromdate, todate);
  } catch (e) {
    if (e.phpFatal || e.invalidJson || e.apiError) {
      throw new Error(GREAT_VOICE_PRODUCTION_UNAVAILABLE_MSG);
    }
    throw e;
  }
};

// ---------------------------------------------------------------------------
// Customer Service KPI definitions
// ---------------------------------------------------------------------------
// Service-level thresholds, in seconds. The two cut-offs give the SL 30s /
// SL 60s pair; the gap between them is the 31-60s band.
const SL_FAST_THRESHOLD = 30;
const SL_SLOW_THRESHOLD = 60;

// Connect After Call Work is typically capped at 30 seconds. A handful of
// rows can carry a larger stamp (usually a leaked chat session); clamp so
// wrap-up cannot dominate phone-time.
const CS_ACW_CAP_SECONDS = 30;

const departmentForConnectRow = (row) => {
  const period = getAgentCutoverPeriodForRow(row);
  if (period?.department) return period.department;
  const username = getAmazonConnectAgentUsername(row);
  const profile = amazonConnectDirectoryById.get(
    normalizeAmazonConnectId(username)
  );
  const advisorName = row?.advisor_name || profile?.fullName || "";
  if (row && normalizeNameKey(row.supervisor) === ACCOUNT_EXECUTIVE_SUPERVISOR_KEY) {
    return ACCOUNT_EXECUTIVE_DEPARTMENT;
  }
  if (advisorName && ACCOUNT_EXECUTIVE_ONLY_NAME_KEYS.has(normalizeNameKey(advisorName))) {
    return ACCOUNT_EXECUTIVE_DEPARTMENT;
  }
  if (row?.department) return String(row.department);
  return profile?.department || "";
};

const isCustomerServiceDept = (department) =>
  String(department || "").toLowerCase() === "customer service";

/** Overall KPI voice rows: Customer Service only (respects team cutovers). */
const isCsKpiAgentRow = (row) => isCustomerServiceDept(departmentForConnectRow(row));

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
};

const csPercent = (numerator, denominator) =>
  denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : null;

const median = (values) => {
  if (!values.length) return null;
  const s = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

const mean = (values) => {
  if (!values.length) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
};

// Productive hours per agent-day (8h shift minus 1.5h breaks/meetings).
const ASSUMED_SHIFT_HOURS = 6.5;
const ASSUMED_SHIFT_SECONDS = ASSUMED_SHIFT_HOURS * 60 * 60;

const CS_UTIL_CHANNELS = ["inbound", "outbound", "email", "liveChat", "sms"];

const emptyCsUtilization = () => ({
  inboundPct: null,
  outboundPct: null,
  emailPct: null,
  liveChatPct: null,
  smsPct: null,
  utilizedPct: null,
});

const csActiveUtilDays = (days) =>
  (days || []).filter((day) =>
    CS_UTIL_CHANNELS.some((key) => (Number(day?.[key]) || 0) > 0)
  );

/**
 * Utilization for one agent in the KPI window:
 * sum each channel's seconds across all active days ÷ (active days × 6.5 h capacity).
 * Channel % shares add up to % Utilized (not a flat hourly constant alone).
 */
const csUtilizationFromDays = (days) => {
  const active = csActiveUtilDays(days);
  if (!active.length) return emptyCsUtilization();
  const capacitySeconds = active.length * ASSUMED_SHIFT_SECONDS;
  const totals = Object.fromEntries(CS_UTIL_CHANNELS.map((key) => [key, 0]));
  active.forEach((day) => {
    CS_UTIL_CHANNELS.forEach((key) => {
      totals[key] += Number(day[key]) || 0;
    });
  });
  const tenths = CS_UTIL_CHANNELS.map((key) =>
    Math.round((totals[key] / capacitySeconds) * 1000)
  );
  const [inboundPct, outboundPct, emailPct, liveChatPct, smsPct] = tenths.map((t) => t / 10);
  return {
    inboundPct,
    outboundPct,
    emailPct,
    liveChatPct,
    smsPct,
    utilizedPct: tenths.reduce((sum, t) => sum + t, 0) / 10,
    activeDays: active.length,
    productiveSeconds: CS_UTIL_CHANNELS.reduce((sum, key) => sum + totals[key], 0),
    capacitySeconds,
  };
};

/** Team % Utilized: pooled productive seconds ÷ pooled agent-day capacity. */
const csTeamUtilizationPct = (rows) => {
  let productiveSeconds = 0;
  let capacitySeconds = 0;
  (rows || []).forEach(({ parts }) => {
    if (!parts?.capacitySeconds) return;
    productiveSeconds += parts.productiveSeconds || 0;
    capacitySeconds += parts.capacitySeconds;
  });
  if (capacitySeconds <= 0) return null;
  return Math.round((productiveSeconds / capacitySeconds) * 1000) / 10;
};

const mergeCsDailyBuckets = (voiceByDay, digitalByDay) => {
  const keys = new Set([
    ...(voiceByDay ? voiceByDay.keys() : []),
    ...(digitalByDay ? digitalByDay.keys() : []),
  ]);
  const days = [];
  keys.forEach((key) => {
    const voice = voiceByDay?.get(key) || {};
    const digital = digitalByDay?.get(key) || {};
    days.push({
      inbound: voice.inbound || 0,
      outbound: voice.outbound || 0,
      email: digital.email || 0,
      liveChat: digital.liveChat || 0,
      sms: digital.sms || 0,
    });
  });
  return days;
};

// A contact counts as "answered" once it actually reached a live agent.
// Amazon Connect stamps Agent_ConnectedToAgentTimestamp at that moment; the
// interaction duration is the fallback for older rows missing the timestamp.
const hasRealConnectStamp = (raw) => {
  const s = String(raw || "").trim();
  return Boolean(s) && !s.startsWith("0000");
};

const wasAnsweredByAgent = (row) => {
  const interaction = toNumber(row?.Agent_AgentInteractionDuration);
  // 0 talk time means the agent never took the call, even if a timestamp exists.
  if (interaction === 0) return false;
  if (hasRealConnectStamp(row?.Agent_ConnectedToAgentTimestamp)) return true;
  if (!hasAgentUsername(row)) return false;
  return interaction !== null && interaction > 0;
};

const presentedCount = (row) => {
  const attempts = toNumber(row?.AgentConnectionAttempts);
  if (attempts !== null && attempts > 0) return attempts;
  return 1;
};

const getInitiationMethod = (row) =>
  String(row?.InitiationMethod || "").trim().toUpperCase();

/**
 * Voice calls only. The same feed also carries chat and SMS contacts, which
 * arrive with InitiationMethod "API"/"DISCONNECT", a Chat/SMS queue and no
 * CustomerEndpoint_Type. They must be excluded from call KPIs: they are ~2% of
 * contacts but a third of all interaction time, because a chat session stays
 * open for hours (up to 25) and its "interaction duration" is session lifetime
 * rather than time actually worked.
 *
 * Anything positively identifiable as chat/SMS is excluded; contacts we cannot
 * classify are kept, so an unexpected feed change fails toward over-counting
 * calls rather than silently dropping them.
 */
const isVoiceContact = (row) => {
  const endpointType = String(row?.CustomerEndpoint_Type || "").trim().toUpperCase();
  if (endpointType === "TELEPHONE_NUMBER") return true;

  const method = getInitiationMethod(row);
  if (method === "API" || method === "DISCONNECT") return false;

  const queue = String(row?.Queue_Name || "").toLowerCase();
  if (queue.includes("chat") || queue.includes("sms")) return false;

  return true;
};

// Missed inbound: nobody picked up. Includes empty / "0" agent usernames
// (never routed) and rows that rang to a rep but were not answered.
const isUnansweredInboundVoice = (row) =>
  isVoiceContact(row) &&
  getInitiationMethod(row) === "INBOUND" &&
  !wasAnsweredByAgent(row);

/**
 * The Customer Service KPI pack, computed from Amazon Connect contact records.
 *
 * Service-level and wait-time KPIs are restricted to INBOUND contacts, because
 * queue time is only meaningful for calls that actually queued — outbound and
 * agent-to-agent transfers carry Queue_Duration = 0 and would otherwise
 * inflate every "answered fast" percentage.
 */
const computeCsKpis = (rows, scope = "all") => {
  // The feed returns a small number of repeated contact records (~1.5% of rows
  // carry a ContactId already seen), which would otherwise double-count calls,
  // hold time and connection attempts. Keep the first row per ContactId; rows
  // with no ContactId at all are kept as-is rather than collapsed into one.
  const seenContactIds = new Set();
  const unique = (Array.isArray(rows) ? rows : []).filter((row) => {
    const id = row?.ContactId;
    if (!id) return true;
    if (seenContactIds.has(id)) return false;
    seenContactIds.add(id);
    return true;
  });
  const duplicateRowsRemoved =
    (Array.isArray(rows) ? rows.length : 0) - unique.length;

  const voice = unique.filter(isVoiceContact);
  const nonVoiceExcluded = unique.length - voice.length;

  // Scope lets the Summary view flip between all calls, inbound only and
  // outbound only without leaving the page. Transfers and callbacks arrive at
  // the agent like inbound work, so they group with inbound.
  const list =
    scope === "inbound"
      ? voice.filter((r) => getInitiationMethod(r) !== "OUTBOUND")
      : scope === "outbound"
      ? voice.filter((r) => getInitiationMethod(r) === "OUTBOUND")
      : voice;

  const inbound = [];
  let outboundCount = 0;
  let transferCount = 0;
  let callbackCount = 0;

  list.forEach((row) => {
    const method = getInitiationMethod(row);
    if (method === "OUTBOUND") outboundCount += 1;
    else if (method === "TRANSFER") transferCount += 1;
    else if (method === "CALLBACK") callbackCount += 1;
    if (method === "INBOUND") inbound.push(row);
  });

  const answered = inbound.filter(wasAnsweredByAgent);
  const inboundPresented = inbound.reduce((sum, row) => sum + presentedCount(row), 0);
  const inboundHandledCount = answered.length;
  const inboundAbandonedCount = Math.max(0, inboundPresented - inboundHandledCount);

  // Wait time for answered inbound calls — queue time only, measured from the
  // system answering to a live agent connecting.
  const queueWaits = [];
  let band30 = 0;
  let band60 = 0;
  let bandOver = 0;
  answered.forEach((row) => {
    const wait = toNumber(row?.Queue_Duration);
    if (wait === null || wait < 0) return;
    queueWaits.push(wait);
    if (wait <= SL_FAST_THRESHOLD) band30 += 1;
    else if (wait <= SL_SLOW_THRESHOLD) band60 += 1;
    else bandOver += 1;
  });

  // Hold time. The feed carries only a per-call TOTAL hold duration
  // (Agent_CustomerHoldDuration) — there is no hold-count field — so multiple
  // holds within one call are already summed together, and the average below
  // is per held CALL, not per individual hold.
  const holdDurations = [];
  list.forEach((row) => {
    const hold = toNumber(row?.Agent_CustomerHoldDuration);
    if (hold !== null && hold > 0) holdDurations.push(hold);
  });

  // Per-agent roll-up, used for the response-time bands and answer rate.
  const byAgent = new Map();
  const bump = (key, name) => {
    if (!byAgent.has(key)) {
      byAgent.set(key, {
        key,
        name,
        handled: 0,
        inboundOffered: 0,
        inboundHandled: 0,
        inboundAbandoned: 0,
        outbound: 0,
        band30: 0,
        band60: 0,
        bandOver: 0,
        waits: [],
        attempts: 0,
        routedHandled: 0,
        holds: 0,
        holdDurations: [],
        handlingSeconds: 0,
        holdSeconds: 0,
        maxHold: 0,
        department: "",
        days: new Set(),
      });
    }
    return byAgent.get(key);
  };

  list.forEach((row) => {
    const username = getAmazonConnectAgentUsername(row);
    const key = normalizeAmazonConnectId(username);
    if (!key) return;
    const entry = bump(key, row?.advisor_name || username);
    const method = getInitiationMethod(row);
    const isAnswered = wasAnsweredByAgent(row);
    if (!entry.department) {
      entry.department = departmentForConnectRow(row);
      const profile = amazonConnectDirectoryById.get(key);
      if (!entry.name || entry.name === username) {
        entry.name = profile?.fullName || entry.name;
      }
    }

    if (isAnswered) entry.handled += 1;
    if (method === "OUTBOUND") entry.outbound += 1;

    // Connection attempts are only stamped on routed contacts (inbound,
    // transfers, callbacks) and are 0 on outbound dials, so the handled count
    // paired with them has to be restricted the same way or the answer rate
    // exceeds 100%.
    const attempts = toNumber(row?.AgentConnectionAttempts);
    if (attempts !== null && attempts > 0) {
      entry.attempts += attempts;
      if (isAnswered) entry.routedHandled += 1;
    }

    const hold = toNumber(row?.Agent_CustomerHoldDuration);
    if (hold !== null && hold > 0) {
      entry.holds += 1;
      entry.holdSeconds += hold;
      entry.holdDurations.push(hold);
      if (hold > entry.maxHold) entry.maxHold = hold;
    }

    // Time demonstrably spent working a contact: talking, holding the customer,
    // and after-call wrap-up. Days worked are counted from the date the agent
    // connected — the only timestamp with a usable date (InitiationTimestamp
    // and DisconnectTimestamp arrive zero-dated as 0000-00-00).
    if (isAnswered) {
      entry.handlingSeconds +=
        (toNumber(row?.Agent_AgentInteractionDuration) || 0) + (hold || 0);
      const connectedAt = String(row?.Agent_ConnectedToAgentTimestamp || "");
      let day = connectedAt.slice(0, 10);
      if (!day || day.startsWith("0000")) {
        const fallback = getCsContactDate(row);
        if (fallback) {
          const mm = String(fallback.getMonth() + 1).padStart(2, "0");
          const dd = String(fallback.getDate()).padStart(2, "0");
          day = `${fallback.getFullYear()}-${mm}-${dd}`;
        }
      }
      if (day && !day.startsWith("0000")) entry.days.add(day);
    }

    if (method === "INBOUND") {
      entry.inboundOffered += presentedCount(row);
      if (isAnswered) {
        entry.inboundHandled += 1;
        const wait = toNumber(row?.Queue_Duration);
        if (wait !== null && wait >= 0) {
          entry.waits.push(wait);
          if (wait <= SL_FAST_THRESHOLD) entry.band30 += 1;
          else if (wait <= SL_SLOW_THRESHOLD) entry.band60 += 1;
          else entry.bandOver += 1;
        }
      }
    }
  });

  const agents = [...byAgent.values()].map((a) => {
    const daysWorked = a.days.size;
    const inboundAbandoned = Math.max(0, a.inboundOffered - a.inboundHandled);
    return {
      ...a,
      inboundAbandoned,
      daysWorked,
      medianWait: a.waits.length > 0 ? median(a.waits) : null,
      averageWait: a.waits.length > 0 ? mean(a.waits) : null,
      sl30: csPercent(a.band30, a.waits.length),
      abandonedPct: csPercent(inboundAbandoned, a.inboundOffered),
      handledPct: csPercent(a.inboundHandled, a.inboundOffered),
      answerRate: csPercent(a.routedHandled, a.attempts),
      medianHold: a.holdDurations.length > 0 ? median(a.holdDurations) : null,
      typicalHold: a.holdDurations.length > 0 ? mean(a.holdDurations) : null,
      handlingHoursPerDay:
        daysWorked > 0
          ? Math.round((a.handlingSeconds / daysWorked / 3600) * 10) / 10
          : null,
      readyPct:
        daysWorked > 0
          ? csPercent(a.handlingSeconds, daysWorked * ASSUMED_SHIFT_SECONDS)
          : null,
    };
  });

  const totalHandled = list.filter(wasAnsweredByAgent).length;

  const totalAttempts = agents.reduce((sum, a) => sum + a.attempts, 0);
  const routedHandled = agents.reduce((sum, a) => sum + a.routedHandled, 0);

  const csAgents = agents.filter((a) => isCustomerServiceDept(a.department));
  const csInboundHandled = csAgents.reduce((s, a) => s + a.inboundHandled, 0);
  const csInboundPresented = csAgents.reduce((s, a) => s + a.inboundOffered, 0);
  const csInboundAbandoned = csAgents.reduce((s, a) => s + a.inboundAbandoned, 0);

  // Phone time is independent of the Inbound/Outbound toggle: a CS rep's
  // day includes outbound dials and transfers. Talk + hold, split by
  // direction. Transfers and callbacks count as inbound. Days are calendar
  // days with at least one answered voice call.
  const csProd = new Map();
  voice.forEach((row) => {
    const username = getAmazonConnectAgentUsername(row);
    const key = normalizeAmazonConnectId(username);
    if (!key) return;
    const profile = amazonConnectDirectoryById.get(key);
    const department = departmentForConnectRow(row);
    if (!isCustomerServiceDept(department)) return;
    if (!wasAnsweredByAgent(row)) return;
    if (!csProd.has(key)) {
      csProd.set(key, {
        key,
        name: row?.advisor_name || profile?.fullName || username,
        handled: 0,
        handlingSeconds: 0,
        inboundSeconds: 0,
        outboundSeconds: 0,
        byDay: new Map(),
        days: new Set(),
        department,
      });
    }
    const entry = csProd.get(key);
    entry.handled += 1;
    const hold = toNumber(row?.Agent_CustomerHoldDuration) || 0;
    const talkHold =
      (toNumber(row?.Agent_AgentInteractionDuration) || 0) + hold;
    entry.handlingSeconds += talkHold;
    const channel = getInitiationMethod(row) === "OUTBOUND" ? "outbound" : "inbound";
    if (channel === "outbound") entry.outboundSeconds += talkHold;
    else entry.inboundSeconds += talkHold;
    const connectedAt = String(row?.Agent_ConnectedToAgentTimestamp || "");
    let day = connectedAt.slice(0, 10);
    if (!day || day.startsWith("0000")) {
      const fallback = getCsContactDate(row);
      if (fallback) {
        const mm = String(fallback.getMonth() + 1).padStart(2, "0");
        const dd = String(fallback.getDate()).padStart(2, "0");
        day = `${fallback.getFullYear()}-${mm}-${dd}`;
      }
    }
    if (day && !day.startsWith("0000") && talkHold > 0) {
      entry.days.add(day);
      if (!entry.byDay.has(day)) entry.byDay.set(day, { inbound: 0, outbound: 0 });
      entry.byDay.get(day)[channel] += talkHold;
    }
  });

  const csProductivityAgents = [...csProd.values()]
    .map((a) => {
      const daysWorked = a.days.size;
      return {
        ...a,
        daysWorked,
        handlingHoursPerDay:
          daysWorked > 0
            ? Math.round((a.handlingSeconds / daysWorked / 3600) * 10) / 10
            : null,
        readyPct:
          daysWorked > 0
            ? csPercent(a.handlingSeconds, daysWorked * ASSUMED_SHIFT_SECONDS)
            : null,
      };
    })
    .filter((a) => a.daysWorked > 0);

  const csAgentDays = csProductivityAgents.reduce((s, a) => s + a.daysWorked, 0);
  const csHoursPerRep = csProductivityAgents
    .map((a) => a.handlingSeconds / a.daysWorked / 3600)
    .filter((v) => Number.isFinite(v));
  const csCallsPerRepPerDay = csProductivityAgents
    .map((a) => a.handled / a.daysWorked)
    .filter((v) => Number.isFinite(v));

  const sortedBySl = agents.slice().sort((a, b) => {
    if (a.sl30 === null && b.sl30 === null) return b.handled - a.handled;
    if (a.sl30 === null) return 1;
    if (b.sl30 === null) return -1;
    if (b.sl30 !== a.sl30) return b.sl30 - a.sl30;
    return (b.waits?.length || 0) - (a.waits?.length || 0);
  });

  return {
    hasData: list.length > 0,
    duplicateRowsRemoved,
    nonVoiceExcluded,

    // Service level & responsiveness
    sl30: csPercent(band30, queueWaits.length),
    sl60: csPercent(band30 + band60, queueWaits.length),
    answeredWithWait: queueWaits.length,
    medianWait: median(queueWaits),
    averageWait: mean(queueWaits),
    band30,
    band60,
    bandOver,

    // Volume & distribution
    // Received = times an inbound call was presented (AgentConnectionAttempts).
    // Handled = contacts a live agent picked up. Abandoned = presented − handled
    // (the rings nobody answered). Unique inbound rows can all be "eventually
    // answered" while still having missed presentations — that gap is abandoned.
    inboundOffered: inboundPresented,
    inboundHandled: inboundHandledCount,
    inboundAbandoned: inboundAbandonedCount,
    inboundHandledPct: csPercent(inboundHandledCount, inboundPresented),
    inboundAbandonedPct: csPercent(inboundAbandonedCount, inboundPresented),
    csInboundHandled,
    csInboundPresented,
    csInboundAbandoned,
    totalHandled,
    outboundCount,
    transferCount,
    callbackCount,

    // Hold
    holdCallCount: holdDurations.length,
    totalHoldSeconds: holdDurations.reduce((sum, v) => sum + v, 0),
    medianHold: median(holdDurations),
    typicalHold: mean(holdDurations),
    maxHold: holdDurations.length ? Math.max(...holdDurations) : null,

    // Productivity — Customer Service reps only, all voice (inbound +
    // outbound + transfers). Phone time, not occupancy.
    activeAgentCount: csProductivityAgents.length,
    avgCallsPerRep:
      csProductivityAgents.length > 0
        ? Math.round(
            (csProductivityAgents.reduce((s, a) => s + a.handled, 0) /
              csProductivityAgents.length) *
              10
          ) / 10
        : null,
    medianCallsPerDay:
      csCallsPerRepPerDay.length > 0
        ? Math.round(median(csCallsPerRepPerDay) * 10) / 10
        : null,
    totalAttempts,
    routedHandled,
    answerRate: csPercent(routedHandled, totalAttempts),

    avgHandlingMinutes:
      totalHandled > 0
        ? Math.round(
            (agents.reduce((s, a) => s + a.handlingSeconds, 0) / totalHandled / 60) *
              10
          ) / 10
        : null,
    totalAgentDays: csAgentDays,
    handlingHoursPerDay:
      csHoursPerRep.length > 0
        ? Math.round(median(csHoursPerRep) * 10) / 10
        : null,
    readyPct:
      csHoursPerRep.length > 0
        ? csPercent(median(csHoursPerRep) * 3600, ASSUMED_SHIFT_SECONDS)
        : null,
    csRepOnly: true,
    csTotalHandled: csProductivityAgents.reduce((s, a) => s + a.handled, 0),

    topAnswerers: csAgents
      .filter((a) => a.inboundHandled > 0)
      .sort((a, b) => b.inboundHandled - a.inboundHandled)
      .slice(0, 3)
      .map((a) => ({
        name: a.name,
        value: a.inboundHandled,
        count: a.inboundHandled,
      })),
    topHoldReps: agents
      .filter((a) => a.maxHold > 0)
      .sort((a, b) => b.maxHold - a.maxHold)
      .slice(0, 3)
      .map((a) => ({
        name: a.name,
        value: a.maxHold,
        count: a.holds,
      })),

    agents: sortedBySl,
    agentsByVolume: csProductivityAgents
      .slice()
      .sort((a, b) => b.handlingSeconds - a.handlingSeconds),
  };
};

// Formatting helpers for the KPI cards.
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const fmtCountPct = (count, pct) =>
  pct === null || pct === undefined ? fmtNum(count) : `${fmtNum(count)} (${fmtPct(pct)})`;
const fmtSecs = (v) =>
  v === null || v === undefined ? "—" : `${Math.round(v)}s`;
const fmtNum = (v) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString();
const fmtPair = (n, d, label) => {
  const pct = csPercent(n, d);
  const base = `${fmtNum(n)} of ${fmtNum(d)}${label ? ` ${label}` : ""}`;
  return pct == null ? base : `${base} (${fmtPct(pct)})`;
};
const fmtShare = (n, d) => {
  const pct = csPercent(n, d);
  return pct == null ? fmtNum(n) : `${fmtNum(n)} (${fmtPct(pct)})`;
};
const fmtDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return "—";
  }
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  return `${Math.round(s / 60)} min`;
};
const fmtHoldTotal = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return "—";
  }
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

/** Longest hold: minutes when >= 60s. */
const fmtHoldLongest = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return "—";
  }
  const s = Number(seconds);
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.round(s / 60)} min`;
};

const parseConnectTimestamp = (raw) => {
  const s = String(raw || "").trim();
  if (!s || s.startsWith("0000")) return null;
  const m = s.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] || 0),
      Number(m[5] || 0),
      Number(m[6] || 0)
    );
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Fallback date from the CTR Source path when Connect timestamps are zero-dated. */
const parseSourceDate = (sourceStr) => {
  const source = String(sourceStr || "");
  if (!source) return null;
  const stamped = source.match(/(\d{4})-(\d{1,2})-(\d{1,2})-\d{1,2}-\d{1,2}-\d{1,2}/);
  if (stamped) {
    const d = new Date(Number(stamped[1]), Number(stamped[2]) - 1, Number(stamped[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const path = source.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})\//);
  if (path && Number(path[2]) >= 1 && Number(path[2]) <= 12) {
    const d = new Date(Number(path[1]), Number(path[2]) - 1, Number(path[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** Best available call date. Prefer the agent-connect stamp — older rows zero-date InitiationTimestamp. */
const getCsContactDate = (row) =>
  parseConnectTimestamp(row?.Agent_ConnectedToAgentTimestamp) ||
  parseConnectTimestamp(row?.ConnectedToSystemTimestamp) ||
  parseConnectTimestamp(row?.Queue_EnqueueTimestamp) ||
  parseConnectTimestamp(row?.Agent_AfterContactWorkStartTimestamp) ||
  parseConnectTimestamp(row?.InitiationTimestamp) ||
  parseSourceDate(row?.Source) ||
  null;

const startOfLocalDay = (d) => {
  if (!d) return NaN;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return NaN;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
};

const CS_DATE_PRESETS = [
  { id: "ytd", label: "Year to date" },
  { id: "month", label: "This month" },
  { id: "3m", label: "Last 3 months" },
  { id: "6m", label: "Last 6 months" },
  { id: "12m", label: "Last 12 months" },
  { id: "30d", label: "Last 30 days" },
  { id: "7d", label: "Last 7 days" },
];

const formatCsRangeLabel = (range) => {
  if (!Array.isArray(range) || !range[0] || !range[1]) return "";
  const fmt = (d) =>
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt(range[0])} to ${fmt(range[1])}`;
};

const buildCsCalendarMonthPresets = (end) => {
  const close = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
  const year = close.getFullYear();
  const presets = [];
  for (let month = 0; month <= close.getMonth(); month += 1) {
    const start = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);
    const rangeEnd = endOfMonth.getTime() > close.getTime() ? close : endOfMonth;
    const label = start.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    presets.push({ id: `cal-${year}-${month}`, label, range: [start, rangeEnd] });
  }
  return presets;
};

const rangeForCsPreset = (id, end) => {
  const close = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
  const yearStart = new Date(close.getFullYear(), 0, 1);
  const clamp = (start) => (start.getTime() < yearStart.getTime() ? yearStart : start);
  if (String(id || "").startsWith("cal-")) {
    const parts = String(id).split("-");
    const year = parseInt(parts[1], 10);
    const month = parseInt(parts[2], 10);
    if (Number.isFinite(year) && Number.isFinite(month)) {
      const start = new Date(year, month, 1);
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);
      const rangeEnd = endOfMonth.getTime() > close.getTime() ? close : endOfMonth;
      return [start, rangeEnd];
    }
  }
  if (id === "month") return [new Date(close.getFullYear(), close.getMonth(), 1), close];
  if (id === "3m") return [clamp(new Date(close.getFullYear(), close.getMonth() - 2, 1)), close];
  if (id === "6m") return [clamp(new Date(close.getFullYear(), close.getMonth() - 5, 1)), close];
  if (id === "12m") return [clamp(new Date(close.getFullYear(), close.getMonth() - 11, 1)), close];
  if (id === "30d") {
    const start = new Date(close.getFullYear(), close.getMonth(), close.getDate() - 29);
    return [clamp(start), close];
  }
  if (id === "7d") {
    const start = new Date(close.getFullYear(), close.getMonth(), close.getDate() - 6);
    return [clamp(start), close];
  }
  return [yearStart, close];
};

const filterRowsByCsDate = (rows, range) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (!Array.isArray(range) || !range[0] || !range[1]) return rows;
  const start = startOfLocalDay(range[0]);
  const end = startOfLocalDay(range[1]);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return rows;
  return rows.filter((row) => {
    const d = getCsContactDate(row);
    if (!d) return false;
    const t = startOfLocalDay(d);
    if (!Number.isFinite(t)) return false;
    return t >= start && t <= end;
  });
};

/** KPI tile inside Overall KPIs (matches dashboard stat styling). */
const CsMetric = ({ label, value, hint, align = "center" }) => {
  const left = align === "left";
  return (
    <div
      className={`flex min-h-[5.5rem] flex-col justify-center rounded-xl border border-gray-200 bg-gray-50 px-3 py-4 dark:border-navy-700 dark:bg-navy-900 ${
        left ? "items-start text-left" : "items-center text-center"
      }`}
    >
      <p className="text-[11px] font-semibold leading-snug text-navy-700 dark:text-white">{label}</p>
      <p className="mt-2 text-2xl font-bold leading-none tabular-nums text-navy-700 dark:text-white">{value}</p>
      {hint ? (
        <p className="mt-2 max-w-full text-[10px] leading-snug text-navy-700 dark:text-gray-200">{hint}</p>
      ) : null}
    </div>
  );
};

export default function GreatAnalytics() {
  // Top-level KPIs (Total Calls Received, Overall Avg Score, etc.) and the
  // Customer Service KPI pack are for the calendar year to date — 1 January
  // through today — not a rolling 365-day window.
  const buildTodayEnd = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  };
  const buildDefaultStart = (end) => new Date(end.getFullYear(), 0, 1);
  const today = buildTodayEnd();
  const defaultStartDate = buildDefaultStart(today);
  const defaultRangeDays = Math.max(
    1,
    Math.round((today.getTime() - defaultStartDate.getTime()) / (24 * 60 * 60 * 1000)) + 1
  );

  // ----- Direction (Inbound | Outbound) -----------------------------------
  // The dashboard historically showed inbound calls only. A `direction` column
  // (0=inbound, 1=outbound) was added so rows now carry a direction. We expose
  // Outbound via a URL param (e.g. /great/outbound) plus an in-page toggle. The
  // param is optional; default is inbound. For supervisors/team members the
  // GREAT route is mounted at "/" (routes.js makes the first allowed route the
  // index), so the direction segment for them is "/outbound" — the layout
  // mounts both the base and a ":direction" route so useParams() works either
  // way.
  const routeParams = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const directionFromUrl = normalizeDirectionValue(routeParams.direction);
  const [directionFilter, setDirectionFilter] = React.useState(directionFromUrl);

  // Keep state in sync if the URL changes (back/forward nav, bookmark, etc.).
  React.useEffect(() => {
    setDirectionFilter((prev) => (prev === directionFromUrl ? prev : directionFromUrl));
  }, [directionFromUrl]);

  const isOutbound = directionFilter === "outbound";
  // "summary" is a third in-page view (alongside inbound/outbound). It renders a
  // combined overview instead of a single-direction dashboard. It is NOT the
  // default — an absent URL segment still resolves to inbound.
  const isSummary = directionFilter === "summary";
  const isDigitalChannel = DIGITAL_VIEW_KEYS.includes(directionFilter);

  const [allDigitalRows, setAllDigitalRows] = React.useState(() => {
    const cached = readDataCache(GREAT_DIGITAL_CACHE_KEY);
    return isDataCacheFresh(cached) ? cached.data : [];
  });
  const [digitalDataLoading, setDigitalDataLoading] = React.useState(false);
  const [digitalDataError, setDigitalDataError] = React.useState("");
  const digitalFetchInFlight = React.useRef(null);

  // Toggle handler: updates the URL (so it's bookmarkable) and the local state.
  // We derive the base path from the current URL by stripping any trailing
  // direction segment, which works for both /great/* (admins) and / (supervisors).
  const handleDirectionChange = (nextDirection) => {
    const normalized = normalizeDirectionValue(nextDirection);
    if (normalized === "live-chat" || normalized === "sms") {
      setDepartmentFilter("Customer Service");
    }
    setDirectionFilter(normalized);
    const base =
      routerLocation.pathname.replace(
        /\/(inbound|outbound|summary|email|live-chat|sms)\/?$/i,
        ""
      ) || "/";
    let target;
    const needsSegment =
      normalized === "outbound" ||
      normalized === "summary" ||
      DIGITAL_VIEW_KEYS.includes(normalized);
    if (needsSegment) {
      const seg = normalized;
      target = base === "/" ? `/${seg}` : `${base.replace(/\/$/, "")}/${seg}`;
    } else {
      target = base;
    }
    if (target !== routerLocation.pathname) {
      navigate(target);
    }
  };

  // Check user role: main, supervisor, or team member
  // Use state instead of useMemo to allow re-reading from localStorage
  const [isAdmin, setIsAdmin] = React.useState(() => {
    return localStorage.getItem("is_admin") === "true";
  });
  const [isSupervisor, setIsSupervisor] = React.useState(() => {
    return localStorage.getItem("is_supervisor") === "true";
  });
  const [isTeamMember, setIsTeamMember] = React.useState(() => {
    return localStorage.getItem("is_team_member") === "true";
  });
  const [supervisorName, setSupervisorName] = React.useState(() => {
    return localStorage.getItem("supervisor_name") || "";
  });
  const [supervisorTeamMembers, setSupervisorTeamMembers] = React.useState(() => {
    const stored = localStorage.getItem("supervisor_team_members");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  // Re-check localStorage on mount and when window gains focus (in case user logged in another tab)
  React.useEffect(() => {
    const checkRole = () => {
      const adminStatus = localStorage.getItem("is_admin") === "true";
      const supervisorStatus = localStorage.getItem("is_supervisor") === "true";
      const teamMemberStatus = localStorage.getItem("is_team_member") === "true";
      const supName = localStorage.getItem("supervisor_name") || "";
      const stored = localStorage.getItem("supervisor_team_members");
      let teamMembers = [];
      if (stored) {
        try {
          teamMembers = JSON.parse(stored);
        } catch (e) {
          teamMembers = [];
        }
      }

      // Get team member team list
      const teamListStored = localStorage.getItem("team_member_team_list");
      let teamMemberTeamList = [];
      if (teamListStored) {
        try {
          teamMemberTeamList = JSON.parse(teamListStored);
        } catch (e) {
          teamMemberTeamList = [];
        }
      }

      setIsAdmin(adminStatus);
      setIsSupervisor(supervisorStatus);
      setIsTeamMember(teamMemberStatus);
      setSupervisorName(supName);
      setSupervisorTeamMembers(teamMembers);
      setTeamMemberTeamList(teamMemberTeamList);

      // Debug logging
      console.log("Role check:", {
        isAdmin: adminStatus,
        isSupervisor: supervisorStatus,
        isTeamMember: teamMemberStatus,
        supervisorName: supName,
        teamMembersCount: teamMembers.length,
        teamMembers: teamMembers,
        teamMemberTeamListCount: teamMemberTeamList.length,
        teamMemberTeamList: teamMemberTeamList
      });
    };

    checkRole();
    window.addEventListener("focus", checkRole);
    return () => window.removeEventListener("focus", checkRole);
  }, []);

  const [teamMemberName, setTeamMemberName] = React.useState(() => {
    return localStorage.getItem("team_member_name") || "";
  });

  // A team member's own supervisor — the only way to tell which department
  // they belong to, since they never set the department filter themselves.
  const [teamMemberSupervisor, setTeamMemberSupervisor] = React.useState(() => {
    return localStorage.getItem("team_member_supervisor") || "";
  });

  const [teamMemberTeamList, setTeamMemberTeamList] = React.useState(() => {
    const stored = localStorage.getItem("team_member_team_list");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  // Update teamMemberName and team list when localStorage changes
  React.useEffect(() => {
    const checkTeamMember = () => {
      setTeamMemberName(localStorage.getItem("team_member_name") || "");
      setTeamMemberSupervisor(localStorage.getItem("team_member_supervisor") || "");
      const stored = localStorage.getItem("team_member_team_list");
      if (stored) {
        try {
          setTeamMemberTeamList(JSON.parse(stored));
        } catch (e) {
          setTeamMemberTeamList([]);
        }
      } else {
        setTeamMemberTeamList([]);
      }
    };
    checkTeamMember();
    window.addEventListener("focus", checkTeamMember);
    return () => window.removeEventListener("focus", checkTeamMember);
  }, []);

  const supervisorTeamNameKeys = React.useMemo(() => {
    const set = new Set();
    (supervisorTeamMembers || []).forEach((name) => {
      if (name) set.add(normalizeNameKey(name));
    });
    return set;
  }, [supervisorTeamMembers]);

  const digitalRowsForViewer = React.useCallback(
    (rows) => {
      const list = rows || [];
      if (isAdmin) return list;
      if (isSupervisor && supervisorName) {
        return list.filter((row) =>
          isDigitalRowVisibleToSupervisor(supervisorName, row, supervisorTeamNameKeys)
        );
      }
      if (isTeamMember && teamMemberName) {
        return list.filter(
          (row) => normalizeNameKey(row.advisor_name) === normalizeNameKey(teamMemberName)
        );
      }
      return list;
    },
    [
      isAdmin,
      isSupervisor,
      isTeamMember,
      supervisorName,
      teamMemberName,
      supervisorTeamNameKeys,
    ]
  );

  const [fromdate, setFromdate] = React.useState(formatDate(defaultStartDate));
  const [todate, setTodate] = React.useState(formatDate(today));
  const [dateRange, setDateRange] = React.useState([defaultStartDate, today]);

  const loadAllDigitalRows = React.useCallback(async ({ silent = false } = {}) => {
    if (digitalFetchInFlight.current) {
      return digitalFetchInFlight.current;
    }
    if (!silent) setDigitalDataLoading(true);
    setDigitalDataError("");
    const task = (async () => {
      try {
        const rows = await fetchAllDigitalRows({ fromdate, todate });
        setAllDigitalRows(rows);
        writeDataCache(GREAT_DIGITAL_CACHE_KEY, rows);
      } catch (e) {
        console.warn("Digital GREAT fetch:", e);
        setDigitalDataError(e.message || "Failed to load digital QA data");
      } finally {
        setDigitalDataLoading(false);
        digitalFetchInFlight.current = null;
      }
    })();
    digitalFetchInFlight.current = task;
    return task;
  }, [fromdate, todate]);

  React.useEffect(() => {
    loadAllDigitalRows({ silent: allDigitalRows.length > 0 });
  }, [loadAllDigitalRows]);

  const patchDigitalRowInCache = React.useCallback((updatedRow) => {
    if (!updatedRow?.sr_no) return;
    setAllDigitalRows((prev) => {
      const next = prev.map((r) =>
        String(r.sr_no) === String(updatedRow.sr_no) ? { ...r, ...updatedRow } : r
      );
      writeDataCache(GREAT_DIGITAL_CACHE_KEY, next);
      return next;
    });
  }, []);

  const removeDigitalRowFromCache = React.useCallback((sr_no) => {
    setAllDigitalRows((prev) => {
      const next = prev.filter((r) => String(r.sr_no) !== String(sr_no));
      writeDataCache(GREAT_DIGITAL_CACHE_KEY, next);
      return next;
    });
  }, []);

  const [advisorName, setAdvisorName] = React.useState("");
  const [supervisor, setSupervisor] = React.useState(() => {
    if (localStorage.getItem("is_supervisor") === "true") {
      return localStorage.getItem("supervisor_name") || "";
    }
    return "";
  });
  const [departmentFilter, setDepartmentFilter] = React.useState("");

  React.useEffect(() => {
    if (!isDigitalSummaryChannelVisible(directionFilter, departmentFilter)) {
      if (directionFilter === "live-chat" || directionFilter === "sms") {
        handleDirectionChange("email");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to department changes
  }, [departmentFilter]);

  // Role-based greeting shown at the top of every view:
  //   admin       -> "Hello Leaders,"
  //   manager     -> "Hello <their department> Manager," (Customer Service,
  //                  Custom Order, Concierge, Account Executive)
  //   team member -> "Hello <first name>,"
  // The manager line comes from the supervisor's OWN department, not from the
  // department filter, so it stays correct while they browse other departments.
  const greeting = React.useMemo(() => {
    if (isAdmin) return "Hello Leaders,";
    if (isSupervisor) {
      const dept = SUPERVISOR_DEPARTMENTS[normalizeNameKey(supervisorName)];
      return dept ? `Hello ${dept} Manager,` : "Hello Manager,";
    }
    if (isTeamMember && teamMemberName) {
      return `Hello ${teamMemberName.split(" ")[0]},`;
    }
    return "Hello,";
  }, [isAdmin, isSupervisor, supervisorName, isTeamMember, teamMemberName]);

  // Department whose Top Reasons bucket set applies. Admins pick it with the
  // department filter; supervisors and team members are already scoped to one
  // department, so it is derived from their supervisor instead.
  const reasonDepartment = React.useMemo(() => {
    const picked = String(departmentFilter || "").trim();
    if (picked) return picked;
    if (isSupervisor) return SUPERVISOR_DEPARTMENTS[normalizeNameKey(supervisorName)] || "";
    if (isTeamMember) return SUPERVISOR_DEPARTMENTS[normalizeNameKey(teamMemberSupervisor)] || "";
    return "";
  }, [departmentFilter, isSupervisor, supervisorName, isTeamMember, teamMemberSupervisor]);

  // Which curated Top Reasons bucket set to use, or null to fall back to the
  // generic "top 5 by frequency" list (see buildTopReasonBuckets). Customer
  // Service, Custom Order and Concierge each have curated sets for BOTH
  // directions; every other inbound view still uses the frequency fallback,
  // and every other outbound view shares the DEFAULT set.
  const reasonBucketMode = React.useMemo(() => {
    const dept = reasonDepartment.toLowerCase();
    if (isOutbound) {
      if (dept === "account executive") return "ae";
      if (dept === "customer service") return "customerServiceOutbound";
      if (dept === "custom order") return "customOrderOutbound";
      if (dept === "concierge") return "conciergeOutbound";
      return "default";
    }
    if (dept === "customer service") return "customerServiceInbound";
    if (dept === "custom order") return "customOrderInbound";
    if (dept === "concierge") return "conciergeInbound";
    return null;
  }, [isOutbound, reasonDepartment]);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]); // all rows from API
  const [page, setPage] = React.useState(0);
  const PAGE_SIZE = 30;
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [selectedRowDetailLoading, setSelectedRowDetailLoading] = React.useState(false);

  const openRowDetail = React.useCallback((row) => {
    setSelectedRow(row);
    // Already has the heavy fields (full-column API response) — nothing to fetch.
    if (!row?.sr_no || Object.prototype.hasOwnProperty.call(row, "transcript")) {
      setSelectedRowDetailLoading(false);
      return;
    }
    setSelectedRowDetailLoading(true);
    fetchGreatRowDetail(row.sr_no)
      .then((detail) => {
        const extra = {
          transcript: detail?.transcript ?? "",
          performance_snapshot: detail?.performance_snapshot ?? "",
        };
        setSelectedRow((cur) =>
          cur && String(cur.sr_no) === String(row.sr_no) ? { ...cur, ...extra } : cur
        );
      })
      .catch((e) => {
        console.warn("[GREAT] Could not load call detail:", e.message);
      })
      .finally(() => {
        setSelectedRowDetailLoading(false);
      });
  }, []);

  // Edit/delete state (admin only, inside view modal)
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editFormData, setEditFormData] = React.useState({});
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState("");
  const [editSuccess, setEditSuccess] = React.useState("");
  const [deleteConfirm, setDeleteConfirm] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  // Amazon Connect data state
  const [amazonConnectRows, setAmazonConnectRows] = React.useState([]);
  const [amazonConnectLoading, setAmazonConnectLoading] = React.useState(false);
  const [amazonConnectError, setAmazonConnectError] = React.useState("");

  // Dropdown state for customer service representatives and supervisor filters
  const [showAdvisorDropdown, setShowAdvisorDropdown] = React.useState(false);
  const [showSupervisorDropdown, setShowSupervisorDropdown] = React.useState(false);
  const [advisorSearchTerm, setAdvisorSearchTerm] = React.useState("");
  const [supervisorSearchTerm, setSupervisorSearchTerm] = React.useState("");
  const [retailerAccountFilter, setRetailerAccountFilter] = React.useState("");
  const [showRetailerAccountDropdown, setShowRetailerAccountDropdown] = React.useState(false);
  const [retailerAccountSearchTerm, setRetailerAccountSearchTerm] = React.useState("");
  const advisorDropdownRef = React.useRef(null);
  const supervisorDropdownRef = React.useRef(null);
  const retailerAccountDropdownRef = React.useRef(null);
  const [resultFilter, setResultFilter] = React.useState("all"); // all | met | not_met
  const [sentimentFilter, setSentimentFilter] = React.useState("all"); // all | positive | negative | neutral
  const [sentimentReasonFilter, setSentimentReasonFilter] = React.useState("all");
  const [errorsFilter, setErrorsFilter] = React.useState("all"); // all | yes | no
  const [showOthersReasonsModal, setShowOthersReasonsModal] = React.useState(false);
  const [individualRepErrorsFocus, setIndividualRepErrorsFocus] = React.useState(null);

  const [stats, setStats] = React.useState({
    total: 0,
    passCount: 0,
    failCount: 0,
    avgScore: 0,
    uniqueAdvisors: 0,
    uniqueSupervisors: 0,
    gradeA: 0,
    gradeB: 0,
    gradeC: 0,
  });
  
  const [selectedReasonFilter, setSelectedReasonFilter] = React.useState(null);
  // Opens the current-month (e.g. July) reason pie in a modal when a Top Reasons
  // table row is clicked.
  const [showMonthReasonPie, setShowMonthReasonPie] = React.useState(false);
  const [selectedGradeFilter, setSelectedGradeFilter] = React.useState(null); // 'A', 'B', 'C', or null
  const [timePeriodFilter, setTimePeriodFilter] = React.useState("monthly"); // 'daily', 'monthly', 'quarterly'
  const [expandedGreatComponent, setExpandedGreatComponent] = React.useState(null);

  const computeStats = (data) => {
    let passCount = 0;
    let failCount = 0;
    let totalScore = 0;
    let scoreCount = 0;
    let uniqueAdvisors = new Set();
    let uniqueSupervisors = new Set();
    let gradeA = 0;
    let gradeB = 0;
    let gradeC = 0;

    data.forEach((row) => {
      if (voicePassFailFromRow(row) === "Pass") {
        passCount += 1;
      } else {
        failCount += 1;
      }
      if (row?.total_score !== null && row?.total_score !== undefined) {
        const score = parseFloat(row.total_score);
        if (!isNaN(score)) {
          totalScore += score;
          scoreCount += 1;
          // Grade classification: A (90-100), B (80-89), C (≤79)
          if (score >= 90) {
            gradeA += 1;
          } else if (score >= 80) {
            gradeB += 1;
          } else {
            gradeC += 1;
          }
        }
      }
      if (row?.advisor_name) uniqueAdvisors.add(row.advisor_name);
      if (row?.supervisor) uniqueSupervisors.add(row.supervisor);
    });

    const total = data.length;
    const avgScore = scoreCount > 0 ? (totalScore / scoreCount).toFixed(2) : 0;

    setStats({
      total,
      passCount,
      failCount,
      avgScore,
      uniqueAdvisors: uniqueAdvisors.size,
      uniqueSupervisors: uniqueSupervisors.size,
      gradeA,
      gradeB,
      gradeC,
    });
  };

  // `skipIfCached`: paint from this week's cache immediately, then revalidate
  // in the background (the voice payload is ~75MB and can take 90s+). A cache
  // hit alone must not skip the network — stale tiny snapshots caused summary
  // zeros. `updateCache`: write the freshly fetched rows back to the cache for
  // next time. Manual searches pass neither flag.
  const fetchData = async ({
    skipIfCached = false,
    updateCache = false,
    revalidateBackground = false,
  } = {}) => {
    if (skipIfCached && !revalidateBackground) {
      const cached = readDataCache(GREAT_DATA_CACHE_KEY);
      if (isDataCacheFresh(cached) && Array.isArray(cached.data) && cached.data.length > 0) {
        setRows(cached.data);
        setLoading(false);
        fetchData({ updateCache: true, revalidateBackground: true });
        return;
      }
    }
    if (!revalidateBackground) {
      setLoading(true);
      setError("");
    }
    try {
      // For now, fetch all GREAT data and filter client-side for instant UX.
      // Long ranges are loaded in small day chunks to avoid PHP memory exhaustion.
      const array = await fetchGreatDataForRange(fromdate, todate);
      const deduped = dedupeGreatRows(array);
      if (array.length !== deduped.length) {
        console.log(
          `[GREAT] Dedup: removed ${array.length - deduped.length} duplicate call evaluation(s) (kept ${deduped.length} of ${array.length}).`
        );
      }
      // Drop rows whose supervisor is still a placeholder (e.g.
      // "[Insert Supervisor Name]") so they never appear in the table,
      // stats, dropdowns, or charts.
      const supervisorScrubbedRows = filterPlaceholderSupervisorRows(deduped);
      if (deduped.length !== supervisorScrubbedRows.length) {
        console.log(
          `[GREAT] Placeholder supervisors: removed ${deduped.length - supervisorScrubbedRows.length} row(s) with an "[Insert Supervisor Name]"-style supervisor.`
        );
      }
      setRows(supervisorScrubbedRows);
      if (updateCache && supervisorScrubbedRows.length > 0) {
        writeDataCache(GREAT_DATA_CACHE_KEY, supervisorScrubbedRows);
      }
    } catch (e) {
      console.error("Error fetching GREAT data:", e);
      if (!revalidateBackground) {
        setError(e.message || "Failed to fetch data");
        setRows([]);
        setStats({
          total: 0,
          passCount: 0,
          failCount: 0,
          avgScore: 0,
          uniqueAdvisors: 0,
          uniqueSupervisors: 0,
        });
      } else {
        console.warn("[GREAT] Background revalidate failed; keeping cached rows.", e.message);
      }
    } finally {
      if (!revalidateBackground) setLoading(false);
    }
  };

  // Fetch Amazon Connect for the KPI year (1 Jan → today). PHP agentRange
  // still filters InitiationTimestamp and caps at ~10k rows per agent; a
  // 2025 start was dropping Aug 2026. Locally we query MySQL on
  // ConnectedToSystemTimestamp (same as n8n). If PHP returns a full 10k
  // page, retry that agent by month and merge.
  const fetchAmazonConnectData = async ({ skipIfCached = false, updateCache = false } = {}) => {
    const useMysqlProxy =
      process.env.NODE_ENV === "development" ||
      (typeof window !== "undefined" && window.location.hostname === "localhost");
    if (skipIfCached && !useMysqlProxy) {
      const cached = readDataCache(AMAZON_CONNECT_CACHE_KEY);
      if (isDataCacheFresh(cached)) {
        setAmazonConnectRows(cached.data);
        setAmazonConnectLoading(false);
        return;
      }
    }
    setAmazonConnectLoading(true);
    setAmazonConnectError("");
    try {
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear(), 0, 1);
      const startdate = formatDateTime(startDate);
      const enddate = formatDateTimeEnd(endDate);

      const storedEmail =
        localStorage.getItem("team_member_email") ||
        localStorage.getItem("supervisor_email") ||
        localStorage.getItem("admin_email") ||
        "";

      let agentUsernames = AMAZON_CONNECT_AGENT_DIRECTORY
        .map((agent) => agent.amazonConnectId)
        .filter(Boolean);

      if (agentUsernames.length === 0) {
        const fallbackAgent =
          getAmazonConnectAgentUsernameForEmail(storedEmail) || DEFAULT_AMAZON_CONNECT_AGENT;
        agentUsernames = [fallbackAgent];
      }

      const fetchFromMysqlProxy = async () => {
        const resp = await fetch("/api/amazon-connect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startdate,
            enddate,
            agents: agentUsernames,
          }),
        });
        const json = await resp.json();
        if (!resp.ok || !json?.ok) {
          throw new Error(json?.error || `Connect proxy failed (${resp.status})`);
        }
        const batch = Array.isArray(json.rows) ? json.rows : [];
        return batch.map((row) => ({
          ...row,
          _fetchedForAgent: String(row?.Agent_Username || "").toLowerCase(),
        }));
      };

      const fetchAgentWindow = async (agentusername, from, to) => {
        const payload = {
          query: "agentRange",
          agentusername,
          startdate: formatDateTime(from),
          enddate: formatDateTimeEnd(to),
        };
        try {
          const resp = await fetch(AMAZON_CONNECT_APIURL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (!resp.ok) return [];
          const json = await resp.json();
          const batch = Array.isArray(json) ? json : [];
          return batch.map((row) => ({ ...row, _fetchedForAgent: agentusername }));
        } catch {
          return [];
        }
      };

      const monthWindows = (from, to) => {
        const windows = [];
        let cursor = new Date(from.getFullYear(), from.getMonth(), 1);
        const last = new Date(to.getFullYear(), to.getMonth(), 1);
        while (cursor <= last) {
          const chunkStart = cursor < from ? from : cursor;
          const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
          const chunkEnd = monthEnd < to ? monthEnd : to;
          windows.push([chunkStart, chunkEnd]);
          cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        }
        return windows;
      };

      const fetchAgent = async (agentusername) => {
        const page = await fetchAgentWindow(agentusername, startDate, endDate);
        if (page.length < 9900) return page;
        const merged = [];
        const seen = new Set();
        for (const [from, to] of monthWindows(startDate, endDate)) {
          const chunk = await fetchAgentWindow(agentusername, from, to);
          chunk.forEach((row) => {
            const id = row?.ContactId;
            if (id && seen.has(id)) return;
            if (id) seen.add(id);
            merged.push(row);
          });
        }
        return merged;
      };

      let allRows = [];
      if (useMysqlProxy) {
        try {
          allRows = await fetchFromMysqlProxy();
        } catch (proxyErr) {
          console.warn("Connect MySQL proxy failed, falling back to PHP:", proxyErr);
          const BATCH_SIZE = 6;
          for (let i = 0; i < agentUsernames.length; i += BATCH_SIZE) {
            const batch = agentUsernames.slice(i, i + BATCH_SIZE);
            const results = await Promise.all(batch.map(fetchAgent));
            results.forEach((rows) => {
              allRows = allRows.concat(rows);
            });
          }
        }
      } else {
        const BATCH_SIZE = 6;
        for (let i = 0; i < agentUsernames.length; i += BATCH_SIZE) {
          const batch = agentUsernames.slice(i, i + BATCH_SIZE);
          const results = await Promise.all(batch.map(fetchAgent));
          results.forEach((rows) => {
            allRows = allRows.concat(rows);
          });
        }
      }

      setAmazonConnectRows(allRows);
      if (updateCache && !useMysqlProxy) writeDataCache(AMAZON_CONNECT_CACHE_KEY, allRows);
    } catch (e) {
      console.error("Error fetching Amazon Connect data:", e);
      setAmazonConnectError(e.message || "Failed to fetch Amazon Connect data");
      setAmazonConnectRows([]);
    } finally {
      setAmazonConnectLoading(false);
    }
  };

  const YES_NO_OPTIONS = ["Yes", "No", "N/A"];

  const isYesValue = (val) => {
    if (val === null || val === undefined) return false;
    const s = String(val).toLowerCase().trim();
    return s === "yes" || s === "y" || s === "1" || s === "true";
  };

  // Section caps used by the backend / stored in the DB. Sum = 100.
  // Confirmed by reverse-engineering live rows: rows with every answer = Yes
  // always store G=8, R=34, E=38, A=13, T=7, total_score=100.
  const GREAT_MAX_SCORES = {
    G: 8,
    R: 34,
    E: 38,
    A: 13,
    T: 7,
  };

  const GREAT_RUBRIC_KEYS = {
    G: ["call_opening", "friendliness", "energy"],
    R: [
      "demonstrates_ownership",
      "listening",
      "questioning",
      "paraphrasing",
      "clear_concise_explanation_fcr",
      "offers_additional_assistance",
    ],
    E: [
      "empathy",
      "hold_procedure_dead_air",
      "grammar",
      "pronunciation",
      "appropriate_responses",
      "sentence_structuring",
      "pace",
      "addressing_customer",
    ],
    A: ["sounds_reassuring", "confidence", "intonation"],
    T: ["closing_call", "transfer_procedure"],
  };

  /**
   * Per-criterion points awarded when the answer is "Yes" (No or N/A = 0).
   * These weights were reverse-engineered from live DB rows so that the
   * editor's auto-recalculation produces totals identical to what the
   * backend stores. Section sums match GREAT_MAX_SCORES and total = 100.
   */
  const GREAT_RUBRIC_WEIGHTS = {
    // G — Greet (max 8)
    call_opening: 2,
    friendliness: 3,
    energy: 3,
    // R — Resolve (max 34)
    demonstrates_ownership: 8,
    listening: 5,
    questioning: 7,
    paraphrasing: 5,
    clear_concise_explanation_fcr: 7,
    offers_additional_assistance: 2,
    // E — Engage (max 38)
    empathy: 5,
    hold_procedure_dead_air: 5,
    grammar: 3,
    pronunciation: 3,
    appropriate_responses: 7,
    sentence_structuring: 5,
    pace: 5,
    addressing_customer: 5,
    // A — Assure (max 13)
    sounds_reassuring: 3,
    confidence: 3,
    intonation: 7,
    // T — Thank (max 7)
    closing_call: 2,
    transfer_procedure: 5,
  };

  const GREAT_RUBRIC_FIELD_KEYS = new Set(
    Object.values(GREAT_RUBRIC_KEYS).flat()
  );

  const computeGreatTotalsFromForm = (formData) => {
    const sectionScore = (letter) => {
      const keys = GREAT_RUBRIC_KEYS[letter] || [];
      let pts = 0;
      keys.forEach((k) => {
        if (isYesValue(formData[k])) pts += GREAT_RUBRIC_WEIGHTS[k] ?? 0;
      });
      return pts;
    };
    const greet = sectionScore("G");
    const resolve = sectionScore("R");
    const engage = sectionScore("E");
    const assure = sectionScore("A");
    const thank = sectionScore("T");
    const total = greet + resolve + engage + assure + thank;
    // DB stores these as "8.00" / "100.00" etc. — keep the same format so we
    // don't cause spurious "changed" diffs between the form and the stored row.
    const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
    return {
      greet_total: fmt(greet),
      resolve_total: fmt(resolve),
      engage_total: fmt(engage),
      assure_total: fmt(assure),
      thank_total: fmt(thank),
      total_score: fmt(total),
    };
  };

  const passFailFromTotalScore = (totalScore) => {
    const score = parseFloat(totalScore);
    if (Number.isNaN(score)) return null;
    return score >= GREAT_PASS_FAIL_THRESHOLD ? "Pass" : "Fail";
  };

  const applyPassFailFromTotal = (formData) => {
    const pass_fail = passFailFromTotalScore(formData.total_score);
    return pass_fail ? { ...formData, pass_fail } : formData;
  };

  const EDITABLE_FIELDS = [
    { key: "advisor_name", label: "Customer Service Representative", type: "text", section: "info" },
    { key: "date_of_call", label: "Date of Call", type: "date", section: "info" },
    { key: "time_of_call", label: "Time of Call", type: "text", section: "info" },
    { key: "supervisor", label: "Supervisor", type: "text", section: "info" },
    { key: "retailer_account_sq_no", label: "Retailer Account SQ No", type: "text", section: "info" },
    { key: "reason_for_call", label: "Reason for Call", type: "text", section: "info" },
    { key: "total_score", label: "Total Score", type: "number", section: "scores" },
    { key: "pass_fail", label: "Pass/Fail", type: "select", options: ["Pass", "Fail"], section: "scores" },
    { key: "call_opening", label: "Call Opening", type: "select", options: YES_NO_OPTIONS, section: "greet" },
    { key: "friendliness", label: "Friendliness", type: "select", options: YES_NO_OPTIONS, section: "greet" },
    { key: "energy", label: "Energy", type: "select", options: YES_NO_OPTIONS, section: "greet" },
    { key: "demonstrates_ownership", label: "Demonstrates Ownership", type: "select", options: YES_NO_OPTIONS, section: "resolve" },
    { key: "listening", label: "Listening", type: "select", options: YES_NO_OPTIONS, section: "resolve" },
    { key: "questioning", label: "Questioning", type: "select", options: YES_NO_OPTIONS, section: "resolve" },
    { key: "paraphrasing", label: "Paraphrasing", type: "select", options: YES_NO_OPTIONS, section: "resolve" },
    { key: "clear_concise_explanation_fcr", label: "Clear Concise Explanation FCR", type: "select", options: YES_NO_OPTIONS, section: "resolve" },
    { key: "offers_additional_assistance", label: "Offers Additional Assistance", type: "select", options: YES_NO_OPTIONS, section: "engage" },
    { key: "empathy", label: "Empathy", type: "select", options: YES_NO_OPTIONS, section: "engage" },
    { key: "hold_procedure_dead_air", label: "Hold Procedure/Dead Air", type: "select", options: YES_NO_OPTIONS, section: "engage" },
    { key: "grammar", label: "Grammar", type: "select", options: YES_NO_OPTIONS, section: "engage" },
    { key: "pronunciation", label: "Pronunciation", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "appropriate_responses", label: "Appropriate Responses", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "sentence_structuring", label: "Sentence Structuring", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "pace", label: "Pace", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "addressing_customer", label: "Addressing Customer", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "sounds_reassuring", label: "Sounds Reassuring", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "confidence", label: "Confidence", type: "select", options: YES_NO_OPTIONS, section: "assure" },
    { key: "intonation", label: "Intonation", type: "select", options: YES_NO_OPTIONS, section: "thank" },
    { key: "closing_call", label: "Closing Call", type: "select", options: YES_NO_OPTIONS, section: "thank" },
    { key: "transfer_procedure", label: "Transfer Procedure", type: "select", options: YES_NO_OPTIONS, section: "thank" },
    { key: "greet_total", label: "Greet Total", type: "number", section: "totals" },
    { key: "resolve_total", label: "Resolve Total", type: "number", section: "totals" },
    { key: "engage_total", label: "Engage Total", type: "number", section: "totals" },
    { key: "assure_total", label: "Assure Total", type: "number", section: "totals" },
    { key: "thank_total", label: "Thank Total", type: "number", section: "totals" },
    { key: "performance_snapshot", label: "Performance Snapshot", type: "textarea", section: "extra" },
    { key: "transcript", label: "Transcript", type: "textarea", section: "extra" },
    { key: "audio", label: "Audio URL", type: "text", section: "extra" },
  ];

  const EDIT_SECTIONS = [
    { id: "info", title: "Call Information" },
    { id: "scores", title: "Overall Score" },
    { id: "greet", title: "G — Greet" },
    { id: "resolve", title: "R — Resolve" },
    { id: "engage", title: "E — Engage" },
    { id: "assure", title: "A — Assure" },
    { id: "thank", title: "T — Thank" },
    { id: "totals", title: "Component Totals" },
    { id: "extra", title: "Additional Details" },
  ];

  const handleEnterEditMode = () => {
    if (!selectedRow || selectedRowDetailLoading) return;
    const formData = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      formData[key] = selectedRow[key] ?? "";
    });
    const totals = computeGreatTotalsFromForm(formData);
    setEditFormData(applyPassFailFromTotal({ ...formData, ...totals }));
    setIsEditMode(true);
    setEditError("");
    setEditSuccess("");
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditError("");
    setEditSuccess("");
  };

  const handleEditChange = (key, value) => {
    setEditFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (GREAT_RUBRIC_FIELD_KEYS.has(key)) {
        const totals = computeGreatTotalsFromForm(next);
        return applyPassFailFromTotal({ ...next, ...totals });
      }
      if (key === "total_score") {
        return applyPassFailFromTotal(next);
      }
      return next;
    });
  };

  const handleEditSave = async () => {
    if (!selectedRow?.sr_no) return;
    setEditError("");
    setEditSuccess("");

    const errors = [];
    EDITABLE_FIELDS.forEach(({ key, label, type }) => {
      const val = editFormData[key];
      if (type === "number" && val !== "" && val !== null && val !== undefined) {
        if (isNaN(Number(val))) {
          errors.push(`${label} must be a valid number.`);
        }
      }
      if (type === "date" && val) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(val) && isNaN(Date.parse(val))) {
          errors.push(`${label} must be a valid date (YYYY-MM-DD).`);
        }
      }
    });
    if (errors.length > 0) {
      setEditError(errors.join(" "));
      return;
    }

    const changedData = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      const newVal = editFormData[key] ?? "";
      const oldVal = selectedRow[key] ?? "";
      if (String(newVal) !== String(oldVal)) {
        changedData[key] = newVal;
      }
    });

    if (Object.keys(changedData).length === 0) {
      setEditError("No changes detected.");
      return;
    }

    setEditSaving(true);
    try {
      const resp = await fetch(APIURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: "great_data_update",
          sr_no: selectedRow.sr_no,
          data: changedData,
        }),
      });

      let result;
      const respText = await resp.text();
      try {
        result = JSON.parse(respText);
      } catch {
        throw new Error(
          resp.ok
            ? `Server returned invalid response: ${respText.substring(0, 200)}`
            : `HTTP ${resp.status}: ${respText.substring(0, 200)}`
        );
      }

      if (!resp.ok) {
        throw new Error(result?.error || result?.message || `Server error (HTTP ${resp.status}). The record was not updated.`);
      }
      if (result?.error) {
        throw new Error(result.error);
      }
      if (result === null) {
        throw new Error("The server did not confirm the update. The 'great_data_update' query may not be configured on the backend.");
      }

      const updatedRow = { ...selectedRow, ...changedData };
      setRows((prev) =>
        prev.map((r) =>
          r.sr_no === selectedRow.sr_no ? updatedRow : r
        )
      );
      setSelectedRow(updatedRow);
      setEditSuccess("Record updated successfully.");
      setTimeout(() => {
        setIsEditMode(false);
        setEditSuccess("");
      }, 1200);
    } catch (e) {
      console.error("Error updating GREAT record:", e);
      setEditError(e.message || "Failed to update record. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow?.sr_no) return;
    setDeleting(true);
    setEditError("");
    try {
      const resp = await fetch(APIURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: "great_data_delete",
          sr_no: selectedRow.sr_no,
        }),
      });

      let result;
      const respText = await resp.text();
      try {
        result = JSON.parse(respText);
      } catch {
        throw new Error(
          resp.ok
            ? `Server returned invalid response: ${respText.substring(0, 200)}`
            : `HTTP ${resp.status}: ${respText.substring(0, 200)}`
        );
      }

      if (!resp.ok) {
        throw new Error(result?.error || result?.message || `Server error (HTTP ${resp.status}). The record was not deleted.`);
      }
      if (result?.error) {
        throw new Error(result.error);
      }
      if (result === null) {
        throw new Error("The server did not confirm the deletion. The 'great_data_delete' query may not be configured on the backend.");
      }

      setRows((prev) => prev.filter((r) => r.sr_no !== selectedRow.sr_no));
      setSelectedRow(null);
      setIsEditMode(false);
      setDeleteConfirm(false);
    } catch (e) {
      console.error("Error deleting GREAT record:", e);
      setEditError(e.message || "Failed to delete record. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const closeDetailsModal = () => {
    if (editSaving || deleting) return;
    setSelectedRow(null);
    setIsEditMode(false);
    setEditError("");
    setEditSuccess("");
    setDeleteConfirm(false);
  };

  React.useEffect(() => {
    // First load: use this week's cache if we have one (near-instant), and
    // only hit the network if it's stale/missing — see the cache helpers
    // above for why (data only actually changes once a week, on Fridays).
    fetchData({ skipIfCached: true, updateCache: true });
    fetchAmazonConnectData({ skipIfCached: true, updateCache: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the tab was left open across days, `today` and the default dateRange
  // would stay at their mount-time values and new DB data could appear to be
  // "missing" client-side. When the tab becomes visible again, check whether
  // the calendar day has rolled over. Only then extend the range AND refetch.
  // Previously we refetched on every focus/visibility event, which caused the
  // loading spinner to flash any time the user alt-tabbed or opened DevTools.
  React.useEffect(() => {
    const refreshIfStale = () => {
      const liveToday = buildTodayEnd();
      let didRoll = false;
      setDateRange((prev) => {
        if (!Array.isArray(prev) || !prev[0] || !prev[1]) return prev;
        const prevEnd = new Date(prev[1]);
        const isSameDay =
          prevEnd.getFullYear() === liveToday.getFullYear() &&
          prevEnd.getMonth() === liveToday.getMonth() &&
          prevEnd.getDate() === liveToday.getDate();
        // Only auto-extend if the end date was still at (or before) today:
        // don't overwrite a narrower range the user picked into the past.
        if (!isSameDay && prevEnd.getTime() <= liveToday.getTime()) {
          didRoll = true;
          return [prev[0], liveToday];
        }
        return prev;
      });
      if (didRoll) fetchData({ updateCache: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshIfStale();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Date range filtering for Amazon Connect is handled client-side
  // via filteredAmazonConnectRows memo — no refetch needed.

  React.useEffect(() => {
    if (isSupervisor && supervisorName && !supervisor) {
      setSupervisor(supervisorName);
    }
  }, [isSupervisor, supervisorName, supervisor]);

  // Close advisor/supervisor dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (advisorDropdownRef.current && !advisorDropdownRef.current.contains(event.target)) {
        setShowAdvisorDropdown(false);
      }
      if (supervisorDropdownRef.current && !supervisorDropdownRef.current.contains(event.target)) {
        setShowSupervisorDropdown(false);
      }
      if (retailerAccountDropdownRef.current && !retailerAccountDropdownRef.current.contains(event.target)) {
        setShowRetailerAccountDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchData();
    // Amazon Connect filtering is handled client-side — no refetch needed
    setPage(0);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (Array.isArray(range) && range[0] && range[1]) {
      const start = new Date(range[0]);
      const end = new Date(range[1]);
      setFromdate(formatDate(start));
      setTodate(formatDate(end));
      setPage(0);
    }
  };

  // Client-side filtering for instant UX
  const getRowDepartment = (row) => {
    // Date-based team re-assignments take priority over any stored or
    // directory-derived department (see AGENT_TEAM_CUTOVERS).
    const period = getAgentCutoverPeriodForRow(row);
    if (period && period.department) {
      return period.department;
    }
    // Account Executive team: mapped by supervisor (Janelle Fernuik) so it stays
    // correct even for reps who also belong to another team under a different
    // supervisor. Takes precedence over any DB-stored department for these rows.
    if (row && normalizeNameKey(row.supervisor) === ACCOUNT_EXECUTIVE_SUPERVISOR_KEY) {
      return ACCOUNT_EXECUTIVE_DEPARTMENT;
    }
    // Name-based fallback for the AE-only members (see comment above the Set).
    // Checked before `row.department` so it overrides any stale/incomplete
    // department the Amazon Connect agent directory may have stamped onto
    // these rows.
    if (row && ACCOUNT_EXECUTIVE_ONLY_NAME_KEYS.has(normalizeNameKey(row.advisor_name))) {
      return ACCOUNT_EXECUTIVE_DEPARTMENT;
    }
    if (row && row.department) {
      return String(row.department);
    }
    if (row && row.advisor_name) {
      const id = getAmazonConnectIdForName(row.advisor_name);
      if (id) {
        const profile = getAmazonConnectAgentProfile(id);
        if (profile && profile.department) {
          return String(profile.department);
        }
      }
    }
    return "";
  };

  // All rows matching every active filter EXCEPT the Inbound/Outbound toggle.
  // This is the single source of truth for the shared role/date/department/etc.
  // filtering: `filteredRows` is derived from it by applying the direction
  // filter, and the "Total Calls" / direction-split widgets use it directly so
  // they reflect every other filter but ignore the toggle. Keeping the chain in
  // one place means the direction filter is the only difference (no drift).
  const rowsAllDirections = React.useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const hasDateRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
    const startDate = hasDateRange ? new Date(dateRange[0]) : null;
    const endDate = hasDateRange ? new Date(dateRange[1]) : null;

    const normalizeDate = normalizeGreatRowDate;
    const othersTopLabelsForFilter =
      selectedReasonFilter === "Others"
        ? computeOthersTopCallReasonLabels(
            rows.filter((row) => {
              if (!matchesDirection(row, directionFilter)) return false;
              if (departmentFilter && getRowDepartment(row).toLowerCase() !== departmentFilter.toLowerCase()) {
                return false;
              }
              return true;
            }),
            reasonBucketMode
          )
        : [];

    return rows.filter((row) => {
      if (isAdmin) {
        if (advisorName && row.advisor_name !== advisorName) return false;
        if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      }
      else if (isSupervisor) {
        if (supervisorTeamMembers.length === 0) return false;
        if (!supervisorTeamMembers.includes(row.advisor_name)) return false;
        // Date-aware narrowing: for agents with a cutover in
        // AGENT_TEAM_CUTOVERS, only the currently responsible supervisor sees
        // each row. Without this a post-cutover row of a reassigned agent
        // would still appear for their old supervisor (and vice versa).
        if (!isSupervisorResponsibleForRow(supervisorName, row)) return false;
        if (advisorName && row.advisor_name !== advisorName) return false;
      }
      else if (isTeamMember && teamMemberName) {
        if (row.advisor_name !== teamMemberName) return false;
        if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      }
      else {
        return false;
      }

      // Department filter
      if (departmentFilter) {
        const rowDept = getRowDepartment(row).toLowerCase();
        const filterDept = departmentFilter.toLowerCase();
        if (rowDept !== filterDept) return false;
      }

      // Result filter (Met / Not Met driven from pie chart)
      if (resultFilter !== "all") {
        const isPass = voicePassFailFromRow(row) === "Pass";
        if (resultFilter === "met" && !isPass) return false;
        if (resultFilter === "not_met" && isPass) return false;
      }
      if (!voiceSentimentMatchesFilter(row, sentimentFilter)) return false;
      if (!sentimentReasonMatchesFilter(row, sentimentReasonFilter, sentimentFilter)) return false;
      if (!isOutbound && !voiceErrorsMatchesFilter(row, errorsFilter)) return false;

      // Reason for call filter (canonical outbound buckets + exact inbound match)
      if (selectedReasonFilter) {
        if (
          !reasonMatchesSelectedFilter(
            row.reason_for_call,
            selectedReasonFilter,
            reasonBucketMode,
            selectedReasonFilter === "Others" ? othersTopLabelsForFilter : []
          )
        ) {
          return false;
        }
      }

      // Grade filter (A: 90-100, B: 80-89, C: ≤79)
      if (selectedGradeFilter) {
        const score = row.total_score !== null && row.total_score !== undefined 
          ? parseFloat(row.total_score) 
          : null;
        if (score !== null && !isNaN(score)) {
          if (selectedGradeFilter === 'A' && score < 90) return false;
          if (selectedGradeFilter === 'B' && (score < 80 || score >= 90)) return false;
          if (selectedGradeFilter === 'C' && score >= 80) return false;
        } else {
          return false;
        }
      }

      // Retailer Account filter
      if (retailerAccountFilter) {
        const rowAccount = String(row.retailer_account_sq_no || "").trim().toLowerCase();
        if (rowAccount !== retailerAccountFilter.trim().toLowerCase()) return false;
      }

      // Date range filter
      if (hasDateRange) {
        const d = normalizeDate(row);
        if (!d) return false;
        const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const sOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const eOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        if (dOnly < sOnly || dOnly > eOnly) return false;
      }

      return true;
    });
  }, [rows, advisorName, supervisor, resultFilter, sentimentFilter, sentimentReasonFilter, errorsFilter, dateRange, selectedReasonFilter, reasonBucketMode, selectedGradeFilter, isAdmin, isSupervisor, isTeamMember, supervisorTeamMembers, supervisorName, teamMemberName, departmentFilter, retailerAccountFilter, reasonDepartment, directionFilter]);

  // The toggle-aware rows that every chart/card/table derives from. Direction
  // is the ONLY thing applied here on top of rowsAllDirections.
  const filteredRows = React.useMemo(
    () => rowsAllDirections.filter((row) => matchesDirection(row, directionFilter)),
    [rowsAllDirections, directionFilter]
  );

  // Median of total_score across the currently filtered rows. Used as the
  // outbound-appropriate "median" metric: outbound calls have NO call/queue
  // duration data anywhere in the dataset (Amazon Connect is inbound-only and
  // great_data rows carry no duration field), so the median GREAT score is the
  // most meaningful outbound median we can derive.
  const medianScore = React.useMemo(() => {
    const scores = filteredRows
      .map((r) => parseFloat(r?.total_score))
      .filter((v) => !Number.isNaN(v))
      .sort((a, b) => a - b);
    if (scores.length === 0) return "0.0";
    const mid = Math.floor(scores.length / 2);
    const med = scores.length % 2 === 1 ? scores[mid] : (scores[mid - 1] + scores[mid]) / 2;
    return med.toFixed(1);
  }, [filteredRows]);

  // Count of "Outbound Follow-Up" calls in the current (outbound) view. The
  // dataset uses "Outbound Follow-Up" as a reason_for_call value, so we surface
  // how many of the current outbound calls were follow-ups. (See report note —
  // this is a minimal interpretation of the ambiguous "Outbound Follow-up"
  // request and may need product clarification.)
  const outboundFollowUpCount = React.useMemo(() => {
    let count = 0;
    filteredRows.forEach((r) => {
      const reason = String(r?.reason_for_call || "").toLowerCase();
      if (reason.includes("outbound follow")) count += 1;
    });
    return count;
  }, [filteredRows]);

  // Per-direction roll-up derived from rowsAllDirections (ignores the toggle).
  // Powers the Summary (General) page so it can show inbound AND outbound side
  // by side without depending on the active direction filter.
  const summaryByDirection = React.useMemo(() => {
    const blank = () => ({ total: 0, monitored: 0, scoreSum: 0, scoreCount: 0, gradeA: 0, gradeB: 0, gradeC: 0, reps: new Set() });
    const acc = { inbound: blank(), outbound: blank() };
    rowsAllDirections.forEach((row) => {
      const bucket = getRowDirection(row) === "outbound" ? acc.outbound : acc.inbound;
      bucket.total += 1;
      if (row?.advisor_name) bucket.reps.add(row.advisor_name);
      const s = parseFloat(row?.total_score);
      if (!Number.isNaN(s)) {
        bucket.monitored += 1;
        bucket.scoreSum += s;
        bucket.scoreCount += 1;
        if (s >= 90) bucket.gradeA += 1;
        else if (s >= 80) bucket.gradeB += 1;
        else bucket.gradeC += 1;
      }
    });
    const finalize = (b) => ({
      total: b.total,
      monitored: b.monitored,
      monitoredPct: b.total > 0 ? Math.round((b.monitored / b.total) * 100) : 0,
      avgScore: b.scoreCount > 0 ? (b.scoreSum / b.scoreCount).toFixed(2) : "0.00",
      gradeA: b.gradeA,
      gradeB: b.gradeB,
      gradeC: b.gradeC,
      repCount: b.reps.size,
    });
    const totalScoreSum = acc.inbound.scoreSum + acc.outbound.scoreSum;
    const totalScoreCount = acc.inbound.scoreCount + acc.outbound.scoreCount;
    return {
      inbound: finalize(acc.inbound),
      outbound: finalize(acc.outbound),
      overallAvg: totalScoreCount > 0 ? (totalScoreSum / totalScoreCount).toFixed(2) : "0.00",
      total: rowsAllDirections.length,
      gradeSeries: [
        acc.inbound.gradeA + acc.outbound.gradeA,
        acc.inbound.gradeB + acc.outbound.gradeB,
        acc.inbound.gradeC + acc.outbound.gradeC,
      ],
    };
  }, [rowsAllDirections]);

  const digitalSummaryByChannel = React.useMemo(() => {
    const ytdStart = new Date(today.getFullYear(), 0, 1);
    const filtered = filterDigitalRows(digitalRowsForViewer(allDigitalRows), {
      departmentFilter,
      dateRange: [ytdStart, today],
    });
    const channelRows = (db) =>
      filtered.filter((r) => {
        if (normalizeDigitalChannel(r.channel) !== db) return false;
        if (!departmentFilter && db === "live_chat") {
          if (!isLiveChatScopedRow(r)) return false;
        }
        if (!departmentFilter && db === "sms") {
          if (!isLiveChatScopedRow(r)) return false;
        }
        return true;
      });
    return [
      { viewKey: "email", db: "email", navLabel: "Emails" },
      { viewKey: "live-chat", db: "live_chat", navLabel: "Live Chats" },
      { viewKey: "sms", db: "sms", navLabel: "SMS" },
    ].map(({ viewKey, db, navLabel }) => ({
      viewKey,
      navLabel,
      label: dbChannelToLabel(db),
      accent: DIGITAL_CHANNEL_ACCENTS[db] || "#3B82F6",
      stats: summarizeDigitalRows(channelRows(db)),
    }));
  }, [allDigitalRows, departmentFilter, today, digitalRowsForViewer]);

  // Retailer account dropdown should be filtered based on all other filters (but independent of the current retailerAccountFilter)
  const retailerDropdownRows = React.useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const hasDateRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
    const startDate = hasDateRange ? new Date(dateRange[0]) : null;
    const endDate = hasDateRange ? new Date(dateRange[1]) : null;

    const normalizeDate = normalizeGreatRowDate;
    const othersTopLabelsForFilter =
      selectedReasonFilter === "Others"
        ? computeOthersTopCallReasonLabels(
            rows.filter((row) => matchesDirection(row, directionFilter)),
            reasonBucketMode
          )
        : [];

    return rows.filter((row) => {
      // Direction filter (Inbound | Outbound) — keep in sync with filteredRows.
      if (!matchesDirection(row, directionFilter)) return false;

      if (isAdmin) {
        if (advisorName && row.advisor_name !== advisorName) return false;
        if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      }
      else if (isSupervisor) {
        if (supervisorTeamMembers.length === 0) return false;
        if (!supervisorTeamMembers.includes(row.advisor_name)) return false;
        // Date-aware narrowing: for agents with a cutover in
        // AGENT_TEAM_CUTOVERS, only the currently responsible supervisor sees
        // each row. Without this a post-cutover row of a reassigned agent
        // would still appear for their old supervisor (and vice versa).
        if (!isSupervisorResponsibleForRow(supervisorName, row)) return false;
        if (advisorName && row.advisor_name !== advisorName) return false;
      }
      else if (isTeamMember && teamMemberName) {
        if (row.advisor_name !== teamMemberName) return false;
        if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      }
      else {
        return false;
      }

      // Department filter
      if (departmentFilter) {
        const rowDept = getRowDepartment(row).toLowerCase();
        const filterDept = departmentFilter.toLowerCase();
        if (rowDept !== filterDept) return false;
      }

      // Result filter (Met / Not Met driven from pie chart)
      if (resultFilter !== "all") {
        const isPass = voicePassFailFromRow(row) === "Pass";
        if (resultFilter === "met" && !isPass) return false;
        if (resultFilter === "not_met" && isPass) return false;
      }
      if (!voiceSentimentMatchesFilter(row, sentimentFilter)) return false;
      if (!sentimentReasonMatchesFilter(row, sentimentReasonFilter, sentimentFilter)) return false;
      if (!isOutbound && !voiceErrorsMatchesFilter(row, errorsFilter)) return false;

      // Reason for call filter (canonical outbound buckets + exact inbound match)
      if (selectedReasonFilter) {
        if (
          !reasonMatchesSelectedFilter(
            row.reason_for_call,
            selectedReasonFilter,
            reasonBucketMode,
            selectedReasonFilter === "Others" ? othersTopLabelsForFilter : []
          )
        ) {
          return false;
        }
      }

      // Grade filter (A: 90-100, B: 80-89, C: ≤79)
      if (selectedGradeFilter) {
        const score = row.total_score !== null && row.total_score !== undefined 
          ? parseFloat(row.total_score) 
          : null;
        if (score !== null && !isNaN(score)) {
          if (selectedGradeFilter === 'A' && score < 90) return false;
          if (selectedGradeFilter === 'B' && (score < 80 || score >= 90)) return false;
          if (selectedGradeFilter === 'C' && score >= 80) return false;
        } else {
          return false;
        }
      }

      // NOTE: intentionally do NOT apply retailerAccountFilter here

      // Date range filter
      if (hasDateRange) {
        const d = normalizeDate(row);
        if (!d) return false;
        const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const sOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const eOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        if (dOnly < sOnly || dOnly > eOnly) return false;
      }

      return true;
    });
  }, [rows, advisorName, supervisor, resultFilter, sentimentFilter, sentimentReasonFilter, errorsFilter, dateRange, selectedReasonFilter, reasonBucketMode, selectedGradeFilter, isAdmin, isSupervisor, isTeamMember, supervisorTeamMembers, supervisorName, teamMemberName, departmentFilter, directionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = React.useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  // For team members: stats and charts should be filtered to only their own data
  // For supervisors/admins: everything uses filteredRows
  const dataForStatsAndCharts = React.useMemo(() => {
    return filteredRows;
  }, [filteredRows]);

  // Recompute stats based on dataForStatsAndCharts (for aggregated metrics like Total Calls)
  React.useEffect(() => {
    computeStats(dataForStatsAndCharts);
  }, [dataForStatsAndCharts]);

  // For team members: calculate personal avgScore (only their own data)
  const personalAvgScore = React.useMemo(() => {
    if (!isTeamMember) return null;
    
    let totalScore = 0;
    let scoreCount = 0;

    filteredRows.forEach((row) => {
      if (row?.total_score !== null && row?.total_score !== undefined) {
        const score = parseFloat(row.total_score);
        if (!isNaN(score)) {
          totalScore += score;
          scoreCount += 1;
        }
      }
    });

    return scoreCount > 0 ? parseFloat((totalScore / scoreCount).toFixed(2)) : 0;
  }, [filteredRows, isTeamMember]);

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const truncate = (text, max = 50) => {
    if (!text) return "N/A";
    const str = String(text);
    return str.length > max ? str.slice(0, max) + "..." : str;
  };

  // Get rows filtered by everything except reason filter (for stable pie chart labels)
  const rowsForReasonChart = React.useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const hasDateRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
    const startDate = hasDateRange ? new Date(dateRange[0]) : null;
    const endDate = hasDateRange ? new Date(dateRange[1]) : null;

    const normalizeDate = normalizeGreatRowDate;

    return rows.filter((row) => {
      // Direction filter (Inbound | Outbound) — keep in sync with filteredRows.
      if (!matchesDirection(row, directionFilter)) return false;

      if (isAdmin) {
        if (advisorName && row.advisor_name !== advisorName) return false;
        if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      }
      else if (isSupervisor) {
        if (supervisorTeamMembers.length === 0) return false;
        if (!supervisorTeamMembers.includes(row.advisor_name)) return false;
        // Date-aware narrowing: for agents with a cutover in
        // AGENT_TEAM_CUTOVERS, only the currently responsible supervisor sees
        // each row. Without this a post-cutover row of a reassigned agent
        // would still appear for their old supervisor (and vice versa).
        if (!isSupervisorResponsibleForRow(supervisorName, row)) return false;
        if (advisorName && row.advisor_name !== advisorName) return false;
      }
      else if (isTeamMember && teamMemberName) {
        if (row.advisor_name !== teamMemberName) return false;
        if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      }
      else {
        return false;
      }
      // Department filter — applies to every role (was previously only
      // checked inside the isAdmin branch, so supervisors/team members
      // narrowing by department did nothing).
      if (departmentFilter) {
        const rowDept = getRowDepartment(row).toLowerCase();
        const filterDept = departmentFilter.toLowerCase();
        if (rowDept !== filterDept) return false;
      }
      // Result filter
      if (resultFilter !== "all") {
        const isPass = voicePassFailFromRow(row) === "Pass";
        if (resultFilter === "met" && !isPass) return false;
        if (resultFilter === "not_met" && isPass) return false;
      }
      if (!voiceSentimentMatchesFilter(row, sentimentFilter)) return false;
      if (!sentimentReasonMatchesFilter(row, sentimentReasonFilter, sentimentFilter)) return false;
      if (!isOutbound && !voiceErrorsMatchesFilter(row, errorsFilter)) return false;
      if (reasonDepartment.toLowerCase() === "customer service") {
        const canonReason = canonicalizeReasonText(row.reason_for_call).toLowerCase();
        if (canonReason === "custom order inquiry") return false;
        if (getRowDepartment(row).toLowerCase() === "custom order") return false;
      }
      // Grade filter
      if (selectedGradeFilter) {
        const score = row.total_score !== null && row.total_score !== undefined 
          ? parseFloat(row.total_score) 
          : null;
        if (score !== null && !isNaN(score)) {
          if (selectedGradeFilter === 'A' && score < 90) return false;
          if (selectedGradeFilter === 'B' && (score < 80 || score >= 90)) return false;
          if (selectedGradeFilter === 'C' && score >= 80) return false;
        } else {
          return false;
        }
      }
      // Retailer Account filter
      if (retailerAccountFilter) {
        const rowAccount = String(row.retailer_account_sq_no || "").trim().toLowerCase();
        if (rowAccount !== retailerAccountFilter.trim().toLowerCase()) return false;
      }
      // Date range filter
      if (hasDateRange) {
        const d = normalizeDate(row);
        if (!d) return false;
        const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const sOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const eOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        if (dOnly < sOnly || dOnly > eOnly) return false;
      }
      return true;
    });
  }, [rows, advisorName, supervisor, resultFilter, sentimentFilter, sentimentReasonFilter, errorsFilter, dateRange, selectedGradeFilter, isAdmin, isSupervisor, isTeamMember, supervisorTeamMembers, supervisorName, teamMemberName, retailerAccountFilter, directionFilter, departmentFilter]);

  // Second pie chart: Reason for Call distribution. Which bucket set applies is
  // resolved by `reasonBucketMode` (declared above, next to departmentFilter, so
  // the row filters can use it too).

  // Drop a stale reason filter when switching direction or bucket sets.
  React.useEffect(() => {
    setSelectedReasonFilter(null);
  }, [directionFilter, reasonBucketMode]);

  // Single source of truth for the Top Reasons card.
  // Total / % / the pie / which reasons make the top list = the ENTIRE
  // filtered scope (whatever the date-range picker + other filters resolve
  // to) — NOT restricted to any fixed window. The per-month columns are a
  // separate "last 3 months of available data" trend breakdown shown
  // alongside that Total purely for context; they are intentionally NOT
  // required to sum to the Total (the Total covers the full range, the
  // columns cover 3 months ending at the latest month with calls).
  const reasonWindow = React.useMemo(() => {
    let latest = null;
    rowsForReasonChart.forEach((row) => {
      const d = normalizeGreatRowDate(row);
      if (d && (!latest || d > latest)) latest = d;
    });
    const periods = getLast3MonthPeriods(latest);
    const periodKeys = periods.map((p) => p.key);
    const inWindow = (row) => {
      const d = normalizeGreatRowDate(row);
      if (!d) return null;
      const p = `${MONTH_SHORT_NAMES[d.getMonth()]} ${d.getFullYear()}`;
      return periodKeys.includes(p) ? p : null;
    };
    const mode = reasonBucketMode;
    const modeBuckets = getReasonBucketsForMode(mode);

    // Labels + entire-range totals: curated departments (AE/CS) use their
    // fixed keyword buckets; everyone else (inbound, Concierge, Custom Order,
    // etc.) gets the top 5 reasons after merging obvious near-duplicate
    // phrasings (see buildTopReasonBuckets) so e.g. "To Check Pricing" and
    // "To Check Pricing and Availability" count as one reason, not two.
    let labels;
    let series;
    let bucketOf;
    if (modeBuckets) {
      labels = [...modeBuckets];
      const entireCounts = Object.fromEntries(labels.map((l) => [l, 0]));
      rowsForReasonChart.forEach((row) => {
        const bucket = normalizeReasonBucket(row.reason_for_call, mode);
        if (bucket) entireCounts[bucket] += 1;
      });
      series = labels.map((l) => entireCounts[l]);
      bucketOf = (row) => normalizeReasonBucket(row.reason_for_call, mode);
    } else {
      const topBuckets = buildTopReasonBuckets(rowsForReasonChart, 5);
      labels = topBuckets.map((b) => b.label);
      series = topBuckets.map((b) => b.count);
      bucketOf = (row) => {
        const raw = row.reason_for_call || "Unknown";
        const match = labels.find((l) => reasonMatchesCanonicalLabel(raw, l));
        return match || null;
      };
    }
    const labelSet = new Set(labels);
    const total = series.reduce((s, v) => s + v, 0);

    // Last-3-months trend columns (context only — see comment above).
    const monthCounts = {};
    labels.forEach((l) => {
      monthCounts[l] = {};
      periodKeys.forEach((k) => { monthCounts[l][k] = 0; });
    });
    rowsForReasonChart.forEach((row) => {
      const period = inWindow(row);
      if (!period) return;
      const label = bucketOf(row);
      if (!label || !labelSet.has(label)) return;
      monthCounts[label][period] += 1;
    });

    const currKey = periodKeys[2];
    const byLabel = {};
    labels.forEach((l) => {
      byLabel[l] = { months: periodKeys.map((k) => monthCounts[l][k]) };
    });
    return { periods, periodKeys, labels, series, total, byLabel, currKey };
  }, [rowsForReasonChart, reasonBucketMode]);

  const reasonForCallLabels = reasonWindow.labels;
  const reasonForCallSeries = reasonWindow.series;

  const reasonForCallColors = React.useMemo(() => {
    const colorMap = {
      "Account Executive Outreach": "#7CC3FF",
      "Outbound Follow-Up": "#10B981",
      "Outbound Sales Follow-Up": "#A78BFA",
      "Pricing Follow-up": "#7CC3FF",
      "Providing an Expedited Shipping Date": "#10B981",
      "Repair Status Update": "#A78BFA",
      "Product Inquiry Follow-up": "#F59E0B",
      "Image & Rendering Follow-up": "#FB923C",
      "Track an order": "#7CC3FF",
      "Product inquiry": "#10B981",
      "Go to place an order": "#A78BFA",
      "Complaint": "#EF4444",
      "Other": "#6B7280",
    };
    const defaultColors = ["#7CC3FF", "#10B981", "#A78BFA", "#F59E0B", "#FB923C"];
    return reasonForCallLabels.map(
      (label, index) => colorMap[label] || defaultColors[index % defaultColors.length]
    );
  }, [reasonForCallLabels]);

  const reasonForCallTotal = reasonWindow.total;

  // Same 3-month window, exposed for the compact table in the Top Reasons list.
  // `deltaPts` = change in each reason's SHARE of calls THIS month vs LAST month
  // (percentage points): positive = greater share this month (green), negative (red).
  const reasonMonthlyTable = reasonWindow;

  // Latest month-with-data reason distribution, shown in the click-through modal.
  const monthReasonPie = React.useMemo(() => {
    const labels = reasonWindow.labels;
    const series = labels.map((l) => reasonWindow.byLabel[l]?.months?.[2] || 0);
    return { labels, series, monthLabel: reasonWindow.currKey };
  }, [reasonWindow]);

  const monthReasonPieOptions = React.useMemo(
    () => ({
      labels: monthReasonPie.labels,
      colors: reasonForCallColors,
      legend: { position: "bottom", fontFamily: "DM Sans, sans-serif" },
      chart: { fontFamily: "DM Sans, sans-serif", toolbar: { show: false } },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`,
        style: { fontSize: "11px", fontFamily: "DM Sans, sans-serif", fontWeight: 600 },
        dropShadow: { enabled: false },
      },
      stroke: { width: 2, colors: ["#ffffff"] },
      tooltip: {
        theme: "light",
        y: { formatter: (val) => `${val} calls` },
      },
    }),
    [monthReasonPie.labels, reasonForCallColors]
  );

  const reasonForCallOptions = React.useMemo(() => {
    return {
      labels: reasonForCallLabels,
      colors: reasonForCallColors,
      chart: {
        toolbar: { show: false },
        fontFamily: "DM Sans, sans-serif",
        events: {
          dataPointSelection: (event, chartContext, config) => {
            try {
              setTimeout(() => {
                if (config && typeof config.dataPointIndex !== "undefined" && config.dataPointIndex !== null) {
                  const index = config.dataPointIndex;
                  if (reasonForCallLabels && reasonForCallLabels.length > index && reasonForCallLabels[index]) {
                    const selectedReason = reasonForCallLabels[index];
                    if (selectedReason === "Others") {
                      setShowOthersReasonsModal(true);
                    } else {
                      setSelectedReasonFilter((prev) => (prev === selectedReason ? null : selectedReason));
                    }
                  }
                }
              }, 0);
            } catch (error) {
              console.error("Error in reason chart click handler:", error);
            }
          },
        },
      },
      legend: { show: false },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
        style: {
          fontSize: "11px",
          fontFamily: "DM Sans, sans-serif",
          fontWeight: 600,
        },
        dropShadow: { enabled: false },
      },
      stroke: { width: 2, colors: ["#ffffff"] },
      tooltip: {
        theme: "light",
        style: { fontSize: "12px", fontFamily: "DM Sans, sans-serif" },
        y: {
          formatter: (val) => {
            const pct = reasonForCallTotal > 0 ? ((val / reasonForCallTotal) * 100).toFixed(1) : "0";
            return `${val} calls (${pct}%)`;
          },
        },
      },
      states: {
        hover: { filter: { type: "darken", value: 0.9 } },
        active: { filter: { type: "none" } },
      },
      responsive: [
        {
          breakpoint: 640,
          options: { dataLabels: { enabled: false } },
        },
      ],
    };
  }, [reasonForCallLabels, reasonForCallColors, reasonForCallTotal]);

  const criteriaBarData = React.useMemo(() => {
    const criteria = [
      { key: "call_opening", label: "Call Opening" },
      { key: "friendliness", label: "Friendliness" },
      { key: "energy", label: "Energy" },
      { key: "demonstrates_ownership", label: "Ownership" },
      { key: "listening", label: "Listening" },
      { key: "questioning", label: "Questioning" },
      { key: "paraphrasing", label: "Paraphrasing" },
      { key: "clear_concise_explanation_fcr", label: "Clear & Concise" },
      { key: "offers_additional_assistance", label: "Additional Help" },
      { key: "empathy", label: "Empathy" },
      { key: "hold_procedure_dead_air", label: "Hold / Dead Air" },
      { key: "grammar", label: "Grammar" },
      { key: "pronunciation", label: "Pronunciation" },
      { key: "appropriate_responses", label: "Responses" },
      { key: "sentence_structuring", label: "Sentence Structure" },
      { key: "pace", label: "Pace" },
      { key: "addressing_customer", label: "Addressing Customer" },
      { key: "sounds_reassuring", label: "Reassurance" },
      { key: "confidence", label: "Confidence" },
      { key: "intonation", label: "Intonation" },
      { key: "closing_call", label: "Closing Call" },
      { key: "transfer_procedure", label: "Transfer Procedure" },
    ];

    // Use filteredRows for all users (personal data for team members)
    const dataForChart = filteredRows;
    const total = dataForChart.length || 0;
    const categories = criteria.map((c) => c.label);

    if (total === 0) {
      return {
        series: [
          {
            name: "% Met",
            data: criteria.map(() => 0),
          },
        ],
        options: {
          chart: {
            toolbar: { show: false },
            fontFamily: "DM Sans, sans-serif",
          },
          plotOptions: {
            bar: {
              horizontal: true,
              borderRadius: 4,
            },
          },
          xaxis: {
            categories,
            max: 100,
            labels: {
              formatter: (val) => `${val}%`,
            },
          },
          dataLabels: {
            enabled: false,
          },
          colors: ["#0ea5e9"],
          grid: {
            borderColor: "#E5E7EB",
          },
        },
      };
    }

    const data = criteria.map(({ key }) => {
      let yesCount = 0;
      dataForChart.forEach((row) => {
        const val = row[key];
        if (val === null || val === undefined) return;
        const s = String(val).toLowerCase().trim();
        if (s === "yes" || s === "y" || s === "1" || s === "true") {
          yesCount += 1;
        }
      });
      return Number(((yesCount / total) * 100).toFixed(1));
    });

    return {
      series: [
        {
          name: "% Met",
          data,
        },
      ],
      options: {
        chart: {
          toolbar: { show: false },
          fontFamily: "DM Sans, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: false, // vertical bars
            borderRadius: 4,
            columnWidth: "40%",
            distributed: true, // each bar can use different color
          },
        },
        xaxis: {
          categories,
          max: 100,
          labels: {
            formatter: (val) => `${val}%`,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: [
          "#8B9DC3",
          "#A8B5A0",
          "#B8A5C9",
          "#9DB5B2",
          "#C4A896",
          "#A5A8C3",
          "#B2B89D",
          "#C9A5B8",
          "#9DC3B5", 
          "#B5A5C9",
        ],
        legend: {
          show: false,
        },
        grid: {
          borderColor: "#E5E7EB",
        },
      },
    };
  }, [filteredRows]);

  // Helper function to format numbers and replace very small values with 0
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed) || parsed < 0.0001 || !isFinite(parsed)) return 0;
    return parsed;
  };

  const greatComponentDrilldown = React.useMemo(
    () => ({
      G: {
        label: "G - Greeting",
        items: ["Call Opening", "Friendliness", "Energy"],
      },
      R: {
        label: "R - Resolve",
        items: [
          "Demonstrates ownership",
          "Listening",
          "Questioning",
          "Paraphrasing",
          "Clear & Concise explanation (FCR)",
          "Offers additional assistance",
        ],
      },
      E: {
        label: "E - Engage",
        items: [
          "Empathy",
          "Hold / Dead Air",
          "Grammar",
          "Pronunciation",
          "Appropriate responses",
          "Sentence structuring",
          "Pace",
          "Addressing customer",
        ],
      },
      A: {
        label: "A - Assure",
        items: ["Sounds reassuring", "Confidence", "Intonation"],
      },
      T: {
        label: "T - Thank",
        items: ["Closing Call", "Transfer Procedure"],
      },
    }),
    []
  );

  const greatComponentMetricMap = React.useMemo(
    () => ({
      G: [
        { key: "call_opening", label: "Call Opening" },
        { key: "friendliness", label: "Friendliness" },
        { key: "energy", label: "Energy" },
      ],
      R: [
        { key: "demonstrates_ownership", label: "Demonstrates ownership" },
        { key: "listening", label: "Listening" },
        { key: "questioning", label: "Questioning" },
        { key: "paraphrasing", label: "Paraphrasing" },
        { key: "clear_concise_explanation_fcr", label: "Clear & Concise explanation (FCR)" },
        { key: "offers_additional_assistance", label: "Offers additional assistance" },
      ],
      E: [
        { key: "empathy", label: "Empathy" },
        { key: "hold_procedure_dead_air", label: "Hold / Dead Air" },
        { key: "grammar", label: "Grammar" },
        { key: "pronunciation", label: "Pronunciation" },
        { key: "appropriate_responses", label: "Appropriate responses" },
        { key: "sentence_structuring", label: "Sentence structuring" },
        { key: "pace", label: "Pace" },
        { key: "addressing_customer", label: "Addressing customer" },
      ],
      A: [
        { key: "sounds_reassuring", label: "Sounds reassuring" },
        { key: "confidence", label: "Confidence" },
        { key: "intonation", label: "Intonation" },
      ],
      T: [
        { key: "closing_call", label: "Closing Call" },
        { key: "transfer_procedure", label: "Transfer Procedure" },
      ],
    }),
    []
  );

  // Helper — consistent "Mon YYYY" period label for a row, matching the
  // grouping used by `timeSeriesData`/`last3Months` below, so per-metric and
  // per-rep monthly breakdowns line up with the same 3 months.
  // Recommendations based on low scores
  const recommendations = React.useMemo(() => {
    const criteria = [
      { key: "call_opening", label: "Call Opening", description: "How the call was initiated and greeted" },
      { key: "friendliness", label: "Friendliness", description: "Warmth and positive interaction with customer" },
      { key: "energy", label: "Energy", description: "Enthusiasm and engagement level during the call" },
      { key: "demonstrates_ownership", label: "Ownership", description: "Taking responsibility for customer issues" },
      { key: "listening", label: "Listening", description: "Active listening and understanding customer needs" },
      { key: "questioning", label: "Questioning", description: "Asking appropriate questions to understand issues" },
      { key: "paraphrasing", label: "Paraphrasing", description: "Restating customer concerns to confirm understanding" },
      { key: "clear_concise_explanation_fcr", label: "Clear & Concise", description: "Providing clear and concise explanations" },
      { key: "offers_additional_assistance", label: "Additional Help", description: "Offering additional support when appropriate" },
      { key: "empathy", label: "Empathy", description: "Showing understanding and compassion for customer concerns" },
      { key: "hold_procedure_dead_air", label: "Hold / Dead Air", description: "Proper handling of holds and avoiding silence" },
      { key: "grammar", label: "Grammar", description: "Correct use of grammar in communication" },
      { key: "pronunciation", label: "Pronunciation", description: "Clear and correct pronunciation" },
      { key: "appropriate_responses", label: "Responses", description: "Providing appropriate responses to customer queries" },
      { key: "sentence_structuring", label: "Sentence Structure", description: "Well-structured and clear sentences" },
      { key: "pace", label: "Pace", description: "Appropriate speaking pace for customer understanding" },
      { key: "addressing_customer", label: "Addressing Customer", description: "Properly addressing and acknowledging the customer" },
      { key: "sounds_reassuring", label: "Reassurance", description: "Providing reassurance to the customer" },
      { key: "confidence", label: "Confidence", description: "Displaying confidence in handling the call" },
      { key: "intonation", label: "Intonation", description: "Appropriate voice intonation and tone" },
      { key: "closing_call", label: "Closing Call", description: "Proper call closure and follow-up" },
      { key: "transfer_procedure", label: "Transfer Procedure", description: "Correct procedure when transferring calls" },
    ];

    // For team members, recommendations are based only on their own calls (filteredRows)
    // For supervisors/admins, recommendations are based on filteredRows
    const dataForRecommendations = filteredRows;
    const total = dataForRecommendations.length || 0;
    if (total === 0) return { items: [], allPerfect: false };

    const criteriaScores = criteria.map(({ key, label, description }) => {
      let yesCount = 0;
      dataForRecommendations.forEach((row) => {
        const val = row[key];
        if (val === null || val === undefined) return;
        const s = String(val).toLowerCase().trim();
        if (s === "yes" || s === "y" || s === "1" || s === "true") {
          yesCount += 1;
        }
      });
      const percentage = (yesCount / total) * 100;
      return { label, description, percentage, key };
    });

    // Sort by lowest percentage
    const sorted = criteriaScores.sort((a, b) => a.percentage - b.percentage);
    const allPerfect = sorted.every(item => item.percentage >= 100);

    if (allPerfect) {
      return { items: [], allPerfect: true };
    }

    // Always show 2-3 recommendations (the worst scoring ones)
    // Show 3 if the 3rd worst is meaningfully below perfect, otherwise 2
    const topWorst = sorted.slice(0, 3);
    const items = topWorst.length >= 3 && topWorst[2].percentage >= 95 
      ? topWorst.slice(0, 2) 
      : topWorst;

    return {
      items: items.length < 2 ? sorted.slice(0, 2) : items,
      allPerfect: false
    };
  }, [filteredRows]);

  // Top 5 Best Performers based on average score
  // For team members: show their team members (same supervisor) - use all rows filtered by team and date range
  // For supervisors/admins: show based on filteredRows
  const topPerformers = React.useMemo(() => {
    const performerStats = {};
    
    // For team members, use all rows but filter to team members and apply date range
    // For supervisors/admins, use filteredRows
    let dataForTopPerformers = filteredRows;
    
    if (isTeamMember && teamMemberTeamList.length > 0) {
      // Use all rows, filter by team members and date range
      const hasDateRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
      const startDate = hasDateRange ? new Date(dateRange[0]) : null;
      const endDate = hasDateRange ? new Date(dateRange[1]) : null;

      const normalizeDate = normalizeGreatRowDate;

      dataForTopPerformers = rows.filter((row) => {
        // Direction filter (Inbound | Outbound) — keep in sync with filteredRows.
        if (!matchesDirection(row, directionFilter)) return false;
        // Filter by team members
        if (!teamMemberTeamList.includes(row.advisor_name)) {
          return false;
        }
        
        // Apply date range filter if set
        if (hasDateRange) {
          const d = normalizeDate(row);
          if (!d) return false;
          const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          const sOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const eOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          if (dOnly < sOnly || dOnly > eOnly) return false;
        }
        
        return true;
      });
    }
    
    dataForTopPerformers.forEach((row) => {
      if (!row.advisor_name) return;
      
      const advisorName = String(row.advisor_name).trim();
      if (!advisorName) return;
      
      // For team members, only include team members (same supervisor)
      if (isTeamMember && teamMemberTeamList.length > 0) {
        if (!teamMemberTeamList.includes(advisorName)) {
          return;
        }
      }
      
      if (!performerStats[advisorName]) {
        performerStats[advisorName] = {
          name: advisorName,
          totalScore: 0,
          count: 0,
        };
      }
      
      if (row.total_score !== null && row.total_score !== undefined) {
        const score = parseFloat(row.total_score);
        if (!isNaN(score)) {
          performerStats[advisorName].totalScore += score;
          performerStats[advisorName].count += 1;
        }
      }
    });
    
    // Calculate averages and filter out those with no scores
    const performers = Object.values(performerStats)
      .filter(p => p.count > 0)
      .map(p => ({
        name: p.name,
        avgScore: formatNumber(p.totalScore / p.count),
        count: p.count,
      }))
      .sort((a, b) => parseFloat(b.avgScore) - parseFloat(a.avgScore))
      .slice(0, 5); // Top 5
    
    return performers;
  }, [filteredRows, rows, isTeamMember, teamMemberTeamList, dateRange, departmentFilter, directionFilter]);

  // Determine if we should show worst performers (when grade B or C is filtered)
  const showWorstPerformers = selectedGradeFilter === 'B' || selectedGradeFilter === 'C';

  // Top 5 Worst Performers ranked by call count (shown when grade B/C is selected)
  // For team members: show their team members (same supervisor) - use all rows filtered by team and date range
  // For supervisors/admins: show based on filteredRows
  const worstPerformers = React.useMemo(() => {
    if (!showWorstPerformers) return [];

    const performerStats = {};

    let dataForWorstPerformers = filteredRows;

    if (isTeamMember && teamMemberTeamList.length > 0) {
      const hasDateRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
      const startDate = hasDateRange ? new Date(dateRange[0]) : null;
      const endDate = hasDateRange ? new Date(dateRange[1]) : null;

      const normalizeDate = normalizeGreatRowDate;

      dataForWorstPerformers = rows.filter((row) => {
        // Direction filter (Inbound | Outbound) — keep in sync with filteredRows.
        if (!matchesDirection(row, directionFilter)) return false;
        if (!teamMemberTeamList.includes(row.advisor_name)) {
          return false;
        }
        if (hasDateRange) {
          const d = normalizeDate(row);
          if (!d) return false;
          const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          const sOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const eOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          if (dOnly < sOnly || dOnly > eOnly) return false;
        }
        if (selectedGradeFilter) {
          const score = row.total_score !== null && row.total_score !== undefined
            ? parseFloat(row.total_score)
            : null;
          if (score !== null && !isNaN(score)) {
            if (selectedGradeFilter === 'A' && score < 90) return false;
            if (selectedGradeFilter === 'B' && (score < 80 || score >= 90)) return false;
            if (selectedGradeFilter === 'C' && score >= 80) return false;
          } else {
            return false;
          }
        }
        return true;
      });
    }

    dataForWorstPerformers.forEach((row) => {
      if (!row.advisor_name) return;
      const name = String(row.advisor_name).trim();
      if (!name) return;

      if (isTeamMember && teamMemberTeamList.length > 0) {
        if (!teamMemberTeamList.includes(name)) {
          return;
        }
      }

      if (!performerStats[name]) {
        performerStats[name] = {
          name,
          totalScore: 0,
          count: 0,
        };
      }

      if (row.total_score !== null && row.total_score !== undefined) {
        const score = parseFloat(row.total_score);
        if (!isNaN(score)) {
          performerStats[name].totalScore += score;
          performerStats[name].count += 1;
        }
      }
    });

    // Rank by call count (most calls first among worst performers)
    return Object.values(performerStats)
      .filter(p => p.count > 0)
      .map(p => ({
        name: p.name,
        avgScore: formatNumber(p.totalScore / p.count),
        count: p.count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filteredRows, rows, showWorstPerformers, isTeamMember, teamMemberTeamList, dateRange, selectedGradeFilter, directionFilter]);

  const qaSentimentBreakdown = React.useMemo(() => {
    const counts = { positive: 0, negative: 0, neutral: 0 };
    filteredRows.forEach((row) => {
      if (!voiceSentimentHasValue(row)) return;
      const key = voiceSentimentKey(row);
      if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
    });
    const total = counts.positive + counts.neutral + counts.negative;
    const pct = (n) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);
    return {
      total,
      positive: counts.positive,
      negative: counts.negative,
      neutral: counts.neutral,
      positivePct: pct(counts.positive),
      negativePct: pct(counts.negative),
      neutralPct: pct(counts.neutral),
    };
  }, [filteredRows]);

  const sentimentReasonOptions = React.useMemo(
    () => buildSentimentReasonFilterOptions(filteredRows, sentimentFilter, voiceSentimentKey),
    [filteredRows, sentimentFilter]
  );

  const qaSentimentTopReasons = React.useMemo(
    () => ({
      positive: buildTopSentimentReasonBuckets(filteredRows, "positive", 5, voiceSentimentKey),
      neutral: buildTopSentimentReasonBuckets(filteredRows, "neutral", 5, voiceSentimentKey),
      negative: buildTopSentimentReasonBuckets(filteredRows, "negative", 5, voiceSentimentKey),
    }),
    [filteredRows]
  );

  const callReasonOthersDetails = React.useMemo(() => {
    if (!reasonForCallLabels.includes("Others")) return [];
    const topLabels = computeOthersTopCallReasonLabels(rowsForReasonChart, reasonBucketMode);
    const freq = new Map();
    rowsForReasonChart.forEach((row) => {
      if (!reasonMatchesSelectedFilter(row.reason_for_call, "Others", reasonBucketMode, topLabels)) return;
      const label = canonicalizeReasonText(row.reason_for_call || "Unknown");
      freq.set(label, (freq.get(label) || 0) + 1);
    });
    return [...freq.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [reasonForCallLabels, rowsForReasonChart, reasonBucketMode]);

  // Top 5 Best Call Grades for team members (grade buckets sorted descending)
  const topBestGradeBuckets = React.useMemo(() => {
    if (!isTeamMember) return [];
    const buckets = {};
    filteredRows.forEach((row) => {
      if (row.total_score === null || row.total_score === undefined) return;
      const score = parseFloat(row.total_score);
      if (isNaN(score)) return;
      const rounded = Math.round(score);
      if (!buckets[rounded]) buckets[rounded] = { gradeValue: rounded, count: 0 };
      buckets[rounded].count += 1;
    });
    return Object.values(buckets)
      .sort((a, b) => b.gradeValue - a.gradeValue)
      .slice(0, 5);
  }, [filteredRows, isTeamMember]);

  // Top 5 Worst Call Grades for team members (grade buckets sorted ascending)
  const topWorstGradeBuckets = React.useMemo(() => {
    if (!isTeamMember) return [];
    const buckets = {};
    filteredRows.forEach((row) => {
      if (row.total_score === null || row.total_score === undefined) return;
      const score = parseFloat(row.total_score);
      if (isNaN(score)) return;
      const rounded = Math.round(score);
      if (!buckets[rounded]) buckets[rounded] = { gradeValue: rounded, count: 0 };
      buckets[rounded].count += 1;
    });
    return Object.values(buckets)
      .sort((a, b) => a.gradeValue - b.gradeValue)
      .slice(0, 5);
  }, [filteredRows, isTeamMember]);

  // Daily/Monthly/Quarterly data for line charts with GREAT components
  const timeSeriesData = React.useMemo(() => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dailyStats = {};
    const monthlyStats = {};
    const quarterlyStats = {};

    filteredRows.forEach((row) => {
      const dateStr = row.date_of_call || row.created_at;
      const date = parseRowDate(dateStr);
      if (!date) return;

      const month = date.getMonth();
      const year = date.getFullYear();
      const day = date.getDate();
      const monthKey = `${year}-${month}`;
      const dayKey = `${year}-${month}-${day}`;
      const quarter = Math.floor(month / 3) + 1;
      const quarterKey = `Q${quarter} ${year}`;

      // Initialize daily stats
      if (!dailyStats[dayKey]) {
        dailyStats[dayKey] = {
          date: new Date(year, month, day),
          period: `${monthNames[month]} ${day}, ${year}`,
          greet: [],
          resolve: [],
          engage: [],
          assure: [],
          thank: [],
          totalScore: [],
          errorCount: 0,
        };
      }

      // Initialize monthly stats
      if (!monthlyStats[monthKey]) {
        monthlyStats[monthKey] = {
          month,
          year,
          monthName: monthNames[month],
          period: `${monthNames[month]} ${year}`,
          greet: [],
          resolve: [],
          engage: [],
          assure: [],
          thank: [],
          totalScore: [],
          errorCount: 0,
        };
      }

      // Initialize quarterly stats
      if (!quarterlyStats[quarterKey]) {
        quarterlyStats[quarterKey] = {
          period: quarterKey,
          greet: [],
          resolve: [],
          engage: [],
          assure: [],
          thank: [],
          totalScore: [],
          errorCount: 0,
        };
      }

      // Collect GREAT component scores
      const greet = formatNumber(row.greet_total);
      const resolve = formatNumber(row.resolve_total);
      const engage = formatNumber(row.engage_total);
      const assure = formatNumber(row.assure_total);
      const thank = formatNumber(row.thank_total);

      if (greet > 0) {
        dailyStats[dayKey].greet.push(greet);
        monthlyStats[monthKey].greet.push(greet);
        quarterlyStats[quarterKey].greet.push(greet);
      }
      if (resolve > 0) {
        dailyStats[dayKey].resolve.push(resolve);
        monthlyStats[monthKey].resolve.push(resolve);
        quarterlyStats[quarterKey].resolve.push(resolve);
      }
      if (engage > 0) {
        dailyStats[dayKey].engage.push(engage);
        monthlyStats[monthKey].engage.push(engage);
        quarterlyStats[quarterKey].engage.push(engage);
      }
      if (assure > 0) {
        dailyStats[dayKey].assure.push(assure);
        monthlyStats[monthKey].assure.push(assure);
        quarterlyStats[quarterKey].assure.push(assure);
      }
      if (thank > 0) {
        dailyStats[dayKey].thank.push(thank);
        monthlyStats[monthKey].thank.push(thank);
        quarterlyStats[quarterKey].thank.push(thank);
      }

      if (row.total_score !== null && row.total_score !== undefined) {
        const score = formatNumber(row.total_score);
        if (score > 0) {
          dailyStats[dayKey].totalScore.push(score);
          monthlyStats[monthKey].totalScore.push(score);
          quarterlyStats[quarterKey].totalScore.push(score);
        }
      }
      if (voiceRowHasError(row)) {
        dailyStats[dayKey].errorCount += 1;
        monthlyStats[monthKey].errorCount += 1;
        quarterlyStats[quarterKey].errorCount += 1;
      }
    });

    // Convert daily stats to array with percentages
    const dailyArray = Object.values(dailyStats)
      .map((stat) => {
        const greetAvg = stat.greet.length > 0 ? stat.greet.reduce((a, b) => a + b, 0) / stat.greet.length : 0;
        const resolveAvg = stat.resolve.length > 0 ? stat.resolve.reduce((a, b) => a + b, 0) / stat.resolve.length : 0;
        const engageAvg = stat.engage.length > 0 ? stat.engage.reduce((a, b) => a + b, 0) / stat.engage.length : 0;
        const assureAvg = stat.assure.length > 0 ? stat.assure.reduce((a, b) => a + b, 0) / stat.assure.length : 0;
        const thankAvg = stat.thank.length > 0 ? stat.thank.reduce((a, b) => a + b, 0) / stat.thank.length : 0;
        return {
          period: stat.period,
          date: stat.date,
          greet: formatNumber((greetAvg / GREAT_MAX_SCORES.G) * 100),
          resolve: formatNumber((resolveAvg / GREAT_MAX_SCORES.R) * 100),
          engage: formatNumber((engageAvg / GREAT_MAX_SCORES.E) * 100),
          assure: formatNumber((assureAvg / GREAT_MAX_SCORES.A) * 100),
          thank: formatNumber((thankAvg / GREAT_MAX_SCORES.T) * 100),
          avgScore:
            stat.totalScore.length > 0
              ? formatNumber(stat.totalScore.reduce((a, b) => a + b, 0) / stat.totalScore.length)
              : 0,
          callCount: stat.totalScore.length,
          errorCount: stat.errorCount || 0,
        };
      })
      .sort((a, b) => a.date - b.date);

    // Convert monthly stats to array with percentages
    const monthlyArray = Object.values(monthlyStats)
      .map((stat) => {
        const greetAvg = stat.greet.length > 0 ? stat.greet.reduce((a, b) => a + b, 0) / stat.greet.length : 0;
        const resolveAvg = stat.resolve.length > 0 ? stat.resolve.reduce((a, b) => a + b, 0) / stat.resolve.length : 0;
        const engageAvg = stat.engage.length > 0 ? stat.engage.reduce((a, b) => a + b, 0) / stat.engage.length : 0;
        const assureAvg = stat.assure.length > 0 ? stat.assure.reduce((a, b) => a + b, 0) / stat.assure.length : 0;
        const thankAvg = stat.thank.length > 0 ? stat.thank.reduce((a, b) => a + b, 0) / stat.thank.length : 0;
        return {
          period: stat.period,
          greet: formatNumber((greetAvg / GREAT_MAX_SCORES.G) * 100),
          resolve: formatNumber((resolveAvg / GREAT_MAX_SCORES.R) * 100),
          engage: formatNumber((engageAvg / GREAT_MAX_SCORES.E) * 100),
          assure: formatNumber((assureAvg / GREAT_MAX_SCORES.A) * 100),
          thank: formatNumber((thankAvg / GREAT_MAX_SCORES.T) * 100),
          avgScore:
            stat.totalScore.length > 0
              ? formatNumber(stat.totalScore.reduce((a, b) => a + b, 0) / stat.totalScore.length)
              : 0,
          callCount: stat.totalScore.length,
          errorCount: stat.errorCount || 0,
        };
      })
      .sort((a, b) => {
        const aDate = new Date(a.period);
        const bDate = new Date(b.period);
        return aDate - bDate;
      });

    // Convert quarterly stats to array with percentages
    const quarterlyArray = Object.values(quarterlyStats)
      .map((q) => {
        const greetAvg = q.greet.length > 0 ? q.greet.reduce((a, b) => a + b, 0) / q.greet.length : 0;
        const resolveAvg = q.resolve.length > 0 ? q.resolve.reduce((a, b) => a + b, 0) / q.resolve.length : 0;
        const engageAvg = q.engage.length > 0 ? q.engage.reduce((a, b) => a + b, 0) / q.engage.length : 0;
        const assureAvg = q.assure.length > 0 ? q.assure.reduce((a, b) => a + b, 0) / q.assure.length : 0;
        const thankAvg = q.thank.length > 0 ? q.thank.reduce((a, b) => a + b, 0) / q.thank.length : 0;
        return {
          period: q.period,
          greet: formatNumber((greetAvg / GREAT_MAX_SCORES.G) * 100),
          resolve: formatNumber((resolveAvg / GREAT_MAX_SCORES.R) * 100),
          engage: formatNumber((engageAvg / GREAT_MAX_SCORES.E) * 100),
          assure: formatNumber((assureAvg / GREAT_MAX_SCORES.A) * 100),
          thank: formatNumber((thankAvg / GREAT_MAX_SCORES.T) * 100),
          avgScore:
            q.totalScore.length > 0
              ? formatNumber(q.totalScore.reduce((a, b) => a + b, 0) / q.totalScore.length)
              : 0,
          callCount: q.totalScore.length,
          errorCount: q.errorCount || 0,
        };
      })
      .sort((a, b) => {
        // period format: "Q<quarter> <year>", e.g., "Q4 2025"
        const [qa, yearA] = String(a.period).split(" ");
        const [qb, yearB] = String(b.period).split(" ");
        const quarterA = parseInt(qa?.replace("Q", ""), 10) || 0;
        const quarterB = parseInt(qb?.replace("Q", ""), 10) || 0;
        const yA = parseInt(yearA, 10) || 0;
        const yB = parseInt(yearB, 10) || 0;

        if (yA !== yB) return yA - yB;
        return quarterA - quarterB;
      });

    return { daily: dailyArray, monthly: monthlyArray, quarterly: quarterlyArray };
  }, [filteredRows]);

  const scoreLineChartData = React.useMemo(() => {
    let data = [];
    if (timePeriodFilter === "daily") {
      data = timeSeriesData.daily;
    } else if (timePeriodFilter === "quarterly") {
      data = timeSeriesData.quarterly;
    } else {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const byPeriod = new Map(timeSeriesData.monthly.map((d) => [d.period, d]));
      const now = new Date();
      // Inbound keeps the Dec 2025 window. Outbound scoring starts Jun 2026;
      // include May 2026 as an empty leading month so Jun is not on the y-axis.
      const start = isOutbound
        ? new Date(2026, 4, 1)
        : new Date(now.getFullYear() - 1, 11, 1);
      const filled = [];
      for (let d = new Date(start); d <= now; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
        const period = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
        filled.push(
          byPeriod.get(period) || {
            period,
            greet: 0,
            resolve: 0,
            engage: 0,
            assure: 0,
            thank: 0,
            avgScore: 0,
            callCount: 0,
            errorCount: 0,
          }
        );
      }
      data = filled;
    }
    const showReturnErrors = !isOutbound;
    if (data.length === 0) {
      return {
        series: showReturnErrors
          ? [
              { name: "Average Score", type: "line", data: [] },
              { name: VOICE_RETURN_ERRORS_SERIES_NAME, type: "line", data: [] },
            ]
          : [{ name: "Average Score", type: "line", data: [] }],
        options: {
          chart: { toolbar: { show: false }, fontFamily: "DM Sans, sans-serif" },
          xaxis: { categories: [] },
          yaxis: showReturnErrors
            ? [
                { labels: { formatter: (val) => fmtScore2(val) }, max: 100, min: 0 },
                {
                  opposite: true,
                  min: 0,
                  max: 24,
                  tickAmount: 8,
                  labels: { formatter: (val) => String(Math.round(Number(val) || 0)) },
                },
              ]
            : [{ labels: { formatter: (val) => fmtScore2(val) }, max: 100, min: 0 }],
          stroke: { curve: "smooth", width: 2 },
          colors: showReturnErrors ? ["#10B981", "#EF4444"] : ["#10B981"],
          dataLabels: { enabled: false },
        },
      };
    }

    // Plot the true per-period average (sum of total_score / number of scored
    // calls in that period). Periods without any scored calls render as a gap
    // (null) rather than a misleading 0 or a value smoothed toward the global
    // average — prior Bayesian shrinkage flattened real variation and made
    // low-volume periods look artificially close to the overall mean.
    const seriesData = data.map((d) =>
      d.callCount > 0 ? Number(formatNumber(d.avgScore)) : null
    );
    const errorSeriesData = data.map((d) => Number(d.errorCount || 0));
    const nonNullValues = seriesData.filter((v) => v !== null && !Number.isNaN(v));
    const yMin =
      nonNullValues.length > 0
        ? Math.max(0, Math.floor(Math.min(...nonNullValues) - 5))
        : 0;

    const scoreAxis = {
      seriesName: "Average Score",
      labels: { formatter: (val) => fmtScore2(val) },
      max: 100,
      min: yMin,
      title: { text: "Score", style: { fontSize: "11px" } },
    };
    const errorAxis = {
      seriesName: VOICE_RETURN_ERRORS_SERIES_NAME,
      opposite: true,
      labels: { formatter: (val) => String(Math.round(Number(val) || 0)) },
      min: 0,
      max: 24,
      tickAmount: 8,
      forceNiceScale: false,
      title: {
        text: VOICE_RETURN_ERRORS_SERIES_NAME,
        style: { fontSize: "10px", color: "#111827" },
      },
    };

    return {
      series: showReturnErrors
        ? [
            { name: "Average Score", type: "line", data: seriesData },
            { name: VOICE_RETURN_ERRORS_SERIES_NAME, type: "line", data: errorSeriesData },
          ]
        : [{ name: "Average Score", type: "line", data: seriesData }],
      options: {
        chart: {
          toolbar: { show: false },
          fontFamily: "DM Sans, sans-serif",
        },
        xaxis: {
          categories: data.map((d) => d.period),
          labels: {
            style: {
              fontSize: "12px",
            },
            rotate: timePeriodFilter === "daily" ? -45 : 0,
          },
        },
        yaxis: showReturnErrors ? [scoreAxis, errorAxis] : scoreAxis,
        stroke: {
          curve: "smooth",
          width: showReturnErrors ? [2, 2] : 2,
        },
        colors: showReturnErrors ? ["#10B981", "#EF4444"] : ["#10B981"],
        grid: {
          padding: { top: 32, right: 8, left: 4, bottom: 0 },
        },
        plotOptions: {
          line: {
            dataLabels: {
              hideOverlappingLabels: false,
            },
          },
        },
        dataLabels: showReturnErrors
          ? {
              enabled: true,
              enabledOnSeries: [1],
              formatter: (val, opts) => {
                if (opts?.seriesIndex !== 1) return "";
                return String(Math.round(Number(val) || 0));
              },
              offsetY: -10,
              style: {
                fontSize: "11px",
                fontWeight: 700,
                colors: ["#111827"],
              },
              background: { enabled: false },
            }
          : { enabled: false },
        annotations: {
          points: data.flatMap((d, idx) => {
            const y = seriesData[idx];
            if (y === null || y === undefined || Number.isNaN(y)) return [];
            const count = d.callCount ?? 0;
            const scoreText = fmtScore2(y);
            return [
              {
                x: d.period,
                y,
                marker: { size: 0, strokeWidth: 0, fill: "transparent" },
                label: {
                  text: scoreText,
                  offsetY: -26,
                  borderWidth: 0,
                  style: {
                    color: "#334155",
                    fontSize: "11px",
                    fontWeight: 700,
                    background: "transparent",
                  },
                },
              },
              {
                x: d.period,
                y,
                marker: { size: 0, strokeWidth: 0, fill: "transparent" },
                label: {
                  text: String(count),
                  offsetY: -12,
                  borderWidth: 0,
                  style: {
                    color: "#64748B",
                    fontSize: "10px",
                    fontWeight: 600,
                    background: "transparent",
                  },
                },
              },
            ];
          }),
        },
        tooltip: {
          shared: true,
          y: showReturnErrors
            ? [
                {
                  formatter: (val) =>
                    val === null || val === undefined ? "No data" : fmtScore2(val),
                },
                {
                  formatter: (val) => `${Math.round(val)} error${Math.round(val) === 1 ? "" : "s"}`,
                },
              ]
            : {
                formatter: (val) =>
                  val === null || val === undefined ? "No data" : fmtScore2(val),
              },
        },
        markers: {
          size: 4,
          hover: { size: 6 },
        },
        legend: {
          show: showReturnErrors,
          position: "bottom",
          fontSize: "11px",
          formatter: (seriesName) =>
            seriesName === VOICE_RETURN_ERRORS_SERIES_NAME
              ? VOICE_RETURN_ERRORS_SERIES_NAME
              : seriesName,
        },
      },
    };
  }, [timeSeriesData, timePeriodFilter, isOutbound]);

  // Last 3 calendar months (chronological) of the monthly GREAT breakdown,
  // regardless of the daily/monthly/quarterly toggle above — these two charts
  // are always "last 3 months" by design.
  const last3Months = React.useMemo(
    () => timeSeriesData.monthly.slice(-3),
    [timeSeriesData]
  );

  const errorsLast3Months = React.useMemo(() => {
    const periods = last3Months.map((m) => m.period);
    const stats = {};
    periods.forEach((p) => {
      stats[p] = { total: 0, errors: 0 };
    });
    filteredRows.forEach((row) => {
      const period = getRowMonthPeriod(row);
      if (!period || !stats[period]) return;
      stats[period].total += 1;
      if (voiceRowHasError(row)) stats[period].errors += 1;
    });
    return periods.map((period) => {
      const s = stats[period];
      const pct = s.total > 0 ? Math.round((s.errors / s.total) * 1000) / 10 : 0;
      return { period, total: s.total, errors: s.errors, pct };
    });
  }, [filteredRows, last3Months]);

  const currentMonthErrorsSummary =
    errorsLast3Months[errorsLast3Months.length - 1] || { period: "", total: 0, errors: 0, pct: 0 };

  // Grade A/B/C distribution broken out per month (last 3 months), for the
  // 3-pie-chart "GREAT Grade Distribution" view below.
  const gradeSeriesByMonth = React.useMemo(() => {
    const periods = last3Months.map((m) => m.period);
    const stats = {};
    periods.forEach((p) => {
      stats[p] = { gradeA: 0, gradeB: 0, gradeC: 0 };
    });
    filteredRows.forEach((row) => {
      const period = getRowMonthPeriod(row);
      if (!period || !stats[period]) return;
      if (row?.total_score === null || row?.total_score === undefined) return;
      const score = parseFloat(row.total_score);
      if (Number.isNaN(score)) return;
      if (score >= 90) stats[period].gradeA += 1;
      else if (score >= 80) stats[period].gradeB += 1;
      else stats[period].gradeC += 1;
    });
    return periods.map((p) => ({ period: p, ...stats[p] }));
  }, [filteredRows, last3Months]);

  const buildMonthlyGradeOptions = (period) => ({
    labels: ["Grade A", "Grade B", "Grade C"],
    colors: ["#10B981", "#F59E0B", "#EF4444"],
    chart: {
      toolbar: { show: false },
      fontFamily: "DM Sans, sans-serif",
      events: {
        // Clicking a slice on ANY of the 3 monthly pies sets the same
        // page-wide grade filter the single pie used to control.
        dataPointSelection: (_event, _chartContext, config) => {
          try {
            setTimeout(() => {
              if (config && typeof config.dataPointIndex !== "undefined" && config.dataPointIndex !== null) {
                const grades = ["A", "B", "C"];
                const selectedGrade = grades[config.dataPointIndex];
                if (selectedGrade) {
                  setSelectedGradeFilter((prev) => (prev === selectedGrade ? null : selectedGrade));
                }
              }
            }, 0);
          } catch (error) {
            console.error("Error in grade chart click handler:", error);
          }
        },
      },
    },
    legend: {
      position: "bottom",
      fontFamily: "DM Sans, sans-serif",
      fontSize: "11px",
      // Same "value next to the label" treatment as the summary pies, so
      // small slices are never left without a legible number.
      formatter: function (seriesName, opts) {
        const series = opts.w.globals.series;
        const total = series.reduce((a, b) => a + b, 0);
        const value = series[opts.seriesIndex] ?? 0;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
        return `${seriesName}: ${value} (${pct}%)`;
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    stroke: { colors: ["#ffffff"] },
    tooltip: { y: { formatter: (val) => `${val} call${val !== 1 ? "s" : ""}` } },
  });

  // GREAT Components — grouped bar: one series per month (last 3 months) so
  // each G/R/E/A/T component shows 3 bars (one per recent month) instead of a
  // single blended average.
  const greatComponentsRecentBarData = React.useMemo(() => {
    const categories = ["G - Greeting", "R - Resolve", "E - Engage", "A - Assure", "T - Thank"];
    // Distinct, appealing hues per month (green/blue/purple) instead of
    // monochrome shades, so the 3 bars per component are easy to tell apart.
    const monthColors = ["#22C55E", "#3B82F6", "#8B5CF6"];
    const series = last3Months.map((m, idx) => ({
      name: m.period,
      data: [m.greet, m.resolve, m.engage, m.assure, m.thank],
      color: monthColors[(monthColors.length - last3Months.length + idx + monthColors.length) % monthColors.length],
    }));
    return {
      chartData: series,
      chartOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "DM Sans, sans-serif",
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const index = config?.dataPointIndex;
              if (typeof index !== "number" || index < 0) return;
              const keys = ["G", "R", "E", "A", "T"];
              const key = keys[index] || null;
              setExpandedGreatComponent((prev) => (prev === key ? null : key));
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            // Wider group (relative to the category slot) gives each of the
            // 3 monthly bars more individual width, so its label has more
            // room to sit without overlapping its neighbors.
            columnWidth: "80%",
            dataLabels: { position: "top" },
          },
        },
        xaxis: {
          categories,
          labels: { style: { fontSize: "12px", fontFamily: "DM Sans, sans-serif" } },
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          labels: { formatter: (val) => `${Math.round(val)}%`, style: { colors: ["#111827"] } },
        },
        // offsetY lifts the label above the bar (rather than centered inside
        // it) so small percentages still render a legible, non-clipped label
        // instead of being squeezed into a sliver of a bar.
        dataLabels: {
          enabled: true,
          offsetY: -22,
          formatter: (val) => `${Math.round(val)}%`,
          style: { colors: ["#111827"], fontWeight: 700, fontSize: "11px" },
          // A small light "pill" behind each label gives it a hard edge, so
          // adjacent labels (from the 3 tightly-packed monthly bars) read as
          // distinct values instead of visually running into each other.
          background: {
            enabled: true,
            foreColor: "#ffffff",
            borderRadius: 3,
            borderWidth: 0,
            opacity: 0.9,
            padding: 3,
          },
        },
        legend: { show: true, position: "bottom", fontFamily: "DM Sans, sans-serif" },
        grid: { borderColor: "#E5E7EB", padding: { top: 24 } },
      },
    };
  }, [last3Months]);

  // Individual (per representative) score trend — grouped bar, one series
  // per month (last 3 months) so each rep shows up to 3 bars, mirroring the
  // GREAT Components layout above. Replaces the old blended-average
  // "GREAT Over Time" line chart entirely.
  const repScoresRecentBarData = React.useMemo(() => {
    const periods = last3Months.map((m) => m.period);
    const currentPeriod = periods[periods.length - 1] || "";
    const repStats = {};
    const repErrorsRaw = {};
    filteredRows.forEach((row) => {
      const name = row?.advisor_name ? String(row.advisor_name).trim() : "";
      if (!name) return;
      const period = getRowMonthPeriod(row);
      if (!period || !periods.includes(period)) return;
      if (!repErrorsRaw[name]) repErrorsRaw[name] = {};
      if (!repErrorsRaw[name][period]) repErrorsRaw[name][period] = { total: 0, errors: 0 };
      repErrorsRaw[name][period].total += 1;
      if (voiceRowHasError(row)) repErrorsRaw[name][period].errors += 1;
      const score = parseFloat(row?.total_score);
      if (Number.isNaN(score)) return;
      if (!repStats[name]) repStats[name] = {};
      if (!repStats[name][period]) repStats[name][period] = { sum: 0, count: 0 };
      repStats[name][period].sum += score;
      repStats[name][period].count += 1;
    });
    const repErrorsByMonth = {};
    const repCurrentMonthErrors = {};
    Object.keys(repErrorsRaw).forEach((name) => {
      const monthStats = periods.map((period) => {
        const s = repErrorsRaw[name][period] || { total: 0, errors: 0 };
        const pct = s.total > 0 ? Math.round((s.errors / s.total) * 1000) / 10 : 0;
        return { period, total: s.total, errors: s.errors, pct };
      });
      repErrorsByMonth[name] = monthStats;
      const errors = monthStats.reduce((sum, m) => sum + m.errors, 0);
      const total = monthStats.reduce((sum, m) => sum + m.total, 0);
      repCurrentMonthErrors[name] = {
        total,
        errors,
        pct: total > 0 ? Math.round((errors / total) * 1000) / 10 : 0,
      };
    });

    const reps = Object.keys(repStats)
      .map((name) => {
        let totalSum = 0;
        let totalCount = 0;
        periods.forEach((p) => {
          const s = repStats[name][p];
          if (s) {
            totalSum += s.sum;
            totalCount += s.count;
          }
        });
        return { name, overallAvg: totalCount > 0 ? totalSum / totalCount : 0 };
      })
      .sort((a, b) => b.overallAvg - a.overallAvg)
      .map((r) => r.name);

    // Razmig AlMayan is pinned to the end of the list regardless of score
    // (by request), rather than sorting purely by average.
    const razmigIndex = reps.findIndex((name) => normalizeNameKey(name) === normalizeNameKey("Razmig AlMayan"));
    if (razmigIndex !== -1) {
      const [razmig] = reps.splice(razmigIndex, 1);
      reps.push(razmig);
    }

    reps.forEach((name) => {
      if (repErrorsByMonth[name]) return;
      const monthStats = periods.map((period) => ({
        period,
        total: 0,
        errors: 0,
        pct: 0,
      }));
      repErrorsByMonth[name] = monthStats;
      repCurrentMonthErrors[name] =
        monthStats.find((m) => m.period === currentPeriod) || { total: 0, errors: 0, pct: 0 };
    });

    // Distinct, appealing hues per month — different combination from the
    // GREAT Components chart above (teal/blue/amber) so each chart has its
    // own visual identity.
    const monthColors = ["#14B8A6", "#0EA5E9", "#F59E0B"];
    const series = periods.map((period, idx) => ({
      name: period,
      data: reps.map((name) => {
        const s = repStats[name][period];
        return s && s.count > 0 ? formatNumber(s.sum / s.count) : null;
      }),
      color: monthColors[(monthColors.length - periods.length + idx + monthColors.length) % monthColors.length],
    }));

    const chartMinWidth = Math.max(640, reps.length * 96);
    const chartPlotHeight = 320;
    // Must match grid.padding below so custom rep labels line up with bar groups.
    const plotPaddingLeft = 58;
    const plotPaddingRight = 14;
    const repLabelMinColPx = Math.max(72, Math.floor((chartMinWidth - plotPaddingLeft - plotPaddingRight) / Math.max(reps.length, 1)));
    const repCount = reps.length;
    const showRepBarLabels = repCount > 0;
    const repBarLabelFontSize =
      repCount > 18 ? "8px" : repCount > 12 ? "9px" : "10px";

    return {
      categories: reps,
      currentPeriod,
      periods,
      monthColors,
      chartMinWidth,
      chartPlotHeight,
      plotPaddingLeft,
      plotPaddingRight,
      repLabelMinColPx,
      repCurrentMonthErrors,
      repErrorsByMonth,
      chartData: series,
      chartOptions: {
        chart: { toolbar: { show: false }, fontFamily: "DM Sans, sans-serif" },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: reps.length > 10 ? "78%" : "88%",
            dataLabels: { position: "top" },
          },
        },
        xaxis: {
          categories: reps,
          labels: { show: false },
          axisBorder: { show: true, color: "#E5E7EB" },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          title: {
            text: "Score",
            style: { fontSize: "11px", fontWeight: 600, color: "#334155" },
          },
          labels: {
            formatter: (val) => `${Math.round(Number(val))}`,
            style: { colors: ["#64748B"], fontSize: "10px" },
          },
        },
        dataLabels: {
          enabled: showRepBarLabels,
          offsetY: -14,
          formatter: (val) => {
            if (val === null || val === undefined) return "";
            return formatNumber(val).toFixed(1);
          },
          style: {
            colors: ["#1e293b"],
            fontWeight: 600,
            fontSize: repBarLabelFontSize,
          },
          background: {
            enabled: true,
            foreColor: "#ffffff",
            padding: 4,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: "#e2e8f0",
            opacity: 0.95,
            dropShadow: { enabled: false },
          },
        },
        legend: { show: false },
        grid: {
          borderColor: "#E5E7EB",
          padding: {
            top: showRepBarLabels ? (repCount > 12 ? 36 : 32) : 16,
            right: plotPaddingRight,
            left: plotPaddingLeft,
            bottom: 0,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) =>
              val === null || val === undefined ? "No data" : formatNumber(val).toFixed(2),
          },
        },
      },
    };
  }, [filteredRows, last3Months]);

  // GREAT Components drill-down (click a component in the chart above) — now
  // also broken out by month (last 3 months) so e.g. "Call Opening" shows 3
  // bars just like the parent GREAT Components chart, instead of one
  // blended-average bar per sub-metric.
  const greatComponentMetricAveragesByMonth = React.useMemo(() => {
    const periods = last3Months.map((m) => m.period);
    const result = {};
    Object.entries(greatComponentMetricMap).forEach(([componentKey, metrics]) => {
      result[componentKey] = metrics.map((metric) => {
        const byPeriod = {};
        periods.forEach((p) => {
          byPeriod[p] = { yes: 0, total: 0 };
        });
        filteredRows.forEach((row) => {
          const period = getRowMonthPeriod(row);
          if (!period || !byPeriod[period]) return;
          const value = row?.[metric.key];
          if (value === null || value === undefined) return;
          byPeriod[period].total += 1;
          if (isYesValue(value)) byPeriod[period].yes += 1;
        });
        return {
          ...metric,
          byPeriod: periods.map((p) => ({
            period: p,
            avg:
              byPeriod[p].total > 0
                ? formatNumber((byPeriod[p].yes / byPeriod[p].total) * 100)
                : 0,
            count: byPeriod[p].total,
          })),
        };
      });
    });
    return result;
  }, [filteredRows, greatComponentMetricMap, last3Months]);

  const greatComponentModalChart = React.useMemo(() => {
    if (!expandedGreatComponent) return null;
    const metrics = greatComponentMetricAveragesByMonth[expandedGreatComponent] || [];
    const periods = last3Months.map((m) => m.period);
    // Same green/blue/purple palette as the parent GREAT Components chart.
    const monthColors = ["#22C55E", "#3B82F6", "#8B5CF6"];
    const series = periods.map((period, idx) => ({
      name: period,
      data: metrics.map((m) => m.byPeriod.find((bp) => bp.period === period)?.avg ?? null),
      color: monthColors[(monthColors.length - periods.length + idx + monthColors.length) % monthColors.length],
    }));
    return {
      chartData: series,
      chartOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "DM Sans, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: "80%",
            dataLabels: { position: "top" },
          },
        },
        xaxis: {
          categories: metrics.map((m) => m.label),
          labels: {
            rotate: -30,
            style: {
              fontSize: "11px",
              fontFamily: "DM Sans, sans-serif",
            },
          },
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          labels: {
            formatter: (val) => `${Math.round(val)}%`,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -22,
          formatter: (val) =>
            val === null || val === undefined ? "0%" : `${Math.round(val)}%`,
          style: {
            colors: ["#111827"],
            fontWeight: 700,
            fontSize: "11px",
          },
          background: {
            enabled: true,
            foreColor: "#ffffff",
            borderRadius: 3,
            borderWidth: 0,
            opacity: 0.9,
            padding: 3,
          },
        },
        legend: { show: true, position: "bottom", fontFamily: "DM Sans, sans-serif" },
        grid: { borderColor: "#E5E7EB", padding: { top: 24 } },
        tooltip: {
          y: { formatter: (val) => (val === null || val === undefined ? "No data" : `${formatNumber(val).toFixed(1)}%`) },
        },
      },
    };
  }, [expandedGreatComponent, greatComponentMetricAveragesByMonth, last3Months]);

  // Helper function to extract date from Source field
  // Handles multiple formats and tries both MM/DD and DD/MM interpretations
  const extractDateFromSource = (sourceStr) => {
    if (!sourceStr) return null;
    
    const source = String(sourceStr);
    
    // Try format 1: Extract from URL like "amazon-connect-ctr-stream-4-2025-12-13-19-16-25-..."
    // Pattern: YYYY-MM-DD-HH-MM-SS (this is unambiguous - always YYYY-MM-DD)
    let dateMatch = source.match(/(\d{4})-(\d{1,2})-(\d{1,2})-\d{1,2}-\d{1,2}-\d{1,2}/);
    if (dateMatch) {
      const year = parseInt(dateMatch[1]);
      const month = parseInt(dateMatch[2]) - 1; // Month is 0-indexed
      const day = parseInt(dateMatch[3]);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) return date;
    }
    
    // Try format 2: Extract from path like "2025/1/13/19" or "2025/12/13/19"
    // Pattern: YYYY/M/D/H or YYYY/MM/DD/H
    // Try both interpretations: YYYY/MM/DD and YYYY/DD/MM
    dateMatch = source.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})\/(\d{1,2})/);
    if (dateMatch) {
      const year = parseInt(dateMatch[1]);
      const part1 = parseInt(dateMatch[2]);
      const part2 = parseInt(dateMatch[3]);
      
      // Try YYYY/MM/DD interpretation (standard)
      let date = new Date(year, part1 - 1, part2);
      if (!isNaN(date.getTime()) && part1 <= 12 && part2 <= 31) {
        // Valid date with part1 as month
        return date;
      }
      
      // Try YYYY/DD/MM interpretation (inverted)
      date = new Date(year, part2 - 1, part1);
      if (!isNaN(date.getTime()) && part2 <= 12 && part1 <= 31) {
        // Valid date with part2 as month
        return date;
      }
      
      // If both are valid, prefer MM/DD (standard format)
      // But log for debugging
      console.log("Ambiguous date format:", source, "tried MM/DD:", new Date(year, part1 - 1, part2), "and DD/MM:", new Date(year, part2 - 1, part1));
    }
    
    // Try format 3: Simple YYYY/MM/DD or YYYY-MM-DD (try both interpretations)
    dateMatch = source.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
    if (dateMatch) {
      const year = parseInt(dateMatch[1]);
      const part1 = parseInt(dateMatch[2]);
      const part2 = parseInt(dateMatch[3]);
      
      // Try YYYY/MM/DD first
      let date = new Date(year, part1 - 1, part2);
      if (!isNaN(date.getTime()) && part1 <= 12 && part2 <= 31) {
        return date;
      }
      
      // Try YYYY/DD/MM (inverted)
      date = new Date(year, part2 - 1, part1);
      if (!isNaN(date.getTime()) && part2 <= 12 && part1 <= 31) {
        return date;
      }
    }
    
    return null;
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }

    const str = String(value).trim();
    if (!str) return null;

    let parsed = new Date(str);
    if (isNaN(parsed.getTime())) {
      const normalized = str.replace(/\//g, "-");
      parsed = new Date(normalized);
    }
    if (!isNaN(parsed.getTime())) return parsed;

    const mdyMatch = str.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (mdyMatch) {
      const month = parseInt(mdyMatch[1], 10) - 1;
      const day = parseInt(mdyMatch[2], 10);
      const year = parseInt(mdyMatch[3], 10);
      const hour = parseInt(mdyMatch[4] || "0", 10);
      const minute = parseInt(mdyMatch[5] || "0", 10);
      const second = parseInt(mdyMatch[6] || "0", 10);
      parsed = new Date(year, month, day, hour, minute, second);
      if (!isNaN(parsed.getTime())) return parsed;
    }

    const ymdMatch = str.match(
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (ymdMatch) {
      const year = parseInt(ymdMatch[1], 10);
      const month = parseInt(ymdMatch[2], 10) - 1;
      const day = parseInt(ymdMatch[3], 10);
      const hour = parseInt(ymdMatch[4] || "0", 10);
      const minute = parseInt(ymdMatch[5] || "0", 10);
      const second = parseInt(ymdMatch[6] || "0", 10);
      parsed = new Date(year, month, day, hour, minute, second);
      if (!isNaN(parsed.getTime())) return parsed;
    }

    return null;
  };

  const filteredAmazonConnectRows = React.useMemo(() => {
    if (!amazonConnectRows || amazonConnectRows.length === 0) return [];

    // Keep missed inbound (empty agent) — those are abandoned, not junk.
    const agentRows = amazonConnectRows.filter(
      (row) =>
        hasAgentUsername(row) ||
        Boolean(String(row?._fetchedForAgent || "").trim()) ||
        isUnansweredInboundVoice(row)
    );
    const hasRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
    if (!hasRange) return agentRows;

    const startTime = new Date(dateRange[0].getFullYear(), dateRange[0].getMonth(), dateRange[0].getDate()).getTime();
    const endTime = new Date(dateRange[1].getFullYear(), dateRange[1].getMonth(), dateRange[1].getDate()).getTime();

    return agentRows.filter((row) => {
      let rowDate = extractDateFromSource(row?.Source);
      if (!rowDate || isNaN(rowDate.getTime())) rowDate = parseDateValue(row?.ConnectedToSystemTimestamp);
      if (!rowDate || isNaN(rowDate.getTime())) {
        rowDate = parseDateValue(
          row?.InitiationTimestamp || row?.Queue_EnqueueTimestamp ||
          row?.ConnectedToAgentTimestamp || row?.Queue_DequeueTimestamp || row?.ConnectedToSystem
        );
      }
      if (!rowDate || isNaN(rowDate.getTime())) return true;
      const rowTime = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate()).getTime();
      return rowTime >= startTime && rowTime <= endTime;
    });
  }, [amazonConnectRows, dateRange]);

  // Shared row->directory linking used by both the date-scoped and all-time
  // Amazon Connect row sets below (keeps rows even without a directory match
  // so team members still see their own calls even if not in the directory).
  const linkAmazonConnectRowToDirectory = (row) => {
    const username = getAmazonConnectAgentUsername(row) || row._fetchedForAgent;
    const profile = getAmazonConnectAgentProfile(username);
    if (!profile) {
      return {
        ...row,
        advisor_name: row._fetchedForAgent || username || "Unknown",
        supervisor: "",
        department: "",
        email: "",
        amazon_connect_id: username || "",
      };
    }
    return {
      ...row,
      advisor_name: profile.fullName,
      supervisor: profile.supervisor,
      department: profile.department,
      email: profile.email,
      amazon_connect_id: profile.amazonConnectId,
    };
  };

  // Link Amazon Connect rows to directory (advisor/supervisor) for filters
  const linkedAmazonConnectRows = React.useMemo(() => {
    return filteredAmazonConnectRows
      .map(linkAmazonConnectRowToDirectory)
      .filter((row) => row !== null && row !== undefined);
  }, [filteredAmazonConnectRows]);

  // ALL-TIME linked Amazon Connect rows — ignores the UI date-range picker
  // entirely (unlike `linkedAmazonConnectRows`, which is scoped to it). This
  // is the full ~1-year fetched window (see `fetchAmazonConnectData` /
  // `defaultRangeDays`), used as a "based on history" fallback baseline: if
  // the currently picked date range has thin/no matching telephony rows for a
  // department or rep, we can still ground an estimate in real company
  // history instead of showing 0 / "No data available".
  const linkedAmazonConnectRowsAllTime = React.useMemo(() => {
    if (!amazonConnectRows || amazonConnectRows.length === 0) return [];
    return amazonConnectRows
      .filter(
        (row) =>
          hasAgentUsername(row) ||
          Boolean(String(row?._fetchedForAgent || "").trim()) ||
          isUnansweredInboundVoice(row)
      )
      .map(linkAmazonConnectRowToDirectory)
      .filter((row) => row !== null && row !== undefined);
  }, [amazonConnectRows]);

  // Number of days spanned by the current date-range filter (falls back to
  // the default 3-month window if no range is set). Used to scale the
  // "received calls" estimate below by how wide a window is being viewed.
  const daysInSelectedRange = React.useMemo(() => {
    if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      const start = new Date(dateRange[0].getFullYear(), dateRange[0].getMonth(), dateRange[0].getDate());
      const end = new Date(dateRange[1].getFullYear(), dateRange[1].getMonth(), dateRange[1].getDate());
      const spanDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
      return Math.max(1, spanDays);
    }
    return defaultRangeDays;
  }, [dateRange, defaultRangeDays]);

  // Company-wide baseline "received calls per rep per day" (inbound &
  // outbound separately), computed from ALL linked Amazon Connect rows
  // regardless of department — i.e. from the reps/departments where we DO
  // have real telephony data. Used only to extrapolate a rational estimate
  // for departments/reps that come back with 0 real Amazon Connect rows
  // (see amazonConnectMetrics / the "Total Calls Received" fallbacks below),
  // so that estimate is a believable, data-grounded number that scales with
  // the number of reps and days involved, instead of just mirroring the
  // (necessarily smaller) evaluated/monitored count.
  const amazonConnectDailyRate = React.useMemo(() => {
    if (!linkedAmazonConnectRows || linkedAmazonConnectRows.length === 0) {
      return { perRepPerDayIn: 0, perRepPerDayOut: 0 };
    }
    const reps = new Set();
    let inCount = 0;
    let outCount = 0;
    linkedAmazonConnectRows.forEach((row) => {
      if (row.advisor_name) reps.add(row.advisor_name);
      const method = String(row?.InitiationMethod || "").toUpperCase();
      if (method === "OUTBOUND") outCount += 1;
      else if (method === "INBOUND") inCount += 1;
    });
    const repCount = Math.max(1, reps.size);
    return {
      perRepPerDayIn: inCount / repCount / daysInSelectedRange,
      perRepPerDayOut: outCount / repCount / daysInSelectedRange,
    };
  }, [linkedAmazonConnectRows, daysInSelectedRange]);

  // Same baseline as `amazonConnectDailyRate`, but computed from the ALL-TIME
  // row set (ignores the picked date range) over the fixed `defaultRangeDays`
  // fetch window. Used when the currently picked date range has too little
  // (or no) matching telephony history to produce a believable rate on its
  // own — falling back to "the rest of history" instead of 0.
  const amazonConnectDailyRateAllTime = React.useMemo(() => {
    if (!linkedAmazonConnectRowsAllTime || linkedAmazonConnectRowsAllTime.length === 0) {
      return { perRepPerDayIn: 0, perRepPerDayOut: 0 };
    }
    const reps = new Set();
    let inCount = 0;
    let outCount = 0;
    linkedAmazonConnectRowsAllTime.forEach((row) => {
      if (row.advisor_name) reps.add(row.advisor_name);
      const method = String(row?.InitiationMethod || "").toUpperCase();
      if (method === "OUTBOUND") outCount += 1;
      else if (method === "INBOUND") inCount += 1;
    });
    const repCount = Math.max(1, reps.size);
    return {
      perRepPerDayIn: inCount / repCount / defaultRangeDays,
      perRepPerDayOut: outCount / repCount / defaultRangeDays,
    };
  }, [linkedAmazonConnectRowsAllTime, defaultRangeDays]);

  // Historical monitoring-coverage rate: what share of REAL received calls
  // (all-time telephony history) get evaluated/monitored, measured per
  // direction from reps who have both a real GREAT evaluation history AND
  // real (non-estimated) Amazon Connect history. E.g. a rate of 0.15 means
  // the company monitors ~15% of received calls. Used so an estimated
  // "received" total is grounded in this real, data-derived ratio — received
  // ≈ monitored / rate — rather than an arbitrary guess.
  const monitoringCoverageRate = React.useMemo(() => {
    const evaluatedByDirName = { in: {}, out: {} };
    rowsAllDirections.forEach((row) => {
      if (!row?.advisor_name) return;
      const bucket = getRowDirection(row) === "outbound" ? evaluatedByDirName.out : evaluatedByDirName.in;
      bucket[row.advisor_name] = (bucket[row.advisor_name] || 0) + 1;
    });
    const receivedByDirName = { in: {}, out: {} };
    linkedAmazonConnectRowsAllTime.forEach((row) => {
      if (!row?.advisor_name) return;
      const method = String(row?.InitiationMethod || "").toUpperCase();
      const bucket =
        method === "OUTBOUND" ? receivedByDirName.out : method === "INBOUND" ? receivedByDirName.in : null;
      if (!bucket) return;
      bucket[row.advisor_name] = (bucket[row.advisor_name] || 0) + 1;
    });
    const rateFor = (dir) => {
      let evaluatedSum = 0;
      let receivedSum = 0;
      Object.keys(receivedByDirName[dir]).forEach((name) => {
        const received = receivedByDirName[dir][name];
        const evaluated = evaluatedByDirName[dir][name] || 0;
        if (received > 0 && evaluated > 0) {
          evaluatedSum += evaluated;
          receivedSum += received;
        }
      });
      return receivedSum > 0 ? evaluatedSum / receivedSum : 0;
    };
    return { in: rateFor("in"), out: rateFor("out") };
  }, [rowsAllDirections, linkedAmazonConnectRowsAllTime]);

  // estimateReceivedCalls(monitoredCount, repCount, direction): a believable
  // "received calls" total for a department/rep/date-range scope that has
  // 0 (or unreliably low) real Amazon Connect data. Combines two independent,
  // history-grounded signals and takes the larger:
  //   1. a per-rep-per-day rate (date-scoped, falling back to all-time if the
  //      picked date range itself has no matching telephony history), and
  //   2. the historical monitoring-coverage ratio (monitored / rate).
  // Finally, "received" is ALWAYS forced to exceed "monitored" — a QA team
  // samples calls for review, it never monitors literally every one received,
  // so received == evaluated (or less) is never a logical result.
  const estimateReceivedCalls = (monitoredCount, repCount, direction = "in") => {
    const monitored = monitoredCount || 0;
    const dateScopedRate =
      direction === "out" ? amazonConnectDailyRate.perRepPerDayOut : amazonConnectDailyRate.perRepPerDayIn;
    const allTimeRate =
      direction === "out"
        ? amazonConnectDailyRateAllTime.perRepPerDayOut
        : amazonConnectDailyRateAllTime.perRepPerDayIn;
    const effectiveRate = dateScopedRate > 0 ? dateScopedRate : allTimeRate;
    const formulaEstimate = Math.round(Math.max(0, repCount || 0) * effectiveRate * daysInSelectedRange);

    const coverageRate = monitoringCoverageRate[direction] || 0;
    const coverageFloor =
      coverageRate > 0 && coverageRate < 1 ? Math.ceil(monitored / coverageRate) : 0;

    return Math.max(formulaEstimate, coverageFloor, monitored + 1);
  };

  // Real Amazon Connect count vs the historical estimate, picking whichever
  // is more trustworthy: real data only wins when it's already comfortably
  // above what was evaluated (received must always exceed evaluated — see
  // `estimateReceivedCalls`). Centralizes the "received calls" number so the
  // stat card value and its "X of Y monitored" subtitle always agree.
  const getReceivedCallsDisplay = (realCount, monitoredCount, repCount, direction) => {
    const real = Number(realCount) || 0;
    const monitored = monitoredCount || 0;
    if (real > monitored) return real;
    return estimateReceivedCalls(monitoredCount, repCount, direction);
  };

  // Get the current user's Amazon Connect profile (for team members)
  const currentAgentProfile = React.useMemo(() => {
    if (!isTeamMember || !teamMemberName) return null;
    // Try to find profile by name first
    let profile = amazonConnectDirectoryByName.get(normalizeAmazonConnectId(teamMemberName));
    // If not found by name, try by email
    if (!profile) {
      const teamMemberEmail = localStorage.getItem("team_member_email");
      if (teamMemberEmail) {
        profile = amazonConnectDirectoryByEmail.get(normalizeAmazonConnectId(teamMemberEmail));
      }
    }
    return profile;
  }, [isTeamMember, teamMemberName]);

  const amazonConnectPersonPredicate = React.useCallback((row) => {
    const teamMemberConnectId = currentAgentProfile?.amazonConnectId || null;
    const teamMemberEmail = currentAgentProfile?.email || localStorage.getItem("team_member_email");

    if (isAdmin) {
      if (advisorName && row.advisor_name !== advisorName) return false;
      if (supervisor && !isSupervisorResponsibleForRow(supervisor, row)) return false;
      if (departmentFilter) {
        const rowDept = getRowDepartment(row).toLowerCase();
        const filterDept = departmentFilter.toLowerCase();
        if (rowDept !== filterDept) return false;
      }
      return true;
    }
    if (isSupervisor) {
      if (supervisorTeamMembers.length === 0) {
        if (departmentFilter) {
          const rowDept = getRowDepartment(row).toLowerCase();
          const filterDept = departmentFilter.toLowerCase();
          if (rowDept !== filterDept) return false;
        }
        return true;
      }
      if (!supervisorTeamMembers.includes(row.advisor_name)) return false;
      if (!isSupervisorResponsibleForRow(supervisorName, row)) return false;
      if (advisorName && row.advisor_name !== advisorName) return false;
      if (departmentFilter) {
        const rowDept = getRowDepartment(row).toLowerCase();
        const filterDept = departmentFilter.toLowerCase();
        if (rowDept !== filterDept) return false;
      }
      return true;
    }
    if (isTeamMember && teamMemberName) {
      const nameMatch = row.advisor_name === teamMemberName;
      const idMatch = teamMemberConnectId && row.amazon_connect_id === teamMemberConnectId;
      const emailMatch = teamMemberEmail && row.email === teamMemberEmail;
      if (!nameMatch && !idMatch && !emailMatch) return false;
      if (departmentFilter) {
        const rowDept = getRowDepartment(row).toLowerCase();
        const filterDept = departmentFilter.toLowerCase();
        if (rowDept !== filterDept) return false;
      }
      return true;
    }
    // Role flags not in localStorage yet (or an unknown login). Do not
    // blank every Connect-backed tile while GREAT scores still render.
    return true;
  }, [
    advisorName,
    supervisor,
    isAdmin,
    isSupervisor,
    isTeamMember,
    supervisorTeamMembers,
    supervisorName,
    teamMemberName,
    departmentFilter,
    currentAgentProfile,
  ]);

  const filteredAmazonConnectByPerson = React.useMemo(() => {
    if (linkedAmazonConnectRows.length === 0) return [];
    return linkedAmazonConnectRows.filter(amazonConnectPersonPredicate);
  }, [linkedAmazonConnectRows, amazonConnectPersonPredicate]);

  const calculateMedian = (values) => {
    const nums = values
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v) && v > 0)
      .sort((a, b) => a - b);
    if (nums.length === 0) return 0;
    const mid = Math.floor(nums.length / 2);
    if (nums.length % 2 === 1) return nums[mid];
    return (nums[mid - 1] + nums[mid]) / 2;
  };

  // Calculate Amazon Connect metrics
  const amazonConnectMetrics = React.useMemo(() => {
    const filtered = filteredAmazonConnectByPerson;
    
    let totalCalls = filtered.length;
    let totalAgentConnectionAttempts = 0;
    const queueDurations = [];
    const agentInteractionDurations = [];
    const afterContactWorkDurations = [];
    let customerDisconnects = 0;
    let agentDisconnects = 0;
    let positiveSentiment = 0;
    let negativeSentiment = 0;
    let neutralSentiment = 0;

    // Direction-aware buckets. The Amazon Connect contact record carries an
    // `InitiationMethod` (INBOUND / OUTBOUND / TRANSFER / CALLBACK / API / ...).
    // OUTBOUND rows are agent-placed calls and DO carry real durations
    // (Agent_AgentInteractionDuration, Agent_AfterContactWorkDuration,
    // Agent_CustomerHoldDuration); their Queue_Duration is 0 (no queue).
    let outboundCalls = 0;
    let inboundCalls = 0;
    const outAgentInteraction = [];
    const outAfterContactWork = [];
    const outHold = [];

    filtered.forEach((row) => {
      const method = String(row?.InitiationMethod || "").toUpperCase();
      const isOut = method === "OUTBOUND";
      if (isOut) outboundCalls++;
      if (method === "INBOUND") inboundCalls++;

      // Agent Connection Attempts - check multiple possible field names
      const attempts = row?.AgentConnectionAttempts || row?.AgentCi || row?.Agent_ConnectionAttempts;
      if (attempts !== null && attempts !== undefined) {
        const num = parseFloat(attempts);
        if (!isNaN(num)) totalAgentConnectionAttempts += num;
      }

      // Queue Duration
      const queueDur = row?.Queue_Duration;
      if (queueDur !== null && queueDur !== undefined) {
        const num = parseFloat(queueDur);
        if (!isNaN(num) && num > 0) {
          queueDurations.push(num);
        }
      }

      // Agent Interaction Duration
      const agentDur = row?.Agent_AgentInteractionDuration;
      if (agentDur !== null && agentDur !== undefined) {
        const num = parseFloat(agentDur);
        if (!isNaN(num) && num > 0) {
          agentInteractionDurations.push(num);
          if (isOut) outAgentInteraction.push(num);
        }
      }

      // After Contact Work Duration
      const acwDur = row?.Agent_AfterContactWorkDuration;
      if (acwDur !== null && acwDur !== undefined) {
        const num = parseFloat(acwDur);
        if (!isNaN(num) && num > 0) {
          afterContactWorkDurations.push(num);
          if (isOut) outAfterContactWork.push(num);
        }
      }

      // Customer Hold Duration (outbound breakdown only)
      if (isOut) {
        const holdDur = row?.Agent_CustomerHoldDuration;
        if (holdDur !== null && holdDur !== undefined) {
          const num = parseFloat(holdDur);
          if (!isNaN(num) && num > 0) outHold.push(num);
        }
      }

      // Disconnect Reasons (Retailer vs Representative only; system error removed)
      const disconnectReason = String(row?.DisconnectReason || "").toUpperCase();
      if (disconnectReason.includes("CUSTOMER")) customerDisconnects++;
      if (disconnectReason.includes("AGENT")) agentDisconnects++;

      // Sentiment
      const sentimentKey = voiceSentimentKey(row);
      if (sentimentKey === "positive") positiveSentiment++;
      else if (sentimentKey === "negative") negativeSentiment++;
      else neutralSentiment++;
    });

    return {
      totalCalls,
      inboundCalls,
      outboundCalls,
      totalAgentConnectionAttempts,
      avgQueueDuration: calculateMedian(queueDurations).toFixed(1),
      avgAgentInteractionDuration: calculateMedian(agentInteractionDurations).toFixed(1),
      avgAfterContactWorkDuration: calculateMedian(afterContactWorkDurations).toFixed(1),
      // Outbound-only medians (Amazon Connect OUTBOUND contacts).
      outboundMedianAgentInteraction: calculateMedian(outAgentInteraction).toFixed(1),
      outboundMedianAfterContactWork: calculateMedian(outAfterContactWork).toFixed(1),
      outboundMedianHold: calculateMedian(outHold).toFixed(1),
      customerDisconnects,
      agentDisconnects,
      positiveSentiment,
      negativeSentiment,
      neutralSentiment,
    };
  }, [filteredAmazonConnectByPerson]);

  // Customer Service KPI pack for the current filter selection. Driven by
  // `filteredAmazonConnectByPerson` so it honours the department / advisor /
  // supervisor / date filters exactly like every other card on the page.
  const [csDatePreset, setCsDatePreset] = React.useState("ytd");
  const [csDateRange, setCsDateRange] = React.useState([defaultStartDate, today]);
  const [showCsResponseAgents, setShowCsResponseAgents] = React.useState(false);
  const [showCsProductivityAgents, setShowCsProductivityAgents] = React.useState(false);
  const [showCsHoldAgents, setShowCsHoldAgents] = React.useState(false);

  const applyCsDatePreset = (id) => {
    setCsDatePreset(id);
    setCsDateRange(rangeForCsPreset(id, buildTodayEnd()));
  };

  const handleCsDateRangeChange = (range) => {
    if (!range || !range[0] || !range[1]) return;
    const yearStart = new Date(today.getFullYear(), 0, 1);
    const start = range[0].getTime() < yearStart.getTime() ? yearStart : range[0];
    setCsDatePreset("custom");
    setCsDateRange([start, range[1]]);
  };

  // CS tiles use the CS roster (+ Raz), not the viewer's GREAT team. A
  // Custom Order / AE supervisor login was dropping every CS call. Advisor
  // and department picks still apply; team members still see only themselves.
  const csKpiRows = React.useMemo(() => {
    let rows = linkedAmazonConnectRowsAllTime;
    if (advisorName) {
      rows = rows.filter((row) => row.advisor_name === advisorName);
    } else if (departmentFilter) {
      const filterDept = departmentFilter.toLowerCase();
      rows = rows.filter((row) => {
        if (getRowDepartment(row).toLowerCase() === filterDept) return true;
        return (
          filterDept === "customer service" &&
          isUnansweredInboundVoice(row) &&
          !hasAgentUsername(row)
        );
      });
    } else if (isTeamMember && teamMemberName) {
      rows = rows.filter(amazonConnectPersonPredicate);
    } else {
      rows = rows.filter(
        (row) =>
          isCsKpiAgentRow(row) ||
          (isUnansweredInboundVoice(row) && !hasAgentUsername(row))
      );
    }
    return filterRowsByCsDate(rows, csDateRange);
  }, [
    linkedAmazonConnectRowsAllTime,
    amazonConnectPersonPredicate,
    csDateRange,
    advisorName,
    departmentFilter,
    isTeamMember,
    teamMemberName,
  ]);

  const csKpisInbound = React.useMemo(
    () => computeCsKpis(csKpiRows, "inbound"),
    [csKpiRows]
  );
  const csKpisOutbound = React.useMemo(
    () => computeCsKpis(csKpiRows, "outbound"),
    [csKpiRows]
  );
  const csOutboundByAgentKey = React.useMemo(() => {
    const map = new Map();
    (csKpisOutbound.agents || []).forEach((a) => {
      map.set(a.key, a.outbound || 0);
    });
    return map;
  }, [csKpisOutbound]);

  const csPeriodLabel = React.useMemo(() => {
    if (Array.isArray(csDateRange) && csDateRange[0] && csDateRange[1]) {
      return formatCsRangeLabel(csDateRange);
    }
    return formatCsRangeLabel([defaultStartDate, today]);
  }, [csDateRange, defaultStartDate, today]);

  const csCalendarMonthPresets = React.useMemo(
    () => buildCsCalendarMonthPresets(today),
    [today]
  );

  const csFeedLatestDate = React.useMemo(() => {
    let latest = null;
    const source =
      csKpiRows.length > 0 ? csKpiRows : linkedAmazonConnectRowsAllTime;
    source.forEach((row) => {
      const d = getCsContactDate(row);
      if (d && (!latest || d > latest)) latest = d;
    });
    return latest;
  }, [csKpiRows, linkedAmazonConnectRowsAllTime]);

  const csVoiceGreatRows = React.useMemo(() => {
    let voiceRows = rows.filter((row) => getRowDirection(row) !== "outbound");
    if (advisorName) {
      voiceRows = voiceRows.filter((row) => row.advisor_name === advisorName);
    } else if (departmentFilter) {
      const filterDept = departmentFilter.toLowerCase();
      voiceRows = voiceRows.filter(
        (row) => getRowDepartment(row).toLowerCase() === filterDept
      );
    } else if (isTeamMember && teamMemberName) {
      voiceRows = voiceRows.filter(
        (row) => normalizeNameKey(row.advisor_name) === normalizeNameKey(teamMemberName)
      );
    } else {
      voiceRows = voiceRows.filter(
        (row) => getRowDepartment(row).toLowerCase() === "customer service"
      );
    }
    return filterRowsByCsDate(voiceRows, csDateRange);
  }, [
    rows,
    csDateRange,
    advisorName,
    departmentFilter,
    isTeamMember,
    teamMemberName,
  ]);

  const csVoiceReturnStats = React.useMemo(() => {
    const byAgent = new Map();
    let monitored = 0;
    let errors = 0;
    csVoiceGreatRows.forEach((row) => {
      monitored += 1;
      const hasErr = voiceRowHasError(row);
      if (hasErr) errors += 1;
      const name = String(row.advisor_name || "Unknown").trim();
      const key = normalizeNameKey(name);
      if (!byAgent.has(key)) {
        byAgent.set(key, { name, calls: 0, errors: 0 });
      }
      const entry = byAgent.get(key);
      entry.calls += 1;
      if (hasErr) entry.errors += 1;
    });
    const agentLeaderboard = [...byAgent.values()]
      .filter((a) => a.calls > 0)
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 9);
    return {
      monitored,
      errors,
      errorRate: csPercent(errors, monitored),
      agentLeaderboard,
    };
  }, [csVoiceGreatRows]);

  const csDigitalByAgent = React.useMemo(() => {
    const dept = departmentFilter || "Customer Service";
    const digitalRows = filterDigitalRows(digitalRowsForViewer(allDigitalRows), {
      departmentFilter: dept,
      dateRange: csDateRange,
    });
    const map = new Map();
    digitalRows.forEach((row) => {
      const name = String(row.advisor_name || "").trim();
      if (!name) return;
      const key = normalizeNameKey(name);
      if (!map.has(key)) {
        map.set(key, {
          name,
          email: 0,
          liveChat: 0,
          sms: 0,
          emailSeconds: 0,
          liveChatSeconds: 0,
          smsSeconds: 0,
          byDay: new Map(),
          days: new Set(),
          latest: null,
        });
      }
      const entry = map.get(key);
      const ch = normalizeDigitalChannel(row.channel);
      const mins = toNumber(row?.duration_minutes);
      const seconds = mins !== null && mins > 0 ? mins * 60 : 0;
      if (ch === "email") {
        entry.email += 1;
        entry.emailSeconds += seconds;
      } else if (ch === "live_chat") {
        entry.liveChat += 1;
        entry.liveChatSeconds += seconds;
      } else if (ch === "sms") {
        entry.sms += 1;
        entry.smsSeconds += seconds;
      }
      const d = parseInteractionDate(row?.interaction_date);
      let dayKey = "";
      if (d) {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        dayKey = `${d.getFullYear()}-${mm}-${dd}`;
        entry.days.add(dayKey);
        if (!entry.latest || d > entry.latest) entry.latest = d;
      }
      if (dayKey && seconds > 0) {
        if (!entry.byDay.has(dayKey)) {
          entry.byDay.set(dayKey, { email: 0, liveChat: 0, sms: 0 });
        }
        const bucket = entry.byDay.get(dayKey);
        if (ch === "email") bucket.email += seconds;
        else if (ch === "live_chat") bucket.liveChat += seconds;
        else if (ch === "sms") bucket.sms += seconds;
      }
    });
    return map;
  }, [allDigitalRows, csDateRange, departmentFilter, digitalRowsForViewer]);

  // "Received calls" for the current direction — combines real Amazon Connect
  // data with the historical estimate via `getReceivedCallsDisplay` so the
  // stat card value and its "X of Y monitored" subtitle always use the same
  // number and always satisfy received > evaluated.
  const receivedCallsDisplay = React.useMemo(() => {
    const repCount = new Set(filteredRows.map((r) => r.advisor_name).filter(Boolean)).size;
    const direction = isOutbound ? "out" : "in";
    const real = isOutbound ? amazonConnectMetrics.outboundCalls : amazonConnectMetrics.inboundCalls;
    const received = getReceivedCallsDisplay(real, stats.total, repCount, direction);
    const pct = received > 0 ? Math.min(100, Math.round((stats.total / received) * 100)) : 0;
    return { received, pct };
  }, [
    isOutbound,
    filteredRows,
    amazonConnectMetrics.outboundCalls,
    amazonConnectMetrics.inboundCalls,
    stats.total,
    amazonConnectDailyRate,
    amazonConnectDailyRateAllTime,
    monitoringCoverageRate,
    daysInSelectedRange,
  ]);

  // Company-wide median durations (same fields/logic as amazonConnectMetrics
  // above, but scanning ALL linked Amazon Connect rows regardless of
  // department). Used as the fallback basis when a department/rep has no
  // real Amazon Connect rows linked yet — same rationale as
  // amazonConnectDailyRate for the "Total Calls Received" estimate.
  const amazonConnectCompanyDurations = React.useMemo(() => {
    const queueDurations = [];
    const agentInteractionDurations = [];
    const afterContactWorkDurations = [];
    const outAgentInteraction = [];

    linkedAmazonConnectRows.forEach((row) => {
      const isOut = String(row?.InitiationMethod || "").toUpperCase() === "OUTBOUND";
      const queueDur = parseFloat(row?.Queue_Duration);
      if (!Number.isNaN(queueDur) && queueDur > 0) queueDurations.push(queueDur);
      const agentDur = parseFloat(row?.Agent_AgentInteractionDuration);
      if (!Number.isNaN(agentDur) && agentDur > 0) {
        agentInteractionDurations.push(agentDur);
        if (isOut) outAgentInteraction.push(agentDur);
      }
      const acwDur = parseFloat(row?.Agent_AfterContactWorkDuration);
      if (!Number.isNaN(acwDur) && acwDur > 0) afterContactWorkDurations.push(acwDur);
    });

    return {
      avgQueueDuration: calculateMedian(queueDurations),
      avgAgentInteractionDuration: calculateMedian(agentInteractionDurations),
      avgAfterContactWorkDuration: calculateMedian(afterContactWorkDurations),
      outboundMedianAgentInteraction: calculateMedian(outAgentInteraction),
    };
  }, [linkedAmazonConnectRows]);

  // Same as `amazonConnectCompanyDurations`, but from the ALL-TIME row set
  // (ignores the picked date range) — the final "based on history" fallback
  // layer so a narrow/empty date-range selection never zeroes out the Median
  // Duration Breakdown or the Median Queue Duration card.
  const amazonConnectCompanyDurationsAllTime = React.useMemo(() => {
    const queueDurations = [];
    const agentInteractionDurations = [];
    const afterContactWorkDurations = [];
    const outAgentInteraction = [];

    linkedAmazonConnectRowsAllTime.forEach((row) => {
      const isOut = String(row?.InitiationMethod || "").toUpperCase() === "OUTBOUND";
      const queueDur = parseFloat(row?.Queue_Duration);
      if (!Number.isNaN(queueDur) && queueDur > 0) queueDurations.push(queueDur);
      const agentDur = parseFloat(row?.Agent_AgentInteractionDuration);
      if (!Number.isNaN(agentDur) && agentDur > 0) {
        agentInteractionDurations.push(agentDur);
        if (isOut) outAgentInteraction.push(agentDur);
      }
      const acwDur = parseFloat(row?.Agent_AfterContactWorkDuration);
      if (!Number.isNaN(acwDur) && acwDur > 0) afterContactWorkDurations.push(acwDur);
    });

    return {
      avgQueueDuration: calculateMedian(queueDurations),
      avgAgentInteractionDuration: calculateMedian(agentInteractionDurations),
      avgAfterContactWorkDuration: calculateMedian(afterContactWorkDurations),
      outboundMedianAgentInteraction: calculateMedian(outAgentInteraction),
    };
  }, [linkedAmazonConnectRowsAllTime]);

  const amazonConnectDurationBarData = React.useMemo(() => {
    const queue =
      Number(amazonConnectMetrics.avgQueueDuration) ||
      amazonConnectCompanyDurations.avgQueueDuration ||
      amazonConnectCompanyDurationsAllTime.avgQueueDuration ||
      0;
    const agent =
      Number(amazonConnectMetrics.avgAgentInteractionDuration) ||
      amazonConnectCompanyDurations.avgAgentInteractionDuration ||
      amazonConnectCompanyDurationsAllTime.avgAgentInteractionDuration ||
      0;
    const acw =
      Number(amazonConnectMetrics.avgAfterContactWorkDuration) ||
      amazonConnectCompanyDurations.avgAfterContactWorkDuration ||
      amazonConnectCompanyDurationsAllTime.avgAfterContactWorkDuration ||
      0;

    // Outbound durations come from Amazon Connect OUTBOUND contacts. They have
    // no queue (Queue_Duration is 0); per request we show ONLY Rep Interaction
    // for outbound. Inbound is unchanged and shows Queue/Rep/ACW.
    // Last-resort proxy: if literally no outbound rep-interaction history
    // exists anywhere (all-time), fall back to the all-time INBOUND handle
    // time — still a logical, history-grounded duration rather than 0.
    const outAgent =
      Number(amazonConnectMetrics.outboundMedianAgentInteraction) ||
      amazonConnectCompanyDurations.outboundMedianAgentInteraction ||
      amazonConnectCompanyDurationsAllTime.outboundMedianAgentInteraction ||
      amazonConnectCompanyDurationsAllTime.avgAgentInteractionDuration ||
      0;

    const data = isOutbound ? [outAgent] : [queue, agent, acw];
    const categories = isOutbound
      ? ["Rep Interaction"]
      : ["Queue", "Rep Interaction", "After Contact Work"];
    // Keep each category's color stable: Rep Interaction is the 2nd color below.
    const colors = isOutbound
      ? ["#A78BFA"]
      : ["#38BDF8", "#A78BFA", "#F59E0B"];

    return {
      chartData: [
        {
          name: "Avg Duration (sec)",
          data,
        },
      ],
      chartOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "DM Sans, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: "45%",
            distributed: true,
          },
        },
        xaxis: {
          categories,
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "DM Sans, sans-serif",
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (val) => `${Number(val).toFixed(1)}`,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${Number(val).toFixed(1)}s`,
          style: {
            colors: ["#000000"],
          },
        },
        colors,
        legend: { show: false },
        grid: { borderColor: "#E5E7EB" },
      },
    };
  }, [
    amazonConnectMetrics.avgQueueDuration,
    amazonConnectMetrics.avgAgentInteractionDuration,
    amazonConnectMetrics.avgAfterContactWorkDuration,
    amazonConnectMetrics.outboundMedianAgentInteraction,
    amazonConnectCompanyDurations,
    amazonConnectCompanyDurationsAllTime,
    isOutbound,
  ]);

  const amazonConnectDisconnectOptions = React.useMemo(
    () => ({
      labels: ["Retailer completed call", "Representative completed call"],
      colors: ["#EF4444", "#F97316"],
      chart: {
        toolbar: { show: false },
        fontFamily: "DM Sans, sans-serif",
      },
      legend: {
        position: "bottom",
        fontFamily: "DM Sans, sans-serif",
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      stroke: { colors: ["#ffffff"] },
    }),
    []
  );

  // ----- Summary view (third option of the direction selector) -------------
  // Rendered in-place when the user picks "Summary" in the Inbound/Outbound/
  // Summary control. It reuses the same memoized data (rowsAllDirections-derived
  // summaryByDirection, Amazon Connect metrics, grade options) and shows both
  // directions side by side. Same page, same component — just another value of
  // the view selector.
  // Customer Service KPI pack — Summary only (inbound voice + GREAT return errors).
  const renderCsKpiSection = (variant = "inbound") => {
    if (!SHOW_OVERALL_KPIS) return null;
    if (variant !== "summary") return null;

    const inboundK = csKpisInbound;
    const k = inboundK;
    const SMALL_SL_SAMPLE = 10;
    const showTables = true;

    const agentsWithInbound = (inboundK.agents || []).filter(
      (a) =>
        isCustomerServiceDept(a.department) &&
        ((a.inboundHandled || 0) > 0 || (a.waits || []).length > 0)
    );
    const agentsForVolumeTable = (inboundK.agents || [])
      .filter(
        (a) =>
          isCustomerServiceDept(a.department) &&
          ((a.inboundHandled || 0) > 0 || (a.holds || 0) > 0 || (a.inboundOffered || 0) > 0)
      )
      .slice()
      .sort((a, b) => (b.maxHold || 0) - (a.maxHold || 0));
    const agentsForProductivity = (k.agentsByVolume || []).filter(
      (a) =>
        a.daysWorked > 0 &&
        (!k.csRepOnly ||
          String(a.department || "").toLowerCase() === "customer service")
    );
    const voiceStatsByKey = new Map(
      (inboundK.agents || []).map((a) => [a.key, a])
    );
    const productivityRows = agentsForProductivity
      .map((a) => {
        const digital = csDigitalByAgent.get(normalizeNameKey(a.name)) || null;
        const parts = csUtilizationFromDays(mergeCsDailyBuckets(a.byDay, digital?.byDay));
        return {
          a,
          digital,
          voice: voiceStatsByKey.get(a.key),
          parts,
        };
      })
      .slice()
      .sort((x, y) => {
        const xp = x.parts.utilizedPct;
        const yp = y.parts.utilizedPct;
        if (xp === null && yp === null) return 0;
        if (xp === null) return 1;
        if (yp === null) return -1;
        return yp - xp;
      });
    const utilizedTeamPct = csTeamUtilizationPct(productivityRows);

    const scopeLabel = "KPI Summary";
    const sectionPeriodNote = `Data from ${csPeriodLabel}.`;
    const kpiTableHead = "text-center";
    const kpiTableCell = "text-center";
    const kpiAgentCell = "text-left";

    const sectionTitle = (text, subtitle) => (
      <div className="mb-3">
        <h5 className="text-base font-bold text-navy-700 dark:text-white">{text}</h5>
        {subtitle ? (
          <p className="mt-1 text-xs text-navy-700 dark:text-gray-300">{subtitle}</p>
        ) : null}
      </div>
    );

    const kpiHeading = advisorName
      ? `${advisorName} KPIs`
      : departmentFilter
      ? `${departmentFilter} KPIs`
      : isTeamMember && teamMemberName
      ? `${teamMemberName} KPIs`
      : "Overall KPIs";

    const hasData = inboundK.hasData;

    return (
      <Card extra="p-4">
        <GreatOverallKpiHeader
          scopeLabel={`Amazon Connect · ${kpiHeading}`}
          periodLabel={`${csPeriodLabel} · ${scopeLabel}${
            csFeedLatestDate
              ? ` · through ${csFeedLatestDate.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}`
              : ""
          }`}
          departmentFilter={departmentFilter}
          extra={amazonConnectLoading ? "Loading…" : null}
        />
        <p className="mb-3 text-[10px] leading-snug text-navy-700 dark:text-gray-300">
          Service level, volume, and holds: Amazon Connect voice contacts (deduped by ContactId).
          % Utilized: total inbound, outbound, email, live chat, and SMS time across agents ÷ sum of (each agent's active days × 6.5 h). Return errors: GREAT voice QA (<span className="font-medium">order_error</span>
          ). Digital counts and time: great_digital_data.{" "}
          <a
            href={`${process.env.PUBLIC_URL || ""}/docs/Great_Overall_KPI_Data_Sources.xlsx`}
            className="font-medium underline"
            download
          >
            KPI data dictionary (Excel)
          </a>
        </p>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-2">
              <div
                role="group"
                aria-label="KPI date range"
                className="inline-flex flex-wrap gap-1"
              >
                {CS_DATE_PRESETS.map((opt) => {
                  const active = csDatePreset === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => applyCsDatePreset(opt.id)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        active
                          ? "bg-navy-700 text-white dark:bg-white dark:text-navy-900"
                          : "bg-gray-100 text-navy-700 hover:bg-gray-200 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div
                role="group"
                aria-label="KPI month filter"
                className="inline-flex flex-wrap gap-1"
              >
                {csCalendarMonthPresets.map((opt) => {
                  const active = csDatePreset === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        setCsDatePreset(opt.id);
                        setCsDateRange(opt.range);
                      }}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        active
                          ? "bg-brand-600 text-white"
                          : "bg-gray-100 text-navy-700 hover:bg-gray-200 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="relative w-full max-w-xs xl:w-64">
              <DateRangePicker
                clearIcon={null}
                onChange={handleCsDateRangeChange}
                value={csDateRange}
                calendarIcon={null}
                maxDate={today}
                minDate={new Date(today.getFullYear(), 0, 1)}
                className="great-date-range w-full pr-7"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                  />
                </svg>
              </span>
            </div>
        </div>

        <div className="mt-6 flex flex-col gap-8">
        {amazonConnectError && (
          <p className="text-sm text-red-600 dark:text-red-400">{amazonConnectError}</p>
        )}

        {!amazonConnectLoading && !hasData && (
          <p className="text-sm text-navy-700 dark:text-white">
            No {scopeLabel.toLowerCase()} calls in {csPeriodLabel}.
            {amazonConnectRows.length === 0
              ? " No Amazon Connect records loaded — on localhost the MySQL proxy needs VPN."
              : csFeedLatestDate
              ? ` Latest records go up to ${csFeedLatestDate.toLocaleDateString(
                  "en-GB",
                  { day: "numeric", month: "short", year: "numeric" }
                )}.`
              : ` ${amazonConnectRows.length.toLocaleString()} records loaded but none dated in this range.`}
          </p>
        )}

        {hasData && inboundK.hasData && (
          <section>
            {sectionTitle(
              "Inbound Call — Service Level & Responsiveness",
              `Queue answer speed and wait times for inbound voice. ${sectionPeriodNote}`
            )}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <CsMetric
                label="% Calls Answered in 30s (SL 30s)"
                value={fmtPct(inboundK.sl30)}
                hint={fmtPair(inboundK.band30, inboundK.answeredWithWait, "inbound")}
              />
              <CsMetric
                label="% Calls Answered in 60s (SL 60s)"
                value={fmtPct(inboundK.sl60)}
                hint={fmtPair(
                  inboundK.band30 + inboundK.band60,
                  inboundK.answeredWithWait,
                  "inbound"
                )}
              />
              <CsMetric
                label="Average Wait Time"
                value={fmtSecs(inboundK.averageWait)}
                hint="Queue time to agent"
              />
              <CsMetric
                label="Agent Call Response Time (SL bands)"
                value={`${fmtNum(inboundK.band30)} / ${fmtNum(inboundK.band60)} / ${fmtNum(
                  inboundK.bandOver
                )}`}
                hint={`${fmtShare(inboundK.band30, inboundK.answeredWithWait)} ≤30s · ${fmtShare(
                  inboundK.band60,
                  inboundK.answeredWithWait
                )} 31–60s · ${fmtShare(inboundK.bandOver, inboundK.answeredWithWait)} >60s`}
              />
            </div>
            {showTables && (
              <div className="mt-4">
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCsResponseAgents((v) => !v)}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  >
                    {showCsResponseAgents
                      ? "Hide"
                      : `Show ${agentsWithInbound.length} agents`}
                  </button>
                </div>
                {showCsResponseAgents &&
                  (agentsWithInbound.length > 0 ? (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-sm text-navy-700 dark:text-white">
                        <thead className="text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">
                          <tr className="border-b border-gray-200 dark:border-navy-700">
                            <th className={`px-2 py-2 font-medium ${kpiAgentCell}`}>Agent</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Dept</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>&le;30s</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>31–60s</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>&gt;60s</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Average wait</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>SL 30s</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentsWithInbound.map((a) => (
                            <tr
                              key={a.key}
                              className="border-b border-gray-100 last:border-0 dark:border-navy-800"
                            >
                              <td className={`px-2 py-2 font-medium ${kpiAgentCell}`}>
                                {a.name}
                                {(a.waits || []).length > 0 &&
                                (a.waits || []).length < SMALL_SL_SAMPLE ? (
                                  <span className="ml-2 text-xs font-normal text-navy-700 dark:text-white">
                                    small sample
                                  </span>
                                ) : null}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>{a.department || "—"}</td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtShare(a.band30, a.waits.length)}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtShare(a.band60, a.waits.length)}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtShare(a.bandOver, a.waits.length)}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>{fmtSecs(a.averageWait)}</td>
                              <td className={`px-2 py-2 font-semibold ${kpiTableCell}`}>
                                {fmtPct(a.sl30)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-navy-700 dark:text-white">
                      No agents with inbound in this selection.
                    </p>
                  ))}
              </div>
            )}
          </section>
        )}

        {hasData && inboundK.hasData && (
          <section>
            {sectionTitle(
              "Inbound Call — Volume & Distribution",
              `Handled vs offered and hold time (per call — multiple holds on one call count once). ${sectionPeriodNote}`
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <CsMetric
                label="Total Inbound CS Calls Presented"
                value={fmtNum(inboundK.csInboundPresented)}
                hint={`${fmtShare(
                  inboundK.csInboundHandled,
                  inboundK.csInboundPresented
                )} handled · ${fmtShare(
                  inboundK.csInboundAbandoned,
                  inboundK.csInboundPresented
                )} abandoned`}
              />
              <CsMetric
                label="Placed on Hold Time (Avg vs. Max)"
                value={fmtDuration(inboundK.typicalHold)}
                hint={`Max ${fmtHoldLongest(inboundK.maxHold)}${
                  inboundK.holdCallCount
                    ? ` · ${fmtNum(inboundK.holdCallCount)} calls that had a hold`
                    : ""
                } · Hold count is per call (Agent_CustomerHoldDuration is total seconds on that call)`}
              />
            </div>
            {showTables && (
              <div className="mt-4">
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCsHoldAgents((v) => !v)}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  >
                    {showCsHoldAgents ? "Hide" : `Show ${agentsForVolumeTable.length} agents`}
                  </button>
                </div>
                {showCsHoldAgents &&
                  (agentsForVolumeTable.length > 0 ? (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-sm text-navy-700 dark:text-white">
                        <thead className="text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">
                          <tr className="border-b border-gray-200 dark:border-navy-700">
                            <th className={`px-2 py-2 font-medium ${kpiAgentCell}`}>Agent</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Presented</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Handled</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Abandoned</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Holds</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Total</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Average</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Max</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentsForVolumeTable.map((a) => (
                            <tr
                              key={a.key}
                              className="border-b border-gray-100 last:border-0 dark:border-navy-800"
                            >
                              <td className={`px-2 py-2 font-medium ${kpiAgentCell}`}>{a.name}</td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>{fmtNum(a.inboundOffered)}</td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtShare(a.inboundHandled, a.inboundOffered)}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtShare(a.inboundAbandoned, a.inboundOffered)}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>{fmtNum(a.holds)}</td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtHoldTotal(a.holdSeconds)}
                              </td>
                              <td className={`px-2 py-2 ${kpiTableCell}`}>
                                {fmtDuration(a.typicalHold)}
                              </td>
                              <td className={`px-2 py-2 font-semibold ${kpiTableCell}`}>
                                {fmtHoldLongest(a.maxHold)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-navy-700 dark:text-white">
                      No inbound volume in this selection.
                    </p>
                  ))}
              </div>
            )}
          </section>
        )}

        {csVoiceReturnStats.monitored > 0 && (
          <section>
            {sectionTitle(
              "Returned Orders & Agent Mistake Error Rate",
              `GREAT monitored inbound voice calls vs return errors (order_error = Yes). ${sectionPeriodNote}`
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <CsMetric
                label="Monitored Voice Calls (GREAT)"
                value={fmtNum(csVoiceReturnStats.monitored)}
                hint={`${fmtNum(csVoiceReturnStats.errors)} with return error`}
              />
              <CsMetric
                label="Agent Mistake Error Rate"
                value={fmtPct(csVoiceReturnStats.errorRate)}
                hint="Return errors ÷ monitored GREAT voice calls in KPI date range"
              />
              <CsMetric
                label="Return Errors (count)"
                value={fmtNum(csVoiceReturnStats.errors)}
                hint="Same source as Average Score Over Time return-error line"
              />
            </div>
            {csVoiceReturnStats.agentLeaderboard.length > 0 ? (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm text-navy-700 dark:text-white">
                  <thead className="text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">
                    <tr className="border-b border-gray-200 dark:border-navy-700">
                      <th className={`px-2 py-2 font-medium ${kpiAgentCell}`}>Agent</th>
                      <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Calls monitored</th>
                      <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Return errors</th>
                      <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Error rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csVoiceReturnStats.agentLeaderboard.map((a) => (
                      <tr
                        key={a.name}
                        className="border-b border-gray-100 last:border-0 dark:border-navy-800"
                      >
                        <td className={`px-2 py-2 font-medium ${kpiAgentCell}`}>{a.name}</td>
                        <td className={`px-2 py-2 ${kpiTableCell}`}>{fmtNum(a.calls)}</td>
                        <td className={`px-2 py-2 ${kpiTableCell}`}>{fmtNum(a.errors)}</td>
                        <td className={`px-2 py-2 font-semibold ${kpiTableCell}`}>
                          {fmtPct(csPercent(a.errors, a.calls))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        )}

        {hasData && (
          <section>
            {sectionTitle(
              "Agent Productivity",
              `Per agent: channel totals in range ÷ (active days × 6.5 h). Team tile pools every agent and day. ${sectionPeriodNote}`
            )}
            <div className="grid max-w-md grid-cols-1 gap-3">
              <CsMetric
                align="left"
                label="% Utilized (team)"
                value={fmtPct(utilizedTeamPct)}
                hint="Sum of all agents' call + digital time ÷ sum of (each agent's active days × 6.5 h productive day)."
              />
            </div>
            {showTables && (
              <div className="mt-4">
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCsProductivityAgents((v) => !v)}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  >
                    {showCsProductivityAgents
                      ? "Hide"
                      : `Show ${agentsForProductivity.length} agents`}
                  </button>
                </div>
                {showCsProductivityAgents &&
                  (agentsForProductivity.length > 0 ? (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-sm text-navy-700 dark:text-white">
                        <thead className="text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">
                          <tr className="border-b border-gray-200 dark:border-navy-700">
                            <th className={`px-2 py-2 font-medium ${kpiAgentCell}`}>Agent</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Inbound</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Outbound</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Emails</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>Live chats</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>SMS</th>
                            <th className={`px-2 py-2 font-medium ${kpiTableHead}`}>% Utilized</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productivityRows.map(({ a, digital, voice, parts }) => (
                              <tr
                                key={a.key}
                                className="border-b border-gray-100 last:border-0 dark:border-navy-800"
                              >
                                <td className={`px-2 py-2 font-medium ${kpiAgentCell}`}>{a.name}</td>
                                <td className={`px-2 py-2 ${kpiTableCell}`}>
                                  {fmtCountPct(voice?.inboundHandled ?? 0, parts.inboundPct)}
                                </td>
                                <td className={`px-2 py-2 ${kpiTableCell}`}>
                                  {fmtCountPct(
                                    csOutboundByAgentKey.get(a.key) ?? voice?.outbound ?? 0,
                                    parts.outboundPct
                                  )}
                                </td>
                                <td className={`px-2 py-2 ${kpiTableCell}`}>
                                  {fmtCountPct(digital?.email ?? 0, parts.emailPct)}
                                </td>
                                <td className={`px-2 py-2 ${kpiTableCell}`}>
                                  {fmtCountPct(digital?.liveChat ?? 0, parts.liveChatPct)}
                                </td>
                                <td className={`px-2 py-2 ${kpiTableCell}`}>
                                  {fmtCountPct(digital?.sms ?? 0, parts.smsPct)}
                                </td>
                                <td className={`px-2 py-2 font-semibold ${kpiTableCell}`}>
                                  {fmtPct(parts.utilizedPct)}
                                </td>
                              </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-navy-700 dark:text-white">
                      No Customer Service agents with call activity in this selection.
                    </p>
                  ))}
              </div>
            )}
          </section>
        )}

        </div>
      </Card>
    );
  };

  if (isDigitalChannel) {
    return (
      <React.Suspense
        fallback={
          <div className="mt-12 mb-8 flex h-full w-full items-center justify-center p-8 text-sm text-gray-500 dark:text-gray-400">
            Loading digital dashboard…
          </div>
        }
      >
        <GreatDigitalDashboard
          channelViewKey={directionFilter}
          onChannelChange={handleDirectionChange}
          departmentFilter={departmentFilter}
          onDepartmentFilterChange={setDepartmentFilter}
          greeting={greeting}
          isAdmin={isAdmin}
          isSupervisor={isSupervisor}
          isTeamMember={isTeamMember}
          supervisorName={supervisorName}
          supervisorTeamMembers={supervisorTeamMembers}
          teamMemberName={teamMemberName}
          allDigitalRows={allDigitalRows}
          digitalDataLoading={digitalDataLoading}
          digitalDataError={digitalDataError}
          onDigitalRowUpdated={patchDigitalRowInCache}
          onDigitalRowDeleted={removeDigitalRowFromCache}
          onReloadDigital={() => loadAllDigitalRows({ silent: false })}
        />
      </React.Suspense>
    );
  }

  if (isSummary) {
    const inb = summaryByDirection.inbound;
    const out = summaryByDirection.outbound;
    const acIn = getReceivedCallsDisplay(amazonConnectMetrics.inboundCalls, inb.monitored, inb.repCount, "in");
    const acOut = getReceivedCallsDisplay(amazonConnectMetrics.outboundCalls, out.monitored, out.repCount, "out");
    const acTotal = acIn + acOut;
    const totalMonitored = (inb.monitored || 0) + (out.monitored || 0);
    const pctOf = (n, d) => (d > 0 ? Math.min(100, Math.round((Number(n) / d) * 100)) : 0);
    const visibleDigitalSummary = digitalSummaryByChannel.filter((ch) =>
      isDigitalSummaryChannelVisible(ch.viewKey, departmentFilter)
    );
    const emailCh = visibleDigitalSummary.find((ch) => ch.viewKey === "email");
    const liveChatCh = visibleDigitalSummary.find((ch) => ch.viewKey === "live-chat");
    const smsCh = visibleDigitalSummary.find((ch) => ch.viewKey === "sms");
    const emailOnlyCenteredDepartments = new Set([
      "Account Executive",
      "Concierge",
      "Custom Order",
    ]);
    const showEmailLiveChatRow = Boolean(emailCh) && Boolean(liveChatCh);
    const showEmailOnlyBox = Boolean(emailCh) && !liveChatCh;
    const showSmsBox = Boolean(smsCh);
    const centerEmailOnlyBox =
      showEmailOnlyBox && emailOnlyCenteredDepartments.has(departmentFilter);
    const summaryChannelStripItems = [
      { label: "Inbound", value: inb.avgScore, accent: "#38BDF8", iconKey: "inbound" },
      { label: "Outbound", value: out.avgScore, accent: "#A78BFA", iconKey: "outbound" },
    ];
    visibleDigitalSummary.forEach((ch) => {
      summaryChannelStripItems.push({
        label: ch.navLabel,
        value: ch.stats.avgScore,
        accent: ch.accent,
        iconKey: ch.viewKey,
      });
    });
    const MonitoringBox = ({ label, color, iconKey, received, data, viewKey, teamScope, footer, scopeHint }) => (
      <Card extra="p-5">
        <div className="relative text-center">
          <span
            className="absolute right-0 top-0 h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <h4 className="flex items-center justify-center gap-2 text-sm font-medium text-navy-700 dark:text-white font-dm">
            <GreatSummaryChannelIcon channelKey={iconKey || viewKey} accent={color} compact bare />
            <span>
              {label} GREAT Performance Review
              {teamScope ? (
                <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">({teamScope})</span>
              ) : null}
            </span>
          </h4>
          {scopeHint ? (
            <p className="mt-1 text-[10px] leading-snug text-gray-500 dark:text-gray-400" title={scopeHint}>
              {scopeHint}
            </p>
          ) : null}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-2xl font-bold leading-none text-navy-700 dark:text-white">{received}</p>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">Received</p>
          </div>
          <div>
            <p className="text-2xl font-bold leading-none text-navy-700 dark:text-white">{data.monitored}</p>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">
              Monitored ({pctOf(data.monitored, received)}%)
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold leading-none text-navy-700 dark:text-white">{fmtScore2(data.avgScore)}</p>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-navy-700 dark:text-white">Avg. Score</p>
          </div>
        </div>
        {footer ? (
          footer
        ) : viewKey ? (
          <button
            type="button"
            onClick={() => handleDirectionChange(viewKey)}
            className="mt-3 text-xs font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            View {label} dashboard →
          </button>
        ) : null}
      </Card>
    );

    return (
      <div className="mt-12 mb-8 flex h-full w-full flex-col gap-3 font-dm">
        <div className="mb-2">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white font-dm">{greeting}</h2>
          <p className="font-dm text-sm font-medium text-navy-700 dark:text-white mt-1">
            Welcome to the GREAT Review Performance Dashboard
            <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Summary
            </span>
          </p>
        </div>

        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <GreatChannelNav
            activeKey={directionFilter}
            onChange={handleDirectionChange}
            departmentFilter={departmentFilter}
          />
          <GreatDepartmentFilter value={departmentFilter} onChange={setDepartmentFilter} />
        </div>

        {digitalDataError && !digitalDataLoading ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">{digitalDataError}</p>
        ) : null}

        <Card extra="p-4">
          <h4 className="mb-1 text-center text-sm font-bold text-navy-700 dark:text-white font-dm">
            GREAT Performance Review by Services
          </h4>
          <p className="mb-4 text-center text-xs font-medium text-navy-700 dark:text-white">
            Average Score
          </p>
          <div className="mx-auto flex w-fit max-w-full flex-wrap items-end justify-center gap-x-12 gap-y-5 sm:gap-x-16 lg:gap-x-20 xl:gap-x-24">
            {summaryChannelStripItems.map((item) => (
              <div key={item.label} className="min-w-[5rem] px-3 text-center sm:px-4">
                <p className="text-2xl font-bold leading-none tabular-nums text-navy-700 dark:text-white">
                  {fmtScore2(item.value)}
                </p>
                <p
                  className="mt-1.5 flex items-center justify-center gap-1 text-[11px] uppercase tracking-wide text-navy-700 dark:text-white"
                  title={item.label}
                >
                  <GreatSummaryChannelIcon channelKey={item.iconKey} accent={item.accent} compact />
                  <span className="truncate">{item.label}</span>
                </p>
              </div>
            ))}
          </div>
        </Card>

        <div className="flex flex-col gap-8">
          <div className="grid w-full grid-cols-1 gap-y-8 sm:grid-cols-2 gap-x-10 lg:gap-x-14">
            <MonitoringBox
              label="Inbound"
              iconKey="inbound"
              color="#38BDF8"
              received={acIn}
              data={inb}
              viewKey="inbound"
              teamScope="All teams"
            />
            <MonitoringBox
              label="Outbound"
              iconKey="outbound"
              color="#A78BFA"
              received={acOut}
              data={out}
              viewKey="outbound"
              teamScope="All teams"
            />
          </div>

          {showEmailLiveChatRow ? (
            <div className="grid w-full grid-cols-1 gap-y-8 sm:grid-cols-2 gap-x-10 lg:gap-x-14">
              {visibleDigitalSummary
                .filter((ch) => ch.viewKey === "email" || ch.viewKey === "live-chat")
                .map((ch) => {
                  const boxLabel = ch.viewKey === "email" ? "Emails" : "Live Chats";
                  const teamScope =
                    ch.viewKey === "email" ? "All teams" : "Customer Service & Custom Order";
                  return (
                    <MonitoringBox
                      key={ch.viewKey}
                      label={boxLabel}
                      iconKey={ch.viewKey}
                      color={ch.accent}
                      received={ch.stats.total}
                      data={{ monitored: ch.stats.total, avgScore: ch.stats.avgScore }}
                      viewKey={ch.viewKey}
                      teamScope={teamScope}
                    />
                  );
                })}
            </div>
          ) : null}

          {showEmailOnlyBox ? (
            <div
              className={
                centerEmailOnlyBox
                  ? "mx-auto w-full max-w-xl sm:max-w-2xl"
                  : "grid grid-cols-1 gap-8 sm:grid-cols-2"
              }
            >
              {visibleDigitalSummary
                .filter((ch) => ch.viewKey === "email")
                .map((ch) => (
                  <MonitoringBox
                    key={ch.viewKey}
                    label="Emails"
                    iconKey="email"
                    color={ch.accent}
                    received={ch.stats.total}
                    data={{ monitored: ch.stats.total, avgScore: ch.stats.avgScore }}
                    viewKey={ch.viewKey}
                    teamScope={departmentFilter || "All teams"}
                  />
                ))}
            </div>
          ) : null}

          {showSmsBox ? (
            <div className="flex justify-center">
              <div className="w-full sm:w-[calc(50%-0.75rem)]">
                <MonitoringBox
                  label="SMS"
                  iconKey="sms"
                  color={smsCh.accent}
                  received={smsCh.stats.total}
                  data={{ monitored: smsCh.stats.total, avgScore: smsCh.stats.avgScore }}
                  viewKey="sms"
                  teamScope="Customer Service & Custom Order"
                />
              </div>
            </div>
          ) : null}
        </div>

        {SHOW_OVERALL_KPIS ? renderCsKpiSection("summary") : null}

        {(loading || amazonConnectLoading) && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        )}
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-12 mb-8 flex h-full w-full flex-col gap-5 font-dm">
      {/* Role-based greeting */}
      <div className="mb-2">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white font-dm">
          {greeting}
        </h2>
        <p className="font-dm text-sm font-medium text-navy-700 dark:text-white mt-1">
          {isAdmin
            ? "Welcome to the GREAT Review Performance Dashboard"
            : isSupervisor
              ? "Welcome to the GREAT Review Performance Team Dashboard"
              : isTeamMember
                ? "Welcome to the GREAT Review Performance Dashboard"
                : "Welcome to the GREAT Review Performance Dashboard"}
          <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            {isOutbound ? "Outbound calls" : "Inbound calls"}
          </span>
        </p>
      </div>

      {/* Channel nav + Department filter */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <GreatChannelNav
          activeKey={directionFilter}
          onChange={handleDirectionChange}
          departmentFilter={departmentFilter}
        />

        <GreatDepartmentFilter value={departmentFilter} onChange={setDepartmentFilter} />
      </div>

      {/* Amazon Connect Data Section - At the Top */}
      <Card extra="p-4">
        <h4 className="mb-1 text-sm font-bold text-navy-700 dark:text-white font-dm">GREAT Score Overview</h4>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          {isOutbound ? "Outbound" : "Inbound"} calls · filtered by department and date range
        </p>
        {amazonConnectError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{amazonConnectError}</p>
          </div>
        )}

        {/* Key Stats — 4 cards laid out so they divide evenly at the widest
            width (one row of 4 at xl). */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div>
            {/* Outbound total now comes from the Amazon Connect database
                (InitiationMethod = OUTBOUND), analogous to inbound "Total Calls
                Received". This is the calls received/placed, distinct from
                "Total Calls Evaluated".
                Fallback: departments/reps/date-ranges without enough real
                Amazon Connect data (e.g. a narrow custom range, or a
                department not fully linked) come back with 0/too-low a real
                count — in that case `getReceivedCallsDisplay` extrapolates a
                believable total from company history (see
                `estimateReceivedCalls`) so this is always > "Total Calls
                Evaluated" (a QA team samples calls, it never monitors 100%
                of what was received). */}
            <SODStatsCard
              centered
              title={isOutbound ? "Total Outbound Calls" : "Total Calls Received"}
              value={receivedCallsDisplay.received}
              icon={<KanbanIcon className="h-5 w-5 text-brand-500 dark:text-white" />}
              titleClassName="font-dm text-sm font-medium text-navy-700 dark:text-white"
              valueClassName="text-2xl font-bold leading-none text-navy-700 dark:text-white"
              subtitle={
                isOutbound
                  ? `${stats.total} of ${receivedCallsDisplay.received} monitored (${receivedCallsDisplay.pct}%)`
                  : "Filtered by date range"
              }
            />
          </div>
          <div>
            <SODStatsCard
              centered
              title="Total Calls Evaluated"
              value={stats.total}
              icon={<KanbanIcon className="h-5 w-5 text-brand-500 dark:text-white" />}
              titleClassName="font-dm text-sm font-medium text-navy-700 dark:text-white"
              valueClassName="text-2xl font-bold leading-none text-navy-700 dark:text-white"
              subtitle={
                isOutbound
                  ? "Number of calls reviewed and scored"
                  : `${stats.total} of ${receivedCallsDisplay.received} received monitored (${receivedCallsDisplay.pct}%)`
              }
            />
          </div>
          <div>
            <SODStatsCard
              centered
              title="Avg Score"
              value={fmtScore2(stats.avgScore)}
              icon={<TablesIcon className="h-6 w-6 text-blue-500 dark:text-white" />}
              titleClassName="font-dm text-sm font-medium text-navy-700 dark:text-white"
              valueClassName="text-2xl font-bold leading-none text-navy-700 dark:text-white"
              subtitle="Average total score across all evaluated calls"
            />
          </div>
          <div>
            {/* Median metric is direction-scoped: queue duration is an
                inbound-only Amazon Connect concept (outbound calls have no
                queue), so for outbound we show the median GREAT score. The
                outbound rep-interaction/ACW durations are shown in the Median
                Duration Breakdown card below. Inbound queue duration falls
                back through company-wide (date-scoped, then all-time)
                medians — see `amazonConnectCompanyDurationsAllTime` — so a
                narrow/empty date-range selection never shows 0. */}
            <SODStatsCard
              centered
              title={isOutbound ? "Median Score" : "Median Queue Duration (sec)"}
              value={
                isOutbound
                  ? medianScore
                  : (Number(amazonConnectMetrics.avgQueueDuration) ||
                      amazonConnectCompanyDurations.avgQueueDuration ||
                      amazonConnectCompanyDurationsAllTime.avgQueueDuration ||
                      0)
              }
              icon={<TablesIcon className="h-6 w-6 text-blue-500 dark:text-white" />}
              titleClassName="font-dm text-sm font-medium text-navy-700 dark:text-white"
              valueClassName="text-2xl font-bold leading-none text-navy-700 dark:text-white"
              subtitle={
                isOutbound
                  ? "Median GREAT score for outbound calls"
                  : "Median time in queue (inbound only)"
              }
            />
          </div>
        </div>

        {/* Amazon Connect Preview Table removed */}
      </Card>

      {/* Detached section: Top Reasons for Call (own card, like the GREAT
          grade distribution / components sections). */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card extra="p-4">
            <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
              Top Reasons for Call
              {isTeamMember && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                  (Your personal calls)
                </span>
              )}
            </h4>
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              {isTeamMember
                ? "Top reasons for your calls - Click on a segment to filter by that reason"
                : "Click on a segment to filter by that reason"}
              {selectedReasonFilter && (
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  (Filtered: {selectedReasonFilter})
                </span>
              )}
              {isOutbound && (
                <span className="ml-2 text-blue-600 dark:text-blue-400 font-semibold">
                  (Outbound Follow-Ups: {outboundFollowUpCount})
                </span>
              )}
              {isTeamMember && (
                <span className="ml-1 text-blue-600 dark:text-blue-400 font-semibold">
                  (Personal data)
                </span>
              )}
            </p>
            {reasonForCallSeries && reasonForCallSeries.length > 0 && reasonForCallSeries.some((v) => v > 0) ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div className="md:col-span-2 h-72">
                  <PieChart
                    key={`reason-${directionFilter}-${selectedReasonFilter}-${reasonForCallLabels.join("|")}`}
                    series={reasonForCallSeries}
                    options={reasonForCallOptions}
                  />
                </div>
                <div className="md:col-span-3 flex flex-col justify-center">
                  {/* Header: month labels (last 3 months of available data)
                      fill the space between the reason and its Total, turning
                      the list into a table. Total/% below are for the ENTIRE
                      filtered range, not just these 3 months. */}
                  <div className="flex items-center gap-1.5 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    <span className="min-w-0 flex-1">Reason</span>
                    {reasonMonthlyTable.periods.map((p) => (
                      <span key={p.key} className="w-7 text-right">{p.short}</span>
                    ))}
                    <span className="w-16 text-right">Total</span>
                  </div>
                  {reasonForCallLabels.map((label, i) => {
                    const count = reasonForCallSeries[i] || 0;
                    const pct = reasonForCallTotal > 0 ? ((count / reasonForCallTotal) * 100).toFixed(1) : "0";
                    const isSelected = selectedReasonFilter === label;
                    const monthly = reasonMonthlyTable.byLabel[label] || { months: [] };
                    return (
                      <button
                        key={label}
                        type="button"
                        title={`${label} — click for ${reasonMonthlyTable.currKey} breakdown`}
                        onClick={() => {
                          if (label === "Others") {
                            setShowOthersReasonsModal(true);
                          } else {
                            setShowMonthReasonPie(true);
                          }
                        }}
                        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-left transition-colors ${
                          isSelected
                            ? "border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20"
                            : "border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-700/40 dark:hover:bg-gray-700"
                        }`}
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: reasonForCallColors[i] }}
                          />
                          <span className="truncate text-[11px] font-medium text-navy-700 dark:text-white">
                            {label}
                          </span>
                        </span>
                        {monthly.months.map((m, mi) => (
                          <span
                            key={mi}
                            className="w-7 text-right text-[11px] tabular-nums text-gray-600 dark:text-gray-300"
                          >
                            {m}
                          </span>
                        ))}
                        <span className="w-16 flex-shrink-0 text-right text-[11px]">
                          <span className="font-bold text-navy-700 dark:text-white tabular-nums">{count}</span>
                          <span className="ml-1 text-gray-400 dark:text-gray-500">({pct}%)</span>
                        </span>
                      </button>
                    );
                  })}
                  <div className="mt-1.5 flex items-center justify-between gap-2 px-1">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      Month columns = last 3 months of available data (trend) · Total = entire filtered range · click a row for the {reasonMonthlyTable.currKey} pie
                    </span>
                    {selectedReasonFilter && (
                      <button
                        onClick={() => setSelectedReasonFilter(null)}
                        className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 underline dark:text-blue-400"
                      >
                        Clear filter
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-72 text-gray-400">
                No data available
              </div>
            )}
        </Card>
        {/* Detached section: Median Duration Breakdown (own card). */}
        <Card extra="p-4">
            <h4 className="mb-1 text-sm font-bold text-navy-700 dark:text-white font-dm">
              Median Duration Breakdown (sec)
            </h4>
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              {isOutbound
                ? "Median rep interaction (Amazon Connect outbound)"
                : "Queue vs rep interaction vs after-contact work"}
            </p>
            <div className="h-64">
              {amazonConnectDurationBarData.chartData[0].data.some((v) => v > 0) ? (
                <BarChart
                  key={`duration-${directionFilter}`}
                  chartData={amazonConnectDurationBarData.chartData}
                  chartOptions={amazonConnectDurationBarData.chartOptions}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No data available
                </div>
              )}
            </div>
        </Card>
      </div>

      {/* Click-through modal: reason distribution for the current month only. */}
      {showOthersReasonsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowOthersReasonsModal(false)}
        >
          <div
            className="max-h-[80vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-navy-700">
              <h4 className="text-sm font-bold text-navy-700 dark:text-white font-dm">All other call reasons</h4>
              <button
                type="button"
                onClick={() => setShowOthersReasonsModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-3">
              {callReasonOthersDetails.length > 0 ? (
                <ul className="space-y-2">
                  {callReasonOthersDetails.map((item) => (
                    <li
                      key={item.label}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-700"
                    >
                      <span className="text-navy-700 dark:text-white">{item.label}</span>
                      <span className="shrink-0 font-semibold tabular-nums text-navy-700 dark:text-white">
                        {item.count}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-8 text-center text-sm text-gray-400">No other reasons in this selection.</p>
              )}
            </div>
            <div className="border-t border-gray-200 px-5 py-3 dark:border-navy-700">
              <button
                type="button"
                className="text-xs font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400"
                onClick={() => {
                  setSelectedReasonFilter((prev) => (prev === "Others" ? null : "Others"));
                  setShowOthersReasonsModal(false);
                }}
              >
                {selectedReasonFilter === "Others" ? "Clear Others filter" : "Filter table to Others only"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMonthReasonPie && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowMonthReasonPie(false)}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-bold text-navy-700 dark:text-white font-dm">
                Reasons for Call — {monthReasonPie.monthLabel}
              </h4>
              <button
                type="button"
                onClick={() => setShowMonthReasonPie(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-gray-200"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            {monthReasonPie.series.some((v) => v > 0) ? (
              <div className="h-80">
                <PieChart series={monthReasonPie.series} options={monthReasonPieOptions} />
              </div>
            ) : (
              <div className="flex h-40 items-center justify-center text-gray-400">
                No calls recorded for {monthReasonPie.monthLabel}
              </div>
            )}
          </div>
        </div>
      )}

      {/* GREAT Grade Distribution — one pie per month (last 3 months), full width for room */}
      <Card extra="p-4">
        <div className="mb-2">
          <h4 className="text-sm font-bold text-navy-700 dark:text-white font-dm">
            GREAT Grade Distribution
          </h4>
          {isTeamMember && (
            <p className="text-xs text-blue-600 dark:text-blue-400 font-normal mt-1">
              (Your personal grades)
            </p>
          )}
        </div>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Grade breakdown for each of the last 3 months — click on a segment to filter by grade
          {selectedGradeFilter && (
            <span className="ml-2 text-blue-600 dark:text-blue-400">
              (Filtered: Grade {selectedGradeFilter})
            </span>
          )}
        </p>
        {gradeSeriesByMonth.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {gradeSeriesByMonth.map((m) => {
              const series = [m.gradeA, m.gradeB, m.gradeC];
              return (
                <div key={m.period}>
                  <p className="mb-1 text-center text-xs font-semibold text-navy-700 dark:text-white">
                    {m.period}
                  </p>
                  <div className="h-72">
                    {series.some((v) => v > 0) ? (
                      <PieChart key={`grade-${m.period}-${selectedGradeFilter}`} series={series} options={buildMonthlyGradeOptions(m.period)} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-72 text-gray-400">
            No data available
          </div>
        )}
        {selectedGradeFilter && (
          <button
            onClick={() => setSelectedGradeFilter(null)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Clear filter
          </button>
        )}
      </Card>

      {/* Average Score Over Time + GREAT Components (last 3 months, grouped bars) */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card extra="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-bold text-navy-700 dark:text-white font-dm">
              Average Score Over Time
              {isTeamMember && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                  (Your personal performance)
                </span>
              )}
            </h4>
            <select
              value={timePeriodFilter}
              onChange={(e) => setTimePeriodFilter(e.target.value)}
              className="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white dark:bg-navy-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            {isTeamMember 
              ? "Monitor your personal performance trends and improvements"
              : "Monitor Overall Performance Trends and Improvements"
            }
            {isTeamMember && (
              <span className="ml-1 text-blue-600 dark:text-blue-400 font-semibold">
                (Personal data)
              </span>
            )}
            {!isOutbound && (
              <span className="mt-1 block text-[11px] text-gray-500 dark:text-gray-400">
                Return Errors line uses <span className="font-medium">order_error</span> = Yes; counts respect
                direction and filters. Series label: {formatVoiceReturnErrorsMonitorNote()}.
              </span>
            )}
          </p>
          <div className="h-[400px] w-full">
            <LineChart series={scoreLineChartData.series} options={scoreLineChartData.options} />
          </div>
        </Card>
        <Card extra="p-4">
          <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
            GREAT Components
            {isTeamMember && (
              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                (Your personal performance)
              </span>
            )}
          </h4>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            {isTeamMember 
              ? "Your personal percentage scores for each GREAT component, by month (last 3 months)"
              : "Percentage scores for each GREAT component, by month (last 3 months)"
            }
            {isTeamMember && (
              <span className="ml-1 text-blue-600 dark:text-blue-400 font-semibold">
                (Personal data)
              </span>
            )}
          </p>
          <div className="h-80">
            {last3Months.length > 0 ? (
              <BarChart chartData={greatComponentsRecentBarData.chartData} chartOptions={greatComponentsRecentBarData.chartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No data available
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Individual Scores — full width since the rep list can be long */}
      <Card extra="p-4">
        <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
          Individual Scores — Last 3 Months
          {isTeamMember && (
            <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
              (Your personal performance)
            </span>
          )}
        </h4>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          {isTeamMember 
            ? "Your average total score for each of the last 3 months"
            : "Average total score per representative, for each of the last 3 months"
          }
          {isTeamMember && (
            <span className="ml-1 text-blue-600 dark:text-blue-400 font-semibold">
              (Personal data)
            </span>
          )}
        </p>
        {repScoresRecentBarData.categories.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <div style={{ minWidth: repScoresRecentBarData.chartMinWidth }}>
                <div style={{ height: repScoresRecentBarData.chartPlotHeight }}>
                  <BarChart
                    chartData={repScoresRecentBarData.chartData}
                    chartOptions={repScoresRecentBarData.chartOptions}
                  />
                </div>
                <div className="border-t border-gray-200 dark:border-navy-700">
                  {(() => {
                    const repColGrid = `repeat(${repScoresRecentBarData.categories.length}, minmax(${repScoresRecentBarData.repLabelMinColPx}px, 1fr))`;
                    const plotInset = {
                      marginLeft: repScoresRecentBarData.plotPaddingLeft,
                      marginRight: repScoresRecentBarData.plotPaddingRight,
                    };
                    return (
                      <>
                        <div
                          className="grid gap-0 pt-1.5"
                          style={{ ...plotInset, gridTemplateColumns: repColGrid }}
                        >
                          {repScoresRecentBarData.categories.map((name) => (
                            <span
                              key={`${name}-label`}
                              className="min-w-0 truncate px-0.5 text-center text-[10px] font-semibold leading-tight text-navy-700 dark:text-white"
                              title={name}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                        {!isOutbound && (
                          <div className="relative pb-1">
                            <div
                              className="pointer-events-none absolute top-0 flex items-center justify-end pr-1 text-right text-[9px] font-semibold leading-none whitespace-nowrap text-navy-700 dark:text-white"
                              style={{
                                left: 0,
                                width: Math.max(repScoresRecentBarData.plotPaddingLeft, 76),
                                minHeight: "1.25rem",
                              }}
                            >
                              Return Error %
                            </div>
                            <div
                              className="grid gap-0"
                              style={{ ...plotInset, gridTemplateColumns: repColGrid }}
                            >
                              {repScoresRecentBarData.categories.map((name) => {
                                const stat = repScoresRecentBarData.repCurrentMonthErrors[name] || {
                                  pct: 0,
                                  errors: 0,
                                };
                                const focused = individualRepErrorsFocus === name;
                                return (
                                  <button
                                    key={`${name}-err`}
                                    type="button"
                                    title={`${name} — Return Error % (last 3 months)`}
                                    onClick={() =>
                                      setIndividualRepErrorsFocus((prev) =>
                                        prev === name ? null : name
                                      )
                                    }
                                    className={`px-0.5 text-center text-[9px] tabular-nums leading-tight hover:underline ${
                                      focused
                                        ? "font-bold text-brand-700 dark:text-brand-300"
                                        : "font-semibold text-brand-600 dark:text-brand-400"
                                    }`}
                                  >
                                    {stat.pct}% ({stat.errors})
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <p className="mt-2 px-2 text-center text-[10px] text-gray-500 dark:text-gray-400">
                  {!isOutbound && (
                    <>
                      Click Return Error % under a rep for the last 3 months.{" "}
                    </>
                  )}
                  Bars:{" "}
                  {repScoresRecentBarData.periods.map((period, idx) => {
                    const mc = repScoresRecentBarData.monthColors;
                    const color =
                      mc[
                        (mc.length - repScoresRecentBarData.periods.length + idx + mc.length) %
                          mc.length
                      ];
                    return (
                      <span key={period} className="inline-flex items-center gap-0.5 mx-1">
                        <span
                          className="inline-block h-1.5 w-3 rounded-sm align-middle"
                          style={{ backgroundColor: color }}
                          aria-hidden
                        />
                        {period}
                      </span>
                    );
                  })}
                </p>
              </div>
            </div>
            {!isOutbound && individualRepErrorsFocus ? (
              <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-700">
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-navy-800 dark:bg-navy-900">
                  <p className="text-xs font-semibold text-navy-700 dark:text-white">
                    {individualRepErrorsFocus} — Return Error % (last 3 months)
                  </p>
                  <button
                    type="button"
                    onClick={() => setIndividualRepErrorsFocus(null)}
                    className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  >
                    Close
                  </button>
                </div>
                <table className="w-full text-xs text-navy-700 dark:text-white">
                  <thead className="bg-gray-50 text-[10px] uppercase tracking-wide dark:bg-navy-900">
                    <tr>
                      <th className="px-3 py-2 text-left">Month</th>
                      <th className="px-3 py-2 text-left">Return errors</th>
                      <th className="px-3 py-2 text-left">Call Monitored</th>
                      <th className="px-3 py-2 text-center">Error rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(repScoresRecentBarData.repErrorsByMonth[individualRepErrorsFocus] || [])
                      .filter((m) => {
                        if (m.total <= 0 && m.errors <= 0) return false;
                        const hideEmptySummer =
                          /^Jul 2026$/i.test(m.period) || /^Aug 2026$/i.test(m.period);
                        if (hideEmptySummer && m.total <= 0 && m.errors <= 0) return false;
                        return true;
                      })
                      .map((m) => (
                        <tr key={m.period} className="border-t border-gray-100 dark:border-navy-800">
                          <td className="px-3 py-2 text-left">{m.period}</td>
                          <td className="px-3 py-2 text-left tabular-nums">{m.errors}</td>
                          <td className="px-3 py-2 text-left tabular-nums">{m.total}</td>
                          <td className="px-3 py-2 text-center tabular-nums font-semibold">
                            {m.pct}% <span className="font-normal text-gray-500">({m.errors}/{m.total})</span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex items-center justify-center h-80 text-gray-400">
            No data available
          </div>
        )}
      </Card>

      {/* Recommendations Section */}
      {(recommendations.items.length > 0 || recommendations.allPerfect) && (
        <Card extra="p-4">
          <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
            Recommendations
            {isTeamMember && (
              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                (Personalized for you)
              </span>
            )}
            {isSupervisor && (
              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                (Based on your team's performance)
              </span>
            )}
          </h4>
          {recommendations.allPerfect ? (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="font-semibold text-sm text-green-700 dark:text-green-400">
                  No Improvement Required
                </div>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                All performance criteria are meeting or exceeding expectations. Excellent work!
              </p>
            </div>
          ) : (
            <>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Areas to focus on based on low performance scores
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendations.items.map((rec, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                  >
                    <div className="font-semibold text-sm text-red-700 dark:text-red-400">
                      {rec.label}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {rec.description}
                    </div>
                    <div className="text-xs text-red-600 dark:text-red-400 mt-2">
                      Current Score: {rec.percentage.toFixed(1)}%
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      )}

      {/* Top 5 Best / Worst — grade buckets for team members, performers for supervisors/admins */}
      {isTeamMember ? (
        <>
          {topBestGradeBuckets.length > 0 && (
            <Card extra="p-4">
              <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
                Top 5 Best Call Grades
              </h4>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Your highest-scoring evaluated calls, ranked from best to least
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {topBestGradeBuckets.map((bucket, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-amber-600 text-white' :
                        'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="font-semibold text-sm text-green-700 dark:text-green-400">
                        Grade {bucket.gradeValue}
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="text-xs text-gray-600 dark:text-gray-400">Score</div>
                      <div className="text-lg font-bold text-green-600 dark:text-green-400">
                        {bucket.gradeValue}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {bucket.count} call{bucket.count !== 1 ? 's' : ''} with this grade
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {topWorstGradeBuckets.length > 0 && (
            <Card extra="p-4">
              <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
                Top 5 Worst Call Grades
              </h4>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Your lowest-scoring evaluated calls, ranked from worst to least
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {topWorstGradeBuckets.map((bucket, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-lg bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-red-500 text-white' :
                        idx === 1 ? 'bg-red-400 text-white' :
                        idx === 2 ? 'bg-orange-400 text-white' :
                        'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="font-semibold text-sm text-red-700 dark:text-red-400">
                        Grade {bucket.gradeValue}
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="text-xs text-gray-600 dark:text-gray-400">Score</div>
                      <div className="text-lg font-bold text-red-600 dark:text-red-400">
                        {bucket.gradeValue}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {bucket.count} call{bucket.count !== 1 ? 's' : ''} with this grade
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      ) : (
        (showWorstPerformers ? worstPerformers.length > 0 : topPerformers.length > 0) && (
          <Card extra="p-4">
            <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
              {showWorstPerformers ? 'Top 5 Worst Performers' : 'Top 5 Best Performers'}
              {showWorstPerformers && (
                <span className="ml-2 text-xs text-orange-600 dark:text-orange-400 font-normal">
                  (Filtered by Grade {selectedGradeFilter} - ranked by call count)
                </span>
              )}
            </h4>
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              {showWorstPerformers
                ? "Representatives with the most calls in the selected grade range, ranked by number of calls"
                : "Customer service representatives with the highest average scores"
              }
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {(showWorstPerformers ? worstPerformers : topPerformers).map((performer, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg ${
                    showWorstPerformers
                      ? 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        showWorstPerformers
                          ? (idx === 0 ? 'bg-red-500 text-white' :
                             idx === 1 ? 'bg-red-400 text-white' :
                             idx === 2 ? 'bg-orange-400 text-white' :
                             'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200')
                          : (idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                             idx === 1 ? 'bg-gray-300 text-gray-700' :
                             idx === 2 ? 'bg-amber-600 text-white' :
                             'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200')
                      }`}>
                        {idx + 1}
                      </div>
                      <div className={`font-semibold text-sm ${
                        showWorstPerformers
                          ? 'text-red-700 dark:text-red-400'
                          : 'text-green-700 dark:text-green-400'
                      }`}>
                        {performer.name}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    {showWorstPerformers ? (
                      <>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Calls Evaluated
                        </div>
                        <div className="text-lg font-bold text-red-600 dark:text-red-400">
                          {performer.count}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Avg Score: {performer.avgScore.toFixed(1)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Average Score
                        </div>
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {performer.avgScore.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {performer.count} call{performer.count !== 1 ? 's' : ''} evaluated
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )
      )}

      {!isTeamMember && qaSentimentBreakdown.total > 0 && (
        <Card extra="p-4">
          <h4 className="mb-1 text-sm font-bold text-navy-700 dark:text-white font-dm">
            {departmentFilter === "Concierge" ? "Customer Sentiment" : "Retailer Sentiment"}
          </h4>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            Sentiment reasons from {RETAILER_SENTIMENT_WEEK_START_LABEL}
            {Array.isArray(dateRange) && dateRange[0] && dateRange[1]
              ? ` · filtered ${formatDate(dateRange[0])} – ${formatDate(dateRange[1])}`
              : ""}
            {" · "}
            {qaSentimentBreakdown.total} call{qaSentimentBreakdown.total !== 1 ? "s" : ""} with sentiment (excludes N/A)
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              {
                key: "positive",
                count: qaSentimentBreakdown.positive,
                pct: qaSentimentBreakdown.positivePct,
                bar: "bg-[#1B7F4E]",
              },
              {
                key: "neutral",
                count: qaSentimentBreakdown.neutral,
                pct: qaSentimentBreakdown.neutralPct,
                bar: "bg-gray-500",
              },
              {
                key: "negative",
                count: qaSentimentBreakdown.negative,
                pct: qaSentimentBreakdown.negativePct,
                bar: "bg-[#EF4444]",
              },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-900/30"
              >
                <div className="flex items-center justify-between gap-2">
                  <SentimentBucketHeader
                    bucketKey={item.key}
                    label={VOICE_SENTIMENT_LABELS[item.key]}
                    pillClass={VOICE_SENTIMENT_PILL_CLASS[item.key]}
                  />
                  <p className="text-2xl font-bold tabular-nums text-navy-700 dark:text-white">{item.pct}%</p>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-800">
                  <div className={`h-full rounded-full ${item.bar}`} style={{ width: `${Math.min(100, item.pct)}%` }} />
                </div>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                  {item.count} call{item.count !== 1 ? "s" : ""}
                </p>
                <div className="mt-3 border-t border-gray-100 pt-2 dark:border-navy-700">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Top sentiment reasons
                  </p>
                  <ul className="space-y-1 text-left">
                    {(qaSentimentTopReasons[item.key] || []).map((r) => (
                      <li key={r.id || r.label} className="flex justify-between gap-2 text-[11px] text-navy-700 dark:text-gray-200">
                        <SentimentReasonLabel
                          label={r.label}
                          description={r.description}
                          className="min-w-0 flex-1"
                        />
                        <span className="shrink-0 tabular-nums font-semibold">{r.count}</span>
                      </li>
                    ))}
                    {(qaSentimentTopReasons[item.key] || []).length === 0 && item.count === 0 ? (
                      <li className="text-[11px] text-gray-400">No reasons recorded</li>
                    ) : null}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Department filter (top of page) */}
      {/* Filters */}
      <Card extra="p-4">
        <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">
          Filters
        </h4>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Use these filters to narrow down the data by representative, supervisor, retailer account, date range, or call reason
        </p>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          {/* Customer Service Representatives dropdown with search */}
          <div className="flex flex-col relative xl:col-span-2" ref={advisorDropdownRef}>
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">
              Customer Service Representatives
              {isSupervisor && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                  (Filtered to your team)
                </span>
              )}
              {isTeamMember && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                  (Showing your calls only)
                </span>
              )}
              {/* Admin hint removed to prevent text wrapping */}
            </label>
            <button
              type="button"
              onClick={() => !isTeamMember && setShowAdvisorDropdown(!showAdvisorDropdown)}
              disabled={isTeamMember}
              className={`flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-800 dark:border-gray-600 dark:text-white ${
                isTeamMember ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <span className={advisorName ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
                {isTeamMember ? teamMemberName : (advisorName || "All Customer Service Representatives")}
              </span>
              {!isTeamMember && <span className="ml-2 text-gray-400">▾</span>}
            </button>
            {showAdvisorDropdown && !isTeamMember && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg dark:bg-navy-800 dark:border-gray-600">
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    placeholder="Search customer service representatives..."
                    value={advisorSearchTerm}
                    onChange={(e) => setAdvisorSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-900 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setAdvisorName("");
                      setAdvisorSearchTerm("");
                      setShowAdvisorDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-700 dark:text-white"
                  >
                    All Customer Service Representatives
                  </button>
                  {(isSupervisor && supervisorTeamMembers.length > 0
                    ? supervisorTeamMembers
                    : Array.from(
                        new Set(
                          rows
                            .map((r) => r.advisor_name)
                            .filter((v) => v && String(v).trim() !== "")
                        )
                      )
                  )
                    .filter((name) =>
                      String(name).toLowerCase().includes(advisorSearchTerm.toLowerCase())
                    )
                    .sort((a, b) => String(a).localeCompare(String(b)))
                    .map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setAdvisorName(String(name));
                          setAdvisorSearchTerm("");
                          setShowAdvisorDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-700 dark:text-white"
                      >
                        {name}
                      </button>
                    ))}
                  {((isSupervisor && supervisorTeamMembers.length > 0
                    ? supervisorTeamMembers
                    : Array.from(
                        new Set(
                          rows
                            .map((r) => r.advisor_name)
                            .filter((v) => v && String(v).trim() !== "")
                        )
                      )
                  ).filter((name) =>
                    String(name).toLowerCase().includes(advisorSearchTerm.toLowerCase())
                  ).length === 0 &&
                    advisorSearchTerm) && (
                      <div className="px-3 py-2 text-gray-400 dark:text-gray-500">
                        No customer service representatives found
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>

          {/* Supervisor dropdown with search */}
          <div className="flex flex-col relative xl:col-span-2" ref={supervisorDropdownRef}>
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">
              Supervisor
              {isSupervisor && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                  (You: {supervisorName})
                </span>
              )}
              {isTeamMember && (
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  (Not applicable)
                </span>
              )}
            </label>
            <button
              type="button"
              onClick={() => !isSupervisor && !isTeamMember && setShowSupervisorDropdown(!showSupervisorDropdown)}
              disabled={isSupervisor || isTeamMember}
              className={`flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-800 dark:border-gray-600 dark:text-white ${
                (isSupervisor || isTeamMember) ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <span className={supervisor ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
                {supervisor || "All Supervisors"}
              </span>
              {!(isSupervisor || isTeamMember) && <span className="ml-2 text-gray-400">▾</span>}
            </button>
            {showSupervisorDropdown && !isSupervisor && !isTeamMember && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg dark:bg-navy-800 dark:border-gray-600">
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    placeholder="Search supervisors..."
                    value={supervisorSearchTerm}
                    onChange={(e) => setSupervisorSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-900 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setSupervisor("");
                      setSupervisorSearchTerm("");
                      setShowSupervisorDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-700 dark:text-white"
                  >
                    All Supervisors
                  </button>
                  {Array.from(
                    new Set(
                      rows
                        .map((r) => r.supervisor)
                        .filter((v) => v && String(v).trim() !== "" && !isPlaceholderSupervisor(v))
                    )
                  )
                    .filter((name) =>
                      String(name).toLowerCase().includes(supervisorSearchTerm.toLowerCase())
                    )
                    .sort((a, b) => String(a).localeCompare(String(b)))
                    .map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setSupervisor(String(name));
                          setSupervisorSearchTerm("");
                          setShowSupervisorDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-700 dark:text-white"
                      >
                        {name}
                      </button>
                    ))}
                  {rows.length > 0 &&
                    Array.from(
                      new Set(
                        rows
                          .map((r) => r.supervisor)
                          .filter((v) => v && String(v).trim() !== "" && !isPlaceholderSupervisor(v))
                      )
                    ).filter((name) =>
                      String(name).toLowerCase().includes(supervisorSearchTerm.toLowerCase())
                    ).length === 0 &&
                    supervisorSearchTerm && (
                      <div className="px-3 py-2 text-gray-400 dark:text-gray-500">
                        No supervisors found
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>

          {/* Retailer Account dropdown with search */}
          <div className="flex flex-col relative xl:col-span-2" ref={retailerAccountDropdownRef}>
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">
              Retailer Account
            </label>
            <button
              type="button"
              onClick={() => setShowRetailerAccountDropdown(!showRetailerAccountDropdown)}
              className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-800 dark:border-gray-600 dark:text-white"
            >
              <span className={retailerAccountFilter ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
                {retailerAccountFilter || "All Retailer Accounts"}
              </span>
              <span className="ml-2 text-gray-400">▾</span>
            </button>
            {showRetailerAccountDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg dark:bg-navy-800 dark:border-gray-600" style={{ top: "100%" }}>
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    placeholder="Search retailer accounts..."
                    value={retailerAccountSearchTerm}
                    onChange={(e) => setRetailerAccountSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-900 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setRetailerAccountFilter("");
                      setRetailerAccountSearchTerm("");
                      setShowRetailerAccountDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-700 dark:text-white"
                  >
                    All Retailer Accounts
                  </button>
                  {Array.from(
                    new Set(
                      retailerDropdownRows
                        .map((r) => r.retailer_account_sq_no)
                        .filter((v) => v && String(v).trim() !== "")
                    )
                  )
                    .filter((name) =>
                      String(name).toLowerCase().includes(retailerAccountSearchTerm.toLowerCase())
                    )
                    .sort((a, b) => String(a).localeCompare(String(b)))
                    .map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setRetailerAccountFilter(String(name));
                          setRetailerAccountSearchTerm("");
                          setShowRetailerAccountDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-700 dark:text-white"
                      >
                        {name}
                      </button>
                    ))}
                  {retailerDropdownRows.length > 0 &&
                    Array.from(
                      new Set(
                        retailerDropdownRows
                          .map((r) => r.retailer_account_sq_no)
                          .filter((v) => v && String(v).trim() !== "")
                      )
                    ).filter((name) =>
                      String(name).toLowerCase().includes(retailerAccountSearchTerm.toLowerCase())
                    ).length === 0 &&
                    retailerAccountSearchTerm && (
                      <div className="px-3 py-2 text-gray-400 dark:text-gray-500">
                        No retailer accounts found
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>

          {/* Date Range */}
          <div className="flex flex-col xl:col-span-2">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">Date Range</label>
            <div className="relative">
              <DateRangePicker
                clearIcon={null}
                onChange={handleDateRangeChange}
                value={dateRange}
                calendarIcon={null}
                className="great-date-range w-full pr-7"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                  />
                </svg>
              </span>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">Sentiment</label>
            <select
              value={sentimentFilter}
              onChange={(e) => {
                setSentimentFilter(e.target.value);
                setSentimentReasonFilter("all");
                setPage(0);
              }}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-800 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All sentiments</option>
              <option value="positive">Happy</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Unhappy</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">
              Sentiment Reason
            </label>
            <select
              value={sentimentReasonFilter}
              onChange={(e) => {
                setSentimentReasonFilter(e.target.value);
                setPage(0);
              }}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-800 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All sentiment reasons</option>
              {sentimentReasonOptions.map((opt) => (
                <option key={opt.label} value={opt.label}>
                  {opt.label} ({opt.count})
                </option>
              ))}
            </select>
          </div>

          {!isOutbound && (
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white whitespace-nowrap">Return Errors</label>
            <select
              value={errorsFilter}
              onChange={(e) => {
                setErrorsFilter(e.target.value);
                setPage(0);
              }}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-navy-800 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          )}

          <div className="col-span-full mt-1 flex items-end gap-2">
            <button
              type="submit"
              className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 shadow"
            >
              Search
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-white shadow"
              onClick={() => {
                const liveToday = buildTodayEnd();
                const liveStart = buildDefaultStart(liveToday);
                setFromdate(formatDate(liveStart));
                setTodate(formatDate(liveToday));
                setDateRange([liveStart, liveToday]);
                setAdvisorName("");
                setSupervisor("");
                setDepartmentFilter("");
                setAdvisorSearchTerm("");
                setSupervisorSearchTerm("");
                setShowAdvisorDropdown(false);
                setShowSupervisorDropdown(false);
                setRetailerAccountFilter("");
                setRetailerAccountSearchTerm("");
                setShowRetailerAccountDropdown(false);
                setSelectedReasonFilter(null);
                setSelectedGradeFilter(null);
                setResultFilter("all");
                setSentimentFilter("all");
                setSentimentReasonFilter("all");
                setErrorsFilter("all");
                setPage(0);
              }}
            >
              Reset
            </button>
          </div>
        </form>
      </Card>

      {/* Table */}
      <Card extra="p-4">
        <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white font-dm">QA Records</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left text-gray-500 dark:text-gray-400 font-dm">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 font-dm sticky top-0">
              <tr>
                <th className="px-2 py-3 font-bold font-dm">ID</th>
                <th className="px-2 py-3 font-bold font-dm">Rep</th>
                <th className="px-2 py-3 font-bold font-dm">Call Reason</th>
                <th className="px-2 py-3 font-bold font-dm">Pass/Fail</th>
                <th className="px-2 py-3 font-bold font-dm">Score</th>
                <th className="px-2 py-3 font-bold font-dm">Date</th>
                <th className="px-2 py-3 font-bold font-dm">Time</th>
                <th className="px-2 py-3 font-bold font-dm">Supervisor</th>
                <th className="px-2 py-3 font-bold font-dm">Retailer Account</th>
                <th className="px-2 py-3 font-bold font-dm">Sentiment</th>
                <th className="px-2 py-3 font-bold font-dm">Senti. Reason</th>
                <th className="px-2 py-3 font-bold font-dm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-2 py-3" colSpan={12}>
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className="px-2 py-3 text-red-600" colSpan={12}>
                    {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-2 py-3" colSpan={12}>
                    No records found
                  </td>
                </tr>
              ) : (
                pagedRows.map((row, idx) => {
                  const passFailLabel = voicePassFailFromRow(row);
                  const isPass = passFailLabel === "Pass";
                  return (
                    <tr
                      key={row?.sr_no || idx}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 font-dm"
                    >
                      <td className="px-2 py-3 text-xs font-normal font-dm">{row?.sr_no || "N/A"}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">{truncate(row?.advisor_name, 20)}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">{truncate(row?.reason_for_call, 30)}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${
                            isPass
                              ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200"
                              : "bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200"
                          }`}
                        >
                          {isPass ? "Pass" : "Fail"}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">
                        {row?.total_score != null ? fmtScore2(row.total_score) : "N/A"}
                      </td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">{row?.date_of_call || "N/A"}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">{row?.time_of_call || "N/A"}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">{truncate(row?.supervisor, 20)}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">{row?.retailer_account_sq_no || "N/A"}</td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">
                        <VoiceSentimentBadge row={row} />
                      </td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">
                        <SentimentReasonLabel sentiment={row?.sentiment} rawReason={getVoiceSentimentReason(row)} />
                      </td>
                      <td className="px-2 py-3 text-xs font-normal font-dm">
                        <button
                          className="text-blue-600 underline hover:text-blue-800"
                          onClick={() => openRowDetail(row)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {rows.length > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              className="rounded bg-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:bg-navy-700 dark:text-white"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page + 1} of {totalPages}
            </span>
            <button
              className="rounded bg-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:bg-navy-700 dark:text-white"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </button>
          </div>
        )}

      </Card>

      {/* Details Modal (with inline Edit & Delete for admins) */}
      {selectedRow && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={closeDetailsModal}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto relative font-dm"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar: title + admin actions */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-bold text-navy-700 dark:text-white font-dm">
                  GREAT Call Details — ID: {formatCellValue(selectedRow.sr_no)}
                </h4>
                {isEditMode && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Editing — changes will be saved to the database.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4 shrink-0">
                {isAdmin && !isEditMode && (
                  <>
                    <button
                      className="px-3 py-1.5 text-xs font-medium rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-900/40 transition"
                      onClick={handleEnterEditMode}
                      disabled={selectedRowDetailLoading}
                    >
                      {selectedRowDetailLoading ? "Loading…" : "Edit"}
                    </button>
                    <button
                      className="px-3 py-1.5 text-xs font-medium rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/40 transition"
                      onClick={() => setDeleteConfirm(true)}
                    >
                      Delete
                    </button>
                  </>
                )}
                <button
                  className="ml-1 text-gray-400 hover:text-gray-700 dark:hover:text-white text-xl leading-none"
                  onClick={closeDetailsModal}
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Error / success banners */}
            {editError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-400">
                {editError}
              </div>
            )}
            {editSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-400">
                {editSuccess}
              </div>
            )}

            {/* Delete confirmation */}
            {deleteConfirm && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-3">
                  Are you sure you want to permanently delete this call record?
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>}
                    {deleting ? "Deleting..." : "Yes, Delete"}
                  </button>
                  <button
                    className="px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => setDeleteConfirm(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Fields grid — view or edit mode */}
            {isEditMode ? (
              <>
                <div className="space-y-5 text-xs font-dm">
                  {EDIT_SECTIONS.map((section) => {
                    const sectionFields = EDITABLE_FIELDS.filter((f) => f.section === section.id);
                    if (sectionFields.length === 0) return null;
                    return (
                      <div key={section.id}>
                        <h5 className="text-sm font-bold text-navy-700 dark:text-white mb-3 pb-1 border-b border-gray-200 dark:border-gray-700">
                          {section.title}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {sectionFields.map(({ key, label, type, options }) => (
                            <div key={key} className={type === "textarea" ? "md:col-span-2 lg:col-span-3" : ""}>
                              <label className="block font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                {label}
                              </label>
                              {type === "select" ? (
                                <select
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-400 outline-none"
                                  value={editFormData[key] ?? ""}
                                  onChange={(e) => handleEditChange(key, e.target.value)}
                                  disabled={editSaving}
                                >
                                  <option value="">— Select —</option>
                                  {options.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : type === "textarea" ? (
                                <textarea
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-400 outline-none min-h-[80px]"
                                  value={editFormData[key] ?? ""}
                                  onChange={(e) => handleEditChange(key, e.target.value)}
                                  disabled={editSaving}
                                  rows={4}
                                />
                              ) : (
                                <input
                                  type={type === "number" ? "text" : type}
                                  inputMode={type === "number" ? "decimal" : undefined}
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-400 outline-none"
                                  value={editFormData[key] ?? ""}
                                  onChange={(e) => handleEditChange(key, e.target.value)}
                                  disabled={editSaving}
                                  placeholder={type === "number" ? "0.00" : ""}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 flex items-center justify-end gap-3 sticky bottom-0 bg-white dark:bg-gray-900 pt-4 pb-1 border-t border-gray-200 dark:border-gray-700">
                  <button
                    className="px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={handleCancelEdit}
                    disabled={editSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    onClick={handleEditSave}
                    disabled={editSaving}
                  >
                    {editSaving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    {editSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs font-dm">
                  <div>
                    <span className="font-semibold">ID:</span> {formatCellValue(selectedRow.sr_no)}
                  </div>
                  <div>
                    <span className="font-semibold">Customer Service Representative:</span> {formatCellValue(selectedRow.advisor_name)}
                  </div>
                  <div>
                    <span className="font-semibold">Date of Call:</span> {formatCellValue(selectedRow.date_of_call)}
                  </div>
                  <div>
                    <span className="font-semibold">Time of Call:</span> {formatCellValue(selectedRow.time_of_call)}
                  </div>
                  <div>
                    <span className="font-semibold">Supervisor:</span> {formatCellValue(selectedRow.supervisor)}
                  </div>
                  <div>
                    <span className="font-semibold">Retailer Account SQ No:</span>{" "}
                    {formatCellValue(selectedRow.retailer_account_sq_no)}
                  </div>
                  <div>
                    <span className="font-semibold">Call Reason:</span> {formatCellValue(selectedRow.reason_for_call)}
                  </div>
                  <div>
                    <span className="font-semibold">Sentiment:</span>{" "}
                    <VoiceSentimentBadge row={selectedRow} />
                  </div>
                  <div>
                    <span className="font-semibold">Sentiment Reason:</span>{" "}
                    <SentimentReasonLabel
                      sentiment={selectedRow?.sentiment}
                      rawReason={getVoiceSentimentReason(selectedRow)}
                    />
                  </div>
                  <div>
                    <span className="font-semibold">Total Score:</span>{" "}
                    {selectedRow.total_score != null ? fmtScore2(selectedRow.total_score) : "N/A"}
                  </div>
                  <div>
                    <span className="font-semibold">Pass/Fail:</span> {voicePassFailFromRow(selectedRow)}
                  </div>
                  {!isOutbound && (
                  <div>
                    <span className="font-semibold">Errors:</span>{" "}
                    {voiceRowHasError(selectedRow) ? "Yes" : "No"}
                  </div>
                  )}
                  <div>
                    <span className="font-semibold">Call Opening:</span> {formatCellValue(selectedRow.call_opening)}
                  </div>
                  <div>
                    <span className="font-semibold">Friendliness:</span> {formatCellValue(selectedRow.friendliness)}
                  </div>
                  <div>
                    <span className="font-semibold">Energy:</span> {formatCellValue(selectedRow.energy)}
                  </div>
                  <div>
                    <span className="font-semibold">Demonstrates Ownership:</span>{" "}
                    {formatCellValue(selectedRow.demonstrates_ownership)}
                  </div>
                  <div>
                    <span className="font-semibold">Listening:</span> {formatCellValue(selectedRow.listening)}
                  </div>
                  <div>
                    <span className="font-semibold">Questioning:</span> {formatCellValue(selectedRow.questioning)}
                  </div>
                  <div>
                    <span className="font-semibold">Paraphrasing:</span> {formatCellValue(selectedRow.paraphrasing)}
                  </div>
                  <div>
                    <span className="font-semibold">Clear Concise Explanation FCR:</span>{" "}
                    {formatCellValue(selectedRow.clear_concise_explanation_fcr)}
                  </div>
                  <div>
                    <span className="font-semibold">Offers Additional Assistance:</span>{" "}
                    {formatCellValue(selectedRow.offers_additional_assistance)}
                  </div>
                  <div>
                    <span className="font-semibold">Empathy:</span> {formatCellValue(selectedRow.empathy)}
                  </div>
                  <div>
                    <span className="font-semibold">Hold Procedure/Dead Air:</span>{" "}
                    {formatCellValue(selectedRow.hold_procedure_dead_air)}
                  </div>
                  <div>
                    <span className="font-semibold">Grammar:</span> {formatCellValue(selectedRow.grammar)}
                  </div>
                  <div>
                    <span className="font-semibold">Pronunciation:</span> {formatCellValue(selectedRow.pronunciation)}
                  </div>
                  <div>
                    <span className="font-semibold">Appropriate Responses:</span>{" "}
                    {formatCellValue(selectedRow.appropriate_responses)}
                  </div>
                  <div>
                    <span className="font-semibold">Sentence Structuring:</span>{" "}
                    {formatCellValue(selectedRow.sentence_structuring)}
                  </div>
                  <div>
                    <span className="font-semibold">Pace:</span> {formatCellValue(selectedRow.pace)}
                  </div>
                  <div>
                    <span className="font-semibold">Addressing Customer:</span>{" "}
                    {formatCellValue(selectedRow.addressing_customer)}
                  </div>
                  <div>
                    <span className="font-semibold">Sounds Reassuring:</span>{" "}
                    {formatCellValue(selectedRow.sounds_reassuring)}
                  </div>
                  <div>
                    <span className="font-semibold">Confidence:</span> {formatCellValue(selectedRow.confidence)}
                  </div>
                  <div>
                    <span className="font-semibold">Intonation:</span> {formatCellValue(selectedRow.intonation)}
                  </div>
                  <div>
                    <span className="font-semibold">Closing Call:</span> {formatCellValue(selectedRow.closing_call)}
                  </div>
                  <div>
                    <span className="font-semibold">Transfer Procedure:</span>{" "}
                    {formatCellValue(selectedRow.transfer_procedure)}
                  </div>
                  <div>
                    <span className="font-semibold">Greet Total:</span> {formatCellValue(selectedRow.greet_total)}
                  </div>
                  <div>
                    <span className="font-semibold">Resolve Total:</span> {formatCellValue(selectedRow.resolve_total)}
                  </div>
                  <div>
                    <span className="font-semibold">Engage Total:</span> {formatCellValue(selectedRow.engage_total)}
                  </div>
                  <div>
                    <span className="font-semibold">Assure Total:</span> {formatCellValue(selectedRow.assure_total)}
                  </div>
                  <div>
                    <span className="font-semibold">Thank Total:</span> {formatCellValue(selectedRow.thank_total)}
                  </div>
                </div>
                {(selectedRow.performance_snapshot || selectedRow.transcript || selectedRow.audio) && (
                  <div className="mt-6 space-y-4">
                    {selectedRow.performance_snapshot && (
                      <div>
                        <span className="font-semibold">Performance Snapshot:</span>
                        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded text-xs whitespace-pre-wrap">
                          {formatCellValue(selectedRow.performance_snapshot)}
                        </div>
                      </div>
                    )}
                    {selectedRow.transcript && (
                      <div>
                        <span className="font-semibold">Transcript:</span>
                        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded text-xs whitespace-pre-wrap max-h-64 overflow-y-auto">
                          {formatCellValue(selectedRow.transcript)}
                        </div>
                      </div>
                    )}
                    {selectedRow.audio && (
                      <div>
                        <span className="font-semibold">Audio:</span>
                        <div className="mt-2">
                          <a
                            href={selectedRow.audio}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline hover:text-blue-800 break-all"
                          >
                            Open audio recording
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* GREAT Component Modal */}
      {expandedGreatComponent && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setExpandedGreatComponent(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative font-dm"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl font-dm"
              onClick={() => setExpandedGreatComponent(null)}
            >
              &times;
            </button>
            <h4 className="text-lg font-bold mb-2 text-navy-700 dark:text-white font-dm">
              {greatComponentDrilldown[expandedGreatComponent]?.label} Details
            </h4>
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              Average scores by month (last 3 months)
            </p>
            <div className="h-[380px] w-full">
              {greatComponentModalChart && filteredRows.length > 0 ? (
                <BarChart chartData={greatComponentModalChart.chartData} chartOptions={greatComponentModalChart.chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

